import React, {
  startTransition,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { format, formatDistanceToNowStrict, parseISO } from 'date-fns';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowUpRight,
  ArrowUp,
  ArrowUpDown,
  Boxes,
  Building2,
  CheckCircle2,
  ChevronDown,
  CircleAlert,
  ClipboardList,
  Factory,
  Loader2,
  Mail,
  Minus,
  MoreHorizontal,
  Package2,
  PackageCheck,
  PackageSearch,
  Plus,
  Printer,
  RefreshCw,
  RotateCcw,
  Search,
  Send,
  ShoppingCart,
  TimerReset,
  Trash2,
  Truck,
  Warehouse,
} from 'lucide-react';
import { useClinic } from '@/contexts/ClinicContext';
import { authService } from '@/services/authService';
import {
  inventoryService,
  type InventoryIssueBatch,
  type InventoryIssueBatchItem,
  type InventoryItem,
  type InventoryItemSupplier,
  type InventoryPurchaseOrder,
  type InventoryPurchaseOrderItem,
  type InventoryRoom,
  type InventoryRoomRequest,
  type InventoryRoomRequestItem,
  type InventorySyncProgress,
  type InventorySupplier,
  type InventoryWorkflowSnapshot,
  type StockTakeSession,
  type StockTakeLine,
  type StockTakeVariancePreview,
  type CycleCountDueItem,
} from '@/services/inventoryService';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  NewPurchaseOrderDialog,
  type NewPurchaseOrderDraftPrefill,
} from '@/components/NewPurchaseOrderDialog';
import { buildPriceHistoryByItemId, formatCentsSGD } from '@/lib/priceHistory';
import ConfirmIssueDialog from '@/components/ConfirmIssueDialog';
import GoodsReceivingDialog from '@/components/GoodsReceivingDialog';
import DispenseLedgerView from '@/components/DispenseLedgerView';
import { openPurchaseOrderPrintView } from '@/components/PurchaseOrderPrintView';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';

const LabelWithHelp = ({
  label,
  help,
  align = 'end',
}: {
  label: string;
  help: string;
  align?: 'start' | 'end';
}) => (
  <span
    className={cn(
      'inline-flex items-center gap-1',
      align === 'end' ? 'justify-end' : 'justify-start',
    )}
  >
    {label}
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex h-4 w-4 cursor-help items-center justify-center text-slate-400 hover:text-slate-600">
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="sr-only">{label} — info</span>
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs leading-relaxed">
          {help}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  </span>
);

const TabPurposeBanner = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-start gap-2.5 rounded-2xl border border-sky-100 bg-sky-50/70 px-4 py-3 text-sm leading-6 text-slate-600">
    <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-500" aria-hidden="true" />
    <span>{children}</span>
  </div>
);

type InventoryTab = 'room-requests' | 'warehouse' | 'purchase-orders' | 'catalog';
type WarehouseSubview = 'planning' | 'ordering' | 'pick-list' | 'requests' | 'stock-take';
type CatalogSubview = 'health' | 'browser' | 'suppliers' | 'ledger';
type CatalogSortKey = 'default' | 'onHand' | 'reorderLevel';
type SortDirection = 'asc' | 'desc';
type CatalogMetricModalId =
  | 'catalog-items'
  | 'supplier-coverage'
  | 'primary-coverage'
  | 'low-stock-watch'
  | 'supplier-contacts'
  | 'suppliers-total'
  | 'item-mappings'
  | 'contactable-suppliers'
  | 'supplier-search-results'
  | 'selected-item-stock-position'
  | 'selected-item-supplier-coverage'
  | 'selected-supplier-mapped-items'
  | 'selected-supplier-operational-averages';

interface CatalogMetricModalState {
  id: CatalogMetricModalId;
  supplierId?: string;
}

const CATALOG_ITEMS_PER_PAGE = 25;
const ORDERING_ITEMS_PER_PAGE = 25;
const SUGGESTED_REORDERS_COLLAPSED = 8;

const INVENTORY_TAB_ROUTES: Record<InventoryTab, string> = {
  'room-requests': '/inventory/room-requests',
  warehouse: '/inventory/warehouse',
  'purchase-orders': '/inventory/purchase-orders',
  catalog: '/inventory/catalog',
};

const resolveInventoryTabFromPath = (pathname: string): InventoryTab | null => {
  if (pathname.startsWith('/inventory/purchase-orders')) {
    return 'purchase-orders';
  }
  if (pathname.startsWith('/inventory/warehouse')) {
    return 'warehouse';
  }
  if (pathname.startsWith('/inventory/catalog')) {
    return 'catalog';
  }
  if (pathname.startsWith('/inventory/room-requests') || pathname === '/inventory') {
    return 'room-requests';
  }

  return null;
};

interface RequestDraftLine {
  quantity: number;
  note: string;
}

interface PendingInactiveRequest {
  itemId: string;
  quantity: number;
}

interface ItemSupplierSummary {
  supplierId: string | null;
  supplierName: string;
  supplierEmail: string | null;
  reorderLevel: number;
  minimumOrderQty: number;
  leadTimeDays: number | null;
}

interface CatalogSupplierLinkDetail extends ItemSupplierSummary {
  linkId: string;
  supplierPhone: string | null;
  contactPerson: string | null;
  isPrimary: boolean;
  supplierSku: string | null;
  supplierPackSize: number | null;
}

interface SupplierCatalogSummary {
  supplier: InventorySupplier;
  totalMappings: number;
  primaryMappings: number;
  mappedItems: InventoryItem[];
  lowStockItems: InventoryItem[];
  averageLeadTimeDays: number | null;
  averageReorderLevel: number | null;
}

interface CatalogMetricCardProps {
  title: string;
  value: React.ReactNode;
  detail: React.ReactNode;
  onClick?: () => void;
  className?: string;
  contentClassName?: string;
  valueClassName?: string;
}

interface WarehouseRow {
  id: string;
  rowType: 'request' | 'monitoring';
  item: InventoryItem;
  requestedQty: number;
  requestedTotalForItem: number;
  projectedRemaining: number;
  shortageQty: number;
  reorderLevel: number;
  supplier: ItemSupplierSummary;
  requestItemIds: string[];
  request: InventoryRoomRequest | null;
  requestItem: InventoryRoomRequestItem | null;
  room: InventoryRoom | null;
  priorityScore: number;
}

interface RoomPendingDemand {
  item: InventoryItem;
  requestedQty: number;
  fulfilledQty: number;
  outstandingQty: number;
  requestCount: number;
  latestSubmittedAt: string;
  statuses: Set<string>;
}

interface IssuePlanningRoomDemand {
  roomId: string;
  roomName: string;
  requestedQty: number;
  fulfilledQty: number;
  outstandingQty: number;
  recommendedIssueQty: number;
  plannedIssueQty: number;
  shortageQty: number;
  requestCount: number;
  requestItemIds: string[];
  isPlanEdited: boolean;
}

interface IssuePlanningRow {
  item: InventoryItem;
  supplier: ItemSupplierSummary;
  roomDemands: IssuePlanningRoomDemand[];
  totalRequestedQty: number;
  totalFulfilledQty: number;
  totalOutstandingQty: number;
  plannedIssueQty: number;
  shortageQty: number;
  onOrderQty: number;
  projectedRemaining: number;
  reorderLevel: number;
  status: 'ready_to_pick' | 'partial_plan' | 'hard_shortage' | 'on_order' | 'over_planned' | 'monitoring';
  priorityScore: number;
}

interface PickListLine {
  item: InventoryItem;
  supplier: ItemSupplierSummary;
  totalPickQty: number;
  roomDemands: IssuePlanningRoomDemand[];
}

const ROOM_REQUEST_STATUSES = new Set(['submitted', 'reviewing', 'partially_fulfilled']);
const ACTIVE_REQUEST_ITEM_STATUSES = new Set(['requested', 'approved', 'short', 'ordered']);
const ACTIVE_PO_STATUSES = new Set(['draft', 'ready', 'sent', 'acknowledged']);

const PO_STATUS_META: Record<string, string> = {
  draft: 'border-slate-300 bg-slate-100 text-slate-900',
  ready: 'border-blue-300 bg-blue-50 text-blue-900',
  sent: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  acknowledged: 'border-violet-300 bg-violet-50 text-violet-900',
  partially_received: 'border-amber-300 bg-amber-50 text-amber-900',
  fully_received: 'border-green-300 bg-green-50 text-green-900',
  cancelled: 'border-rose-300 bg-rose-50 text-rose-900',
};

const REQUEST_STATUS_META: Record<InventoryRoomRequest['status'], string> = {
  draft: 'border-slate-300 bg-slate-100 text-slate-900',
  submitted: 'border-blue-300 bg-blue-50 text-blue-900',
  reviewing: 'border-amber-300 bg-amber-50 text-amber-900',
  partially_fulfilled: 'border-orange-300 bg-orange-50 text-orange-900',
  fulfilled: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  cancelled: 'border-rose-300 bg-rose-50 text-rose-900',
};

const formatStamp = (value: string | null) => {
  if (!value) {
    return 'Not yet';
  }

  return format(parseISO(value), 'dd MMM yyyy, h:mm a');
};

const formatAgo = (value: string | null) => {
  if (!value) {
    return 'No activity yet';
  }

  return `${formatDistanceToNowStrict(parseISO(value), { addSuffix: true })}`;
};

const inventoryActivationLabel = (isActive: boolean) => (isActive ? 'Active' : 'Inactive');

const titleCaseStatus = (value: string) => value.replace(/_/g, ' ');

const getRequestItemOutstandingQty = (item: InventoryRoomRequestItem) =>
  Math.max(item.requested_qty - item.fulfilled_qty, 0);

const getIssuePlanKey = (itemId: string, roomId: string, requestItemIds: string[] = []) =>
  `${itemId}:${roomId}:${[...requestItemIds].sort().join('|')}`;

const isCurrentMonth = (value: string | null) => {
  if (!value) {
    return false;
  }

  const date = parseISO(value);
  const now = new Date();

  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
};

const distributeStockFairly = (
  roomDemands: Array<{ roomId: string; outstandingQty: number }>,
  stockOnHand: number
) => {
  const allocation = new Map<string, number>();
  const activeDemands = roomDemands
    .filter((demand) => demand.outstandingQty > 0)
    .sort((left, right) => left.roomId.localeCompare(right.roomId));
  let remainingStock = Math.max(Math.floor(stockOnHand), 0);

  activeDemands.forEach((demand) => {
    allocation.set(demand.roomId, 0);
  });

  while (remainingStock > 0) {
    const nextDemand = activeDemands.find((demand) => {
      const currentQty = allocation.get(demand.roomId) ?? 0;
      return currentQty < demand.outstandingQty;
    });

    if (!nextDemand) {
      break;
    }

    allocation.set(nextDemand.roomId, (allocation.get(nextDemand.roomId) ?? 0) + 1);
    remainingStock -= 1;
  }

  return allocation;
};

const formatSyncMode = (mode: InventorySyncProgress['mode']) =>
  mode === 'incremental' ? 'Incremental' : 'Full';

const formatSyncResource = (resource: InventorySyncProgress['resource']) =>
  resource === 'supplier' ? 'Supplier' : 'Inventory';

const byName = <T extends { name: string }>(left: T, right: T) =>
  left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });

const getPlatoSupplierReference = (item: InventoryItem) =>
  String(item.raw_plato_payload?.supplier ?? '').trim();

const CatalogMetricCard: React.FC<CatalogMetricCardProps> = ({
  title,
  value,
  detail,
  onClick,
  className,
  contentClassName,
  valueClassName,
}) => {
  const isInteractive = Boolean(onClick);

  return (
    <Card
      className={cn(
        'rounded-2xl border-slate-200 bg-slate-50 shadow-none',
        isInteractive ? 'transition-all hover:border-slate-300 hover:bg-white' : '',
        className
      )}
    >
      <button
        type="button"
        onClick={onClick}
        disabled={!isInteractive}
        className={cn(
          'block w-full rounded-2xl text-left',
          isInteractive
            ? 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2'
            : 'cursor-default'
        )}
      >
        <CardContent className={cn('px-4 py-4', contentClassName)}>
          <p className="text-xs uppercase tracking-[0.16em] text-slate-500">{title}</p>
          <p className={cn('mt-2 text-2xl font-semibold text-slate-950', valueClassName)}>{value}</p>
          <p className="mt-1 text-xs text-slate-500">{detail}</p>
          {isInteractive ? (
            <div className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-slate-600">
              <span>View details</span>
              <ArrowUpRight className="h-3.5 w-3.5" />
            </div>
          ) : null}
        </CardContent>
      </button>
    </Card>
  );
};

const InventoryView: React.FC = () => {
  const { toast } = useToast();
  const { currentClinic } = useClinic();
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();
  const currentClinicId = currentClinic?.id ?? null;

  const canSeeWarehouse = ['admin', 'operations_manager', 'office_admin'].includes(
    currentUser?.role ?? ''
  );
  const canSeeCatalog = canSeeWarehouse;
  const isManager = ['admin', 'operations_manager'].includes(currentUser?.role ?? '');
  const defaultTab: InventoryTab = 'room-requests';

  const [activeTab, setActiveTab] = useState<InventoryTab>(defaultTab);
  const [warehouseSubview, setWarehouseSubview] = useState<WarehouseSubview>('planning');
  const [catalogSubview, setCatalogSubview] = useState<CatalogSubview>('health');
  const [snapshot, setSnapshot] = useState<InventoryWorkflowSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  // Stock-take / cycle-count state.
  const [stockTakeSessions, setStockTakeSessions] = useState<StockTakeSession[]>([]);
  const [selectedStockTakeSessionId, setSelectedStockTakeSessionId] = useState<string | null>(null);
  const [stockTakeLines, setStockTakeLines] = useState<StockTakeLine[]>([]);
  // Local draft of counted qty keyed by inventory item id for the active session.
  const [stockTakeDraft, setStockTakeDraft] = useState<Record<string, string>>({});
  const [stockTakeFilter, setStockTakeFilter] = useState('');
  const [creatingStockTake, setCreatingStockTake] = useState(false);
  const [applyingStockTake, setApplyingStockTake] = useState(false);
  const [savingStockTakeItemId, setSavingStockTakeItemId] = useState<string | null>(null);
  const [confirmApplyStockTakeOpen, setConfirmApplyStockTakeOpen] = useState(false);
  const [stockTakePreview, setStockTakePreview] = useState<StockTakeVariancePreview[]>([]);
  const [loadingStockTakePreview, setLoadingStockTakePreview] = useState(false);
  const [cycleCountDue, setCycleCountDue] = useState<CycleCountDueItem[]>([]);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const submittingRequestRef = useRef(false);
  // Per-batch idempotency keys for issue-batch confirmation: a retry of the
  // same batch reuses the same key so stock isn't double-deducted.
  const issueBatchIdempotencyKeys = useRef<Record<string, string>>({});
  const [creatingOrders, setCreatingOrders] = useState(false);
  // Direct-PO dialog (backlog item #15) — lets staff create a PO without
  // going through a room request first.
  const [directPoDialogOpen, setDirectPoDialogOpen] = useState(false);
  // Prefill for the direct-PO dialog when opened from the Suggested reorders
  // board (backlog item #14). Cleared whenever the dialog closes so a plain
  // "New purchase order" click always starts blank.
  const [directPoInitialDraft, setDirectPoInitialDraft] =
    useState<NewPurchaseOrderDraftPrefill | null>(null);
  const [confirmIssueRequest, setConfirmIssueRequest] = useState<{ id: string; roomName: string } | null>(null);
  const [receivingPO, setReceivingPO] = useState<{ id: string; poNumber: string; supplierName: string } | null>(null);
  const [creatingIssueBatch, setCreatingIssueBatch] = useState(false);
  const [confirmingIssueBatch, setConfirmingIssueBatch] = useState(false);
  const [retryingIssueBatchId, setRetryingIssueBatchId] = useState<string | null>(null);
  const [syncingCatalog, setSyncingCatalog] = useState(false);
  const [syncingSuppliers, setSyncingSuppliers] = useState(false);
  const [syncProgress, setSyncProgress] = useState<InventorySyncProgress | null>(null);
  const [updatingItemIds, setUpdatingItemIds] = useState<Record<string, boolean>>({});
  const [roomSearch, setRoomSearch] = useState('');
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const [selectedWarehouseRoomId, setSelectedWarehouseRoomId] = useState<string>('all');
  const [selectedWarehouseRequestId, setSelectedWarehouseRequestId] = useState<string>('');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [currentCatalogPage, setCurrentCatalogPage] = useState(1);
  const [catalogSortKey, setCatalogSortKey] = useState<CatalogSortKey>('default');
  const [catalogSortDirection, setCatalogSortDirection] =
    useState<SortDirection>('desc');
  const [selectedRoomId, setSelectedRoomId] = useState<string>('');
  const [selectedCatalogItemId, setSelectedCatalogItemId] = useState<string>('');
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
  const [requestNotes, setRequestNotes] = useState('');
  const [requestDraft, setRequestDraft] = useState<Record<string, RequestDraftLine>>({});
  const [issuePlanOverrides, setIssuePlanOverrides] = useState<Record<string, number>>({});
  const [selectedIssueBatchId, setSelectedIssueBatchId] = useState('');
  const [confirmIssueQuantities, setConfirmIssueQuantities] = useState<Record<string, number>>({});
  const [confirmIssueReasons, setConfirmIssueReasons] = useState<Record<string, string>>({});
  const [pendingInactiveRequest, setPendingInactiveRequest] =
    useState<PendingInactiveRequest | null>(null);
  const [selectedWarehouseItems, setSelectedWarehouseItems] = useState<Record<string, boolean>>({});
  const [catalogMetricModal, setCatalogMetricModal] =
    useState<CatalogMetricModalState | null>(null);
  const [suggestingPoItemIds, setSuggestingPoItemIds] = useState<Record<string, boolean>>({});
  const [planningShortagesOnly, setPlanningShortagesOnly] = useState(false);
  const [planningShowStockDetails, setPlanningShowStockDetails] = useState(false);
  const [orderingShowAll, setOrderingShowAll] = useState(false);
  const [orderingPage, setOrderingPage] = useState(1);
  const [suggestedReordersExpanded, setSuggestedReordersExpanded] = useState(false);
  const [reorderNeedsSetupExpanded, setReorderNeedsSetupExpanded] = useState(false);
  const [orderUnitDraft, setOrderUnitDraft] = useState('');
  const [packSizeDraft, setPackSizeDraft] = useState('');
  const [savingOrderConfig, setSavingOrderConfig] = useState(false);

  const deferredRoomSearch = useDeferredValue(roomSearch);
  const deferredWarehouseSearch = useDeferredValue(warehouseSearch);
  const deferredCatalogSearch = useDeferredValue(catalogSearch);
  const deferredSupplierSearch = useDeferredValue(supplierSearch);

  const loadSnapshot = useCallback(async () => {
    try {
      setLoading(true);
      const nextSnapshot = await inventoryService.getWorkflowSnapshot(currentClinicId ?? undefined);
      setSnapshot(nextSnapshot);
    } catch (error) {
      console.error('Failed to load inventory workflow snapshot:', error);
      toast({
        title: 'Inventory unavailable',
        description: 'The new inventory workflow could not load its live data.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [currentClinicId, toast]);

  useEffect(() => {
    void loadSnapshot();
  }, [loadSnapshot]);

  const isInventoryPath = useMemo(
    () => location.pathname === '/inventory' || location.pathname.startsWith('/inventory/'),
    [location.pathname]
  );
  const routedTab = useMemo(() => resolveInventoryTabFromPath(location.pathname), [location.pathname]);

  useEffect(() => {
    if (!isInventoryPath) {
      return;
    }

    const nextTab = routedTab ?? defaultTab;
    const restrictedTab = (!canSeeWarehouse && nextTab !== 'room-requests') || (!canSeeCatalog && nextTab === 'catalog');

    if (restrictedTab) {
      navigate(INVENTORY_TAB_ROUTES['room-requests'], { replace: true });
      return;
    }

    if (!routedTab) {
      navigate(INVENTORY_TAB_ROUTES[nextTab], { replace: true });
      return;
    }

    if (activeTab !== nextTab) {
      setActiveTab(nextTab);
    }
  }, [activeTab, canSeeCatalog, canSeeWarehouse, defaultTab, isInventoryPath, navigate, routedTab]);

  const rooms = useMemo(() => snapshot?.rooms ?? [], [snapshot]);
  const items = useMemo(() => snapshot?.items ?? [], [snapshot]);

  // -- Stock-take / cycle-count ------------------------------------------
  const loadStockTakeSessions = useCallback(async () => {
    try {
      const sessions = await inventoryService.listStockTakeSessions();
      setStockTakeSessions(sessions);
    } catch (error) {
      console.error('Failed to load stock-take sessions:', error);
    }
  }, []);

  useEffect(() => {
    if (warehouseSubview === 'stock-take') {
      void loadStockTakeSessions();
    }
  }, [warehouseSubview, loadStockTakeSessions]);

  const loadStockTakeLines = useCallback(async (sessionId: string) => {
    try {
      const lines = await inventoryService.listStockTakeLines(sessionId);
      setStockTakeLines(lines);
      const draft: Record<string, string> = {};
      lines.forEach((line) => {
        draft[line.inventory_item_id] = String(line.counted_qty);
      });
      setStockTakeDraft(draft);
    } catch (error) {
      console.error('Failed to load stock-take lines:', error);
    }
  }, []);

  useEffect(() => {
    if (selectedStockTakeSessionId) {
      void loadStockTakeLines(selectedStockTakeSessionId);
    } else {
      setStockTakeLines([]);
      setStockTakeDraft({});
    }
  }, [selectedStockTakeSessionId, loadStockTakeLines]);

  const selectedStockTakeSession = useMemo(
    () => stockTakeSessions.find((session) => session.id === selectedStockTakeSessionId) ?? null,
    [stockTakeSessions, selectedStockTakeSessionId]
  );

  const stockTakeLineByItemId = useMemo(() => {
    const map = new Map<string, StockTakeLine>();
    stockTakeLines.forEach((line) => map.set(line.inventory_item_id, line));
    return map;
  }, [stockTakeLines]);

  // Catalog rows for the count sheet (stock-tracked + active items).
  const stockTakeCatalogItems = useMemo(() => {
    const term = stockTakeFilter.trim().toLowerCase();
    return items.filter((item) => {
      if (!item.is_active) return false;
      if (!term) return true;
      return (
        item.name.toLowerCase().includes(term) ||
        (item.category ?? '').toLowerCase().includes(term) ||
        (item.given_id ?? '').toLowerCase().includes(term)
      );
    });
  }, [items, stockTakeFilter]);

  const stockTakeVarianceCount = useMemo(
    () => stockTakeLines.filter((line) => line.variance !== 0).length,
    [stockTakeLines]
  );

  const handleCreateStockTakeSession = useCallback(async () => {
    if (!currentUser) {
      toast({
        title: 'Sign in required',
        description: 'Your session is no longer valid. Please log in again.',
        variant: 'destructive',
      });
      return;
    }
    try {
      setCreatingStockTake(true);
      const session = await inventoryService.createStockTakeSession({
        createdBy: currentUser.id,
        createdByEmail: currentUser.email,
      });
      await loadStockTakeSessions();
      setSelectedStockTakeSessionId(session.id);
      toast({
        title: 'Stock-take session started',
        description: 'Enter the physically counted quantity for each item, then apply the adjustments.',
      });
    } catch (error) {
      console.error('Failed to start stock-take session:', error);
      toast({
        title: 'Could not start stock-take',
        description: error instanceof Error ? error.message : String(error),
        variant: 'destructive',
      });
    } finally {
      setCreatingStockTake(false);
    }
  }, [currentUser, loadStockTakeSessions, toast]);

  const handleSaveStockTakeLine = useCallback(
    async (item: InventoryItem) => {
      if (!selectedStockTakeSessionId) return;
      const raw = stockTakeDraft[item.id];
      if (raw == null || raw.trim() === '') return;
      const countedQty = Number(raw);
      if (!Number.isFinite(countedQty) || countedQty < 0) {
        toast({
          title: 'Invalid count',
          description: 'Counted quantity must be a number of zero or more.',
          variant: 'destructive',
        });
        return;
      }
      try {
        setSavingStockTakeItemId(item.id);
        await inventoryService.saveStockTakeLine({
          sessionId: selectedStockTakeSessionId,
          inventoryItemId: item.id,
          countedQty,
          systemQtySnapshot: item.warehouse_on_hand,
        });
        await loadStockTakeLines(selectedStockTakeSessionId);
      } catch (error) {
        console.error('Failed to save stock-take line:', error);
        toast({
          title: 'Could not save count',
          description: error instanceof Error ? error.message : String(error),
          variant: 'destructive',
        });
      } finally {
        setSavingStockTakeItemId(null);
      }
    },
    [selectedStockTakeSessionId, stockTakeDraft, loadStockTakeLines, toast]
  );

  // Load the read-only variance preview before showing the apply-confirm dialog,
  // so the counter sees exactly what will change and which lines are MAJOR.
  const openApplyStockTakeConfirm = useCallback(async () => {
    if (!selectedStockTakeSessionId) return;
    setConfirmApplyStockTakeOpen(true);
    try {
      setLoadingStockTakePreview(true);
      const preview = await inventoryService.previewStockTake(selectedStockTakeSessionId);
      setStockTakePreview(preview.filter((row) => !row.applied));
    } catch (error) {
      console.error('Failed to load variance preview:', error);
      setStockTakePreview([]);
    } finally {
      setLoadingStockTakePreview(false);
    }
  }, [selectedStockTakeSessionId]);

  const stockTakeMajorPreview = useMemo(
    () => stockTakePreview.filter((row) => row.is_major && row.variance !== 0),
    [stockTakePreview]
  );

  const handleApplyStockTake = useCallback(async () => {
    if (!selectedStockTakeSessionId || !currentUser) return;
    const hasMajor = stockTakeMajorPreview.length > 0;
    if (hasMajor && !isManager) {
      toast({
        title: 'Manager approval required',
        description:
          'This stock-take has major variances. Only an admin or operations manager can confirm them.',
        variant: 'destructive',
      });
      return;
    }
    try {
      setApplyingStockTake(true);
      const result = await inventoryService.applyStockTake({
        sessionId: selectedStockTakeSessionId,
        appliedBy: currentUser.id,
        appliedByEmail: currentUser.email,
        // Major variances require the explicit force flag; small variances ignore it.
        force: hasMajor,
      });
      setConfirmApplyStockTakeOpen(false);
      await Promise.all([loadSnapshot(), loadStockTakeSessions()]);
      await loadStockTakeLines(selectedStockTakeSessionId);
      toast({
        title: 'Stock-take applied',
        description: `${result?.lines_applied ?? 0} item(s) reconciled. On-hand updated and the session is closed.`,
      });
    } catch (error) {
      console.error('Failed to apply stock-take:', error);
      toast({
        title: 'Could not apply adjustments',
        description: error instanceof Error ? error.message : String(error),
        variant: 'destructive',
      });
    } finally {
      setApplyingStockTake(false);
    }
  }, [selectedStockTakeSessionId, currentUser, stockTakeMajorPreview, isManager, loadSnapshot, loadStockTakeSessions, loadStockTakeLines, toast]);

  const loadCycleCountDue = useCallback(async () => {
    try {
      const due = await inventoryService.listCycleCountDue(8);
      setCycleCountDue(due);
    } catch (error) {
      console.error('Failed to load cycle-count worklist:', error);
    }
  }, []);

  useEffect(() => {
    if (warehouseSubview === 'stock-take') {
      void loadCycleCountDue();
    }
  }, [warehouseSubview, loadCycleCountDue, stockTakeSessions]);
  // ----------------------------------------------------------------------

  const suppliers = useMemo(() => snapshot?.suppliers ?? [], [snapshot]);
  const itemSuppliers = useMemo(() => snapshot?.itemSuppliers ?? [], [snapshot]);
  const requests = useMemo(() => snapshot?.requests ?? [], [snapshot]);
  const requestItems = useMemo(() => snapshot?.requestItems ?? [], [snapshot]);
  const purchaseOrders = useMemo(() => snapshot?.purchaseOrders ?? [], [snapshot]);
  const purchaseOrderItems = useMemo(() => snapshot?.purchaseOrderItems ?? [], [snapshot]);
  const issueBatches = useMemo(() => snapshot?.issueBatches ?? [], [snapshot]);
  const issueBatchItems = useMemo(() => snapshot?.issueBatchItems ?? [], [snapshot]);
  const latestSyncLog = snapshot?.latestSyncLog ?? null;

  const supplierById = useMemo(() => {
    return new Map(suppliers.map((supplier) => [supplier.id, supplier]));
  }, [suppliers]);

  const itemById = useMemo(() => {
    return new Map(items.map((item) => [item.id, item]));
  }, [items]);

  const roomById = useMemo(() => {
    return new Map(rooms.map((room) => [room.id, room]));
  }, [rooms]);

  const requestItemsByRequestId = useMemo(() => {
    const groups = new Map<string, InventoryRoomRequestItem[]>();

    requestItems.forEach((item) => {
      const current = groups.get(item.room_request_id) ?? [];
      current.push(item);
      groups.set(item.room_request_id, current);
    });

    return groups;
  }, [requestItems]);

  const issueBatchItemsByBatchId = useMemo(() => {
    const groups = new Map<string, InventoryIssueBatchItem[]>();

    issueBatchItems.forEach((item) => {
      const current = groups.get(item.issue_batch_id) ?? [];
      current.push(item);
      groups.set(item.issue_batch_id, current);
    });

    return groups;
  }, [issueBatchItems]);

  const selectedIssueBatch = useMemo(() => {
    return issueBatches.find((batch) => batch.id === selectedIssueBatchId) ?? issueBatches[0] ?? null;
  }, [issueBatches, selectedIssueBatchId]);

  const selectedIssueBatchItems = useMemo(() => {
    if (!selectedIssueBatch) {
      return [];
    }

    return issueBatchItemsByBatchId.get(selectedIssueBatch.id) ?? [];
  }, [issueBatchItemsByBatchId, selectedIssueBatch]);

  useEffect(() => {
    if (selectedIssueBatchId || issueBatches.length === 0) {
      return;
    }

    setSelectedIssueBatchId(issueBatches[0].id);
  }, [issueBatches, selectedIssueBatchId]);

  useEffect(() => {
    if (!selectedIssueBatch || selectedIssueBatch.confirmed_at) {
      return;
    }

    setConfirmIssueQuantities((current) => {
      const next = { ...current };
      selectedIssueBatchItems.forEach((item) => {
        if (next[item.id] === undefined) {
          next[item.id] = item.planned_qty;
        }
      });
      return next;
    });
  }, [selectedIssueBatch, selectedIssueBatchItems]);

  const primarySupplierByItemId = useMemo(() => {
    const groups = new Map<string, InventoryItemSupplier[]>();

    itemSuppliers.forEach((link) => {
      const current = groups.get(link.inventory_item_id) ?? [];
      current.push(link);
      groups.set(link.inventory_item_id, current);
    });

    const summary = new Map<string, ItemSupplierSummary>();

    items.forEach((item) => {
      const links = groups.get(item.id) ?? [];
      const primaryLink = links.find((link) => link.is_primary) ?? links[0] ?? null;
      const supplier = primaryLink
        ? supplierById.get(primaryLink.inventory_supplier_id) ?? null
        : null;

      summary.set(item.id, {
        supplierId: primaryLink?.inventory_supplier_id ?? null,
        supplierName: supplier?.name ?? 'Supplier mapping needed',
        supplierEmail: supplier?.email ?? null,
        reorderLevel: primaryLink?.reorder_level ?? 0,
        minimumOrderQty: primaryLink?.minimum_order_qty ?? 0,
        leadTimeDays: primaryLink?.lead_time_days ?? null,
      });
    });

    return summary;
  }, [itemSuppliers, items, supplierById]);

  /** Per-item PO unit-cost history, newest first (backlog item #8). */
  const priceHistoryByItemId = useMemo(
    () => buildPriceHistoryByItemId(purchaseOrders, purchaseOrderItems),
    [purchaseOrders, purchaseOrderItems]
  );

  /**
   * Suggested reorders (backlog item #14, simple logic): active catalog items
   * with an active supplier mapping whose reorder level is set and whose
   * warehouse stock has fallen to or below it. Pure client-side read of the
   * snapshot — no cron, no notifications.
   */
  const suggestedReorders = useMemo(() => {
    return items
      .filter((item) => item.is_active)
      .map((item) => {
        const supplier = primarySupplierByItemId.get(item.id);
        if (!supplier?.supplierId || supplier.reorderLevel <= 0) {
          return null;
        }
        if (item.warehouse_on_hand > supplier.reorderLevel) {
          return null;
        }
        const lastCost = priceHistoryByItemId.get(item.id)?.[0] ?? null;
        return {
          item,
          supplier,
          lastCostCents: lastCost?.unitCostCents ?? null,
          deficit: supplier.reorderLevel - item.warehouse_on_hand,
        };
      })
      .filter((row): row is NonNullable<typeof row> => row != null)
      .sort(
        (left, right) =>
          right.deficit - left.deficit ||
          left.item.name.localeCompare(right.item.name, undefined, { sensitivity: 'base' })
      );
  }, [items, primarySupplierByItemId, priceHistoryByItemId]);

  /**
   * A3 (reorder NULL-vs-unset): active, requestable, supplier-linked items that
   * have NO usable reorder rule (reorderLevel is null/0). These are silently
   * absent from the suggestion board today; surfacing them as a separate
   * "needs reorder setup" list means an unset threshold is visible rather than
   * mistaken for "nothing to order".
   */
  const reorderNeedsSetup = useMemo(() => {
    return items
      .filter((item) => item.is_active)
      .map((item) => {
        const supplier = primarySupplierByItemId.get(item.id);
        // Only items that DO have a primary supplier but lack a reorder level.
        if (!supplier?.supplierId) {
          return null;
        }
        if (supplier.reorderLevel > 0) {
          return null;
        }
        return { item, supplier };
      })
      .filter((row): row is NonNullable<typeof row> => row != null)
      .sort((left, right) =>
        left.item.name.localeCompare(right.item.name, undefined, { sensitivity: 'base' })
      );
  }, [items, primarySupplierByItemId]);

  const itemSupplierLinksByItemId = useMemo(() => {
    const groups = new Map<string, CatalogSupplierLinkDetail[]>();

    itemSuppliers.forEach((link) => {
      const supplier = supplierById.get(link.inventory_supplier_id);
      const current = groups.get(link.inventory_item_id) ?? [];

      current.push({
        linkId: link.id,
        supplierId: link.inventory_supplier_id,
        supplierName: supplier?.name ?? 'Unknown supplier',
        supplierEmail: supplier?.email ?? null,
        supplierPhone: supplier?.telephone ?? supplier?.handphone ?? null,
        contactPerson: supplier?.contact_person ?? null,
        reorderLevel: link.reorder_level ?? 0,
        minimumOrderQty: link.minimum_order_qty ?? 0,
        leadTimeDays: link.lead_time_days ?? null,
        isPrimary: link.is_primary,
        supplierSku: link.supplier_sku ?? null,
        supplierPackSize: link.supplier_pack_size ?? null,
      });

      groups.set(link.inventory_item_id, current);
    });

    groups.forEach((links) => {
      links.sort((left, right) => {
        if (left.isPrimary !== right.isPrimary) {
          return left.isPrimary ? -1 : 1;
        }

        return left.supplierName.localeCompare(right.supplierName, undefined, {
          sensitivity: 'base',
        });
      });
    });

    return groups;
  }, [itemSuppliers, supplierById]);

  const supplierCatalogSummaries = useMemo(() => {
    return suppliers
      .map((supplier) => {
        const links = itemSuppliers.filter(
          (link) => link.inventory_supplier_id === supplier.id
        );
        const mappedItems = links
          .map((link) => itemById.get(link.inventory_item_id))
          .filter((item): item is InventoryItem => Boolean(item));
        const lowStockItems = mappedItems.filter((item) => {
          const primaryLink = itemSupplierLinksByItemId
            .get(item.id)
            ?.find((link) => link.supplierId === supplier.id && link.isPrimary);
          const reorderLevel = primaryLink?.reorderLevel ?? 0;

          return reorderLevel > 0 && item.warehouse_on_hand <= reorderLevel;
        });
        const leadTimes = links
          .map((link) => link.lead_time_days)
          .filter((value): value is number => value != null && value > 0);
        const reorderLevels = links
          .map((link) => link.reorder_level)
          .filter((value): value is number => value != null && value > 0);

        return {
          supplier,
          totalMappings: links.length,
          primaryMappings: links.filter((link) => link.is_primary).length,
          mappedItems,
          lowStockItems,
          averageLeadTimeDays: leadTimes.length
            ? Math.round(
                leadTimes.reduce((sum, value) => sum + value, 0) / leadTimes.length
              )
            : null,
          averageReorderLevel: reorderLevels.length
            ? Math.round(
                reorderLevels.reduce((sum, value) => sum + value, 0) /
                  reorderLevels.length
              )
            : null,
        } satisfies SupplierCatalogSummary;
      })
      .sort((left, right) => {
        if (left.totalMappings !== right.totalMappings) {
          return right.totalMappings - left.totalMappings;
        }

        return left.supplier.name.localeCompare(right.supplier.name, undefined, {
          sensitivity: 'base',
        });
      });
  }, [itemById, itemSupplierLinksByItemId, itemSuppliers, suppliers]);

  const accessibleRooms = useMemo(() => {
    if (canSeeWarehouse) {
      return [...rooms].sort(byName);
    }

    const accessRows = snapshot?.roomAccess.filter((row) => row.user_id === currentUser?.id && row.can_request) ?? [];
    const mappedRooms = accessRows
      .map((row) => roomById.get(row.room_id))
      .filter((room): room is InventoryRoom => Boolean(room));

    if (mappedRooms.length > 0) {
      return mappedRooms.sort(byName);
    }

    const roomLikeFallback = rooms
      .filter((room) => /room/i.test(room.name))
      .sort(byName);

    return roomLikeFallback.length > 0 ? roomLikeFallback : [...rooms].sort(byName);
  }, [canSeeWarehouse, currentUser?.id, roomById, rooms, snapshot?.roomAccess]);

  useEffect(() => {
    if (!accessibleRooms.length) {
      setSelectedRoomId('');
      return;
    }

    if (!selectedRoomId || !accessibleRooms.some((room) => room.id === selectedRoomId)) {
      setSelectedRoomId(accessibleRooms[0].id);
    }
  }, [accessibleRooms, selectedRoomId]);

  const selectedRoom = useMemo(() => {
    return accessibleRooms.find((room) => room.id === selectedRoomId) ?? accessibleRooms[0] ?? null;
  }, [accessibleRooms, selectedRoomId]);

  const filteredRequestableItems = useMemo(() => {
    const normalizedSearch = deferredRoomSearch.trim().toLowerCase();

    return items
      .filter((item) => item.is_requestable)
      .filter((item) => {
        if (!normalizedSearch) {
          return true;
        }

        return (
          item.name.toLowerCase().includes(normalizedSearch) ||
          (item.given_id ?? '').toLowerCase().includes(normalizedSearch) ||
          (item.category ?? '').toLowerCase().includes(normalizedSearch) ||
          (item.manufacturer ?? '').toLowerCase().includes(normalizedSearch)
        );
      })
      .sort((left, right) => {
        if (left.is_active !== right.is_active) {
          return left.is_active ? -1 : 1;
        }

        return left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });
      })
      .slice(0, 80);
  }, [deferredRoomSearch, items]);

  const draftLines = useMemo(() => {
    return Object.entries(requestDraft)
      .map(([itemId, draft]) => {
        const item = itemById.get(itemId);
        if (!item || draft.quantity <= 0) {
          return null;
        }

        return { item, draft };
      })
      .filter((entry): entry is { item: InventoryItem; draft: RequestDraftLine } => Boolean(entry));
  }, [itemById, requestDraft]);

  const roomRequests = useMemo(() => {
    if (!selectedRoom) {
      return [];
    }

    return requests
      .filter((request) => request.room_id === selectedRoom.id)
      .sort((left, right) => right.created_at.localeCompare(left.created_at));
  }, [requests, selectedRoom]);

  const monthlyRoomRequests = useMemo(() => {
    return roomRequests.filter((request) =>
      isCurrentMonth(request.submitted_at || request.created_at)
    );
  }, [roomRequests]);

  const roomPendingDemands = useMemo(() => {
    if (!selectedRoom) {
      return [];
    }

    const activeRequestIds = new Set(
      roomRequests
        .filter((request) => ROOM_REQUEST_STATUSES.has(request.status))
        .map((request) => request.id)
    );
    const requestById = new Map(roomRequests.map((request) => [request.id, request]));
    const demandByItemId = new Map<string, RoomPendingDemand>();

    requestItems.forEach((requestItem) => {
      if (!activeRequestIds.has(requestItem.room_request_id)) {
        return;
      }

      if (!ACTIVE_REQUEST_ITEM_STATUSES.has(requestItem.status)) {
        return;
      }

      const outstandingQty = getRequestItemOutstandingQty(requestItem);
      if (outstandingQty <= 0) {
        return;
      }

      const item = itemById.get(requestItem.inventory_item_id);
      const request = requestById.get(requestItem.room_request_id);
      if (!item || !request) {
        return;
      }

      const latestSubmittedAt = request.submitted_at || request.created_at;
      const current = demandByItemId.get(item.id) ?? {
        item,
        requestedQty: 0,
        fulfilledQty: 0,
        outstandingQty: 0,
        requestCount: 0,
        latestSubmittedAt,
        statuses: new Set<string>(),
      };

      current.requestedQty += requestItem.requested_qty;
      current.fulfilledQty += requestItem.fulfilled_qty;
      current.outstandingQty += outstandingQty;
      current.requestCount += 1;
      current.statuses.add(requestItem.status);
      if (latestSubmittedAt.localeCompare(current.latestSubmittedAt) > 0) {
        current.latestSubmittedAt = latestSubmittedAt;
      }

      demandByItemId.set(item.id, current);
    });

    return [...demandByItemId.values()].sort((left, right) =>
      right.latestSubmittedAt.localeCompare(left.latestSubmittedAt)
    );
  }, [itemById, requestItems, roomRequests, selectedRoom]);

  const warehouseFilterRooms = useMemo(() => {
    return [...rooms].sort(byName);
  }, [rooms]);

  const warehouseRequestForms = useMemo(() => {
    const normalizedSearch = deferredWarehouseSearch.trim().toLowerCase();

    return requests
      .filter((request) => ROOM_REQUEST_STATUSES.has(request.status))
      .filter((request) => {
        return selectedWarehouseRoomId === 'all' || request.room_id === selectedWarehouseRoomId;
      })
      .map((request) => {
        const lines = (requestItemsByRequestId.get(request.id) ?? [])
          .map((line) => ({
            ...line,
            item: itemById.get(line.inventory_item_id) ?? null,
          }))
          .sort((left, right) => {
            const leftName = left.item?.name ?? '';
            const rightName = right.item?.name ?? '';
            return leftName.localeCompare(rightName, undefined, { sensitivity: 'base' });
          });
        const room = roomById.get(request.room_id) ?? null;
        const totalRequestedQty = lines.reduce((sum, line) => sum + line.requested_qty, 0);
        const shortageLineCount = lines.filter((line) => line.shortage_qty > 0).length;

        return {
          request,
          room,
          lines,
          totalRequestedQty,
          shortageLineCount,
        };
      })
      .filter(({ request, room, lines }) => {
        if (!normalizedSearch) {
          return true;
        }

        return (
          (room?.name ?? '').toLowerCase().includes(normalizedSearch) ||
          request.id.toLowerCase().includes(normalizedSearch) ||
          lines.some((line) => {
            const itemName = line.item?.name ?? '';
            const itemCode = line.item?.given_id ?? '';
            return (
              itemName.toLowerCase().includes(normalizedSearch) ||
              itemCode.toLowerCase().includes(normalizedSearch)
            );
          })
        );
      })
      .sort((left, right) => {
        const leftStamp = left.request.submitted_at || left.request.created_at;
        const rightStamp = right.request.submitted_at || right.request.created_at;
        return rightStamp.localeCompare(leftStamp);
      });
  }, [
    deferredWarehouseSearch,
    itemById,
    requestItemsByRequestId,
    requests,
    roomById,
    selectedWarehouseRoomId,
  ]);

  const warehouseRequestRoomGroups = useMemo(() => {
    const groups = new Map<
      string,
      {
        roomId: string;
        room: InventoryRoom | null;
        forms: typeof warehouseRequestForms;
        totalLines: number;
        totalRequestedQty: number;
        totalOutstandingQty: number;
        shortageLineCount: number;
        latestSubmittedAt: string;
        itemSummaries: Map<
          string,
          {
            itemId: string;
            itemName: string;
            givenId: string | null;
            requestedQty: number;
            outstandingQty: number;
            lineCount: number;
          }
        >;
      }
    >();

    warehouseRequestForms.forEach((form) => {
      const roomId = form.request.room_id;
      const submittedAt = form.request.submitted_at || form.request.created_at;
      const group = groups.get(roomId) ?? {
        roomId,
        room: form.room,
        forms: [],
        totalLines: 0,
        totalRequestedQty: 0,
        totalOutstandingQty: 0,
        shortageLineCount: 0,
        latestSubmittedAt: submittedAt,
        itemSummaries: new Map(),
      };

      group.forms.push(form);
      group.totalLines += form.lines.length;
      group.totalRequestedQty += form.totalRequestedQty;
      group.shortageLineCount += form.shortageLineCount;

      if (submittedAt.localeCompare(group.latestSubmittedAt) > 0) {
        group.latestSubmittedAt = submittedAt;
      }

      form.lines.forEach((line) => {
        const itemId = line.inventory_item_id;
        const itemSummary = group.itemSummaries.get(itemId) ?? {
          itemId,
          itemName: line.item?.name ?? 'Unknown item',
          givenId: line.item?.given_id ?? null,
          requestedQty: 0,
          outstandingQty: 0,
          lineCount: 0,
        };
        const outstandingQty = getRequestItemOutstandingQty(line);

        itemSummary.requestedQty += line.requested_qty;
        itemSummary.outstandingQty += outstandingQty;
        itemSummary.lineCount += 1;
        group.totalOutstandingQty += outstandingQty;
        group.itemSummaries.set(itemId, itemSummary);
      });

      groups.set(roomId, group);
    });

    return [...groups.values()]
      .map((group) => ({
        ...group,
        itemSummaries: [...group.itemSummaries.values()].sort((left, right) => {
          if (left.outstandingQty !== right.outstandingQty) {
            return right.outstandingQty - left.outstandingQty;
          }

          return left.itemName.localeCompare(right.itemName, undefined, {
            sensitivity: 'base',
          });
        }),
      }))
      .sort((left, right) => {
        const leftName = left.room?.name ?? 'Unknown room';
        const rightName = right.room?.name ?? 'Unknown room';

        return leftName.localeCompare(rightName, undefined, { sensitivity: 'base' });
      });
  }, [warehouseRequestForms]);

  const purchaseOrderQtyByItemId = useMemo(() => {
    const activeOrderIds = new Set(
      purchaseOrders
        .filter((purchaseOrder) => ACTIVE_PO_STATUSES.has(purchaseOrder.status))
        .map((purchaseOrder) => purchaseOrder.id)
    );
    const quantities = new Map<string, number>();

    purchaseOrderItems.forEach((line) => {
      if (!activeOrderIds.has(line.purchase_order_id)) {
        return;
      }

      const itemId = line.inventory_item_id;
      if (!itemId) {
        // Free-text direct-PO lines have no catalog FK — skip on-order tally.
        return;
      }

      quantities.set(itemId, (quantities.get(itemId) ?? 0) + line.qty_to_order);
    });

    return quantities;
  }, [purchaseOrderItems, purchaseOrders]);

  // Last-known PO line per inventory item, used to prefill "Suggest PO" drafts
  // for low-stock items (backlog #14, simple-logic v1: no usage/lead-time forecasting).
  const lastPurchaseOrderLineByItemId = useMemo(() => {
    const orderById = new Map(purchaseOrders.map((order) => [order.id, order]));
    const lastLine = new Map<
      string,
      {
        supplierId: string;
        qtyToOrder: number;
        unitCostSnapshot: number | null;
        createdAt: string;
      }
    >();

    purchaseOrderItems.forEach((line) => {
      const order = orderById.get(line.purchase_order_id);
      if (!order) {
        return;
      }

      const existing = lastLine.get(line.inventory_item_id);
      if (existing && existing.createdAt >= order.created_at) {
        return;
      }

      lastLine.set(line.inventory_item_id, {
        supplierId: order.supplier_id,
        qtyToOrder: line.qty_to_order,
        unitCostSnapshot: line.unit_cost_snapshot ?? null,
        createdAt: order.created_at,
      });
    });

    return lastLine;
  }, [purchaseOrderItems, purchaseOrders]);

  const issuePlanningRows = useMemo(() => {
    const normalizedSearch = deferredWarehouseSearch.trim().toLowerCase();
    const actionableRequests = requests.filter((request) => ROOM_REQUEST_STATUSES.has(request.status));
    const requestById = new Map(actionableRequests.map((request) => [request.id, request]));
    const groupedByItem = new Map<
      string,
      {
        item: InventoryItem;
        supplier: ItemSupplierSummary;
        roomDemands: Map<string, IssuePlanningRoomDemand>;
      }
    >();

    requestItems.forEach((requestItem) => {
      if (!ACTIVE_REQUEST_ITEM_STATUSES.has(requestItem.status)) {
        return;
      }

      const request = requestById.get(requestItem.room_request_id);
      if (!request) {
        return;
      }

      if (selectedWarehouseRoomId !== 'all' && request.room_id !== selectedWarehouseRoomId) {
        return;
      }

      const outstandingQty = getRequestItemOutstandingQty(requestItem);
      if (outstandingQty <= 0) {
        return;
      }

      const item = itemById.get(requestItem.inventory_item_id);
      if (!item) {
        return;
      }

      const room = roomById.get(request.room_id);
      const supplier = primarySupplierByItemId.get(item.id) ?? {
        supplierId: null,
        supplierName: 'Supplier mapping needed',
        supplierEmail: null,
        reorderLevel: 0,
        minimumOrderQty: 0,
        leadTimeDays: null,
      };
      const itemGroup = groupedByItem.get(item.id) ?? {
        item,
        supplier,
        roomDemands: new Map<string, IssuePlanningRoomDemand>(),
      };
      const roomDemand = itemGroup.roomDemands.get(request.room_id) ?? {
        roomId: request.room_id,
        roomName: room?.name ?? 'Unknown room',
        requestedQty: 0,
        fulfilledQty: 0,
        outstandingQty: 0,
        recommendedIssueQty: 0,
        plannedIssueQty: 0,
        shortageQty: 0,
        requestCount: 0,
        requestItemIds: [],
        isPlanEdited: false,
      };

      roomDemand.requestedQty += requestItem.requested_qty;
      roomDemand.fulfilledQty += requestItem.fulfilled_qty;
      roomDemand.outstandingQty += outstandingQty;
      roomDemand.requestCount += 1;
      roomDemand.requestItemIds.push(requestItem.id);
      itemGroup.roomDemands.set(request.room_id, roomDemand);
      groupedByItem.set(item.id, itemGroup);
    });

    return [...groupedByItem.values()]
      .map(({ item, supplier, roomDemands }) => {
        const sortedRoomDemands = [...roomDemands.values()].sort((left, right) =>
          left.roomName.localeCompare(right.roomName, undefined, { sensitivity: 'base' })
        );
        const allocation = distributeStockFairly(sortedRoomDemands, item.warehouse_on_hand);
        const plannedRoomDemands = sortedRoomDemands.map((roomDemand) => {
          const recommendedIssueQty = allocation.get(roomDemand.roomId) ?? 0;
          const planKey = getIssuePlanKey(item.id, roomDemand.roomId, roomDemand.requestItemIds);
          const hasPlanOverride = Object.prototype.hasOwnProperty.call(issuePlanOverrides, planKey);
          const overrideQuantity = Number(issuePlanOverrides[planKey] ?? 0);
          const plannedIssueQty = hasPlanOverride
            ? Math.min(
                Math.max(Number.isFinite(overrideQuantity) ? Math.floor(overrideQuantity) : 0, 0),
                roomDemand.outstandingQty
              )
            : 0;

          return {
            ...roomDemand,
            recommendedIssueQty,
            plannedIssueQty,
            shortageQty: Math.max(roomDemand.outstandingQty - plannedIssueQty, 0),
            isPlanEdited: hasPlanOverride && plannedIssueQty !== recommendedIssueQty,
          };
        });
        const totalRequestedQty = plannedRoomDemands.reduce(
          (sum, roomDemand) => sum + roomDemand.requestedQty,
          0
        );
        const totalFulfilledQty = plannedRoomDemands.reduce(
          (sum, roomDemand) => sum + roomDemand.fulfilledQty,
          0
        );
        const totalOutstandingQty = plannedRoomDemands.reduce(
          (sum, roomDemand) => sum + roomDemand.outstandingQty,
          0
        );
        const plannedIssueQty = plannedRoomDemands.reduce(
          (sum, roomDemand) => sum + roomDemand.plannedIssueQty,
          0
        );
        const shortageQty = Math.max(totalOutstandingQty - plannedIssueQty, 0);
        const onOrderQty = purchaseOrderQtyByItemId.get(item.id) ?? 0;
        const projectedRemaining = item.warehouse_on_hand - plannedIssueQty;
        let status: IssuePlanningRow['status'] = 'ready_to_pick';
        let priorityScore = 3;

        if (plannedIssueQty > item.warehouse_on_hand) {
          status = 'over_planned';
          priorityScore = 0;
        } else if (shortageQty > 0 && item.warehouse_on_hand <= 0) {
          status = 'hard_shortage';
          priorityScore = 0;
        } else if (shortageQty > 0 && onOrderQty > 0) {
          status = 'on_order';
          priorityScore = 1;
        } else if (shortageQty > 0) {
          status = 'partial_plan';
          priorityScore = 1;
        } else if (supplier.reorderLevel > 0 && projectedRemaining <= supplier.reorderLevel) {
          status = 'monitoring';
          priorityScore = 2;
        }

        return {
          item,
          supplier,
          roomDemands: plannedRoomDemands,
          totalRequestedQty,
          totalFulfilledQty,
          totalOutstandingQty,
          plannedIssueQty,
          shortageQty,
          onOrderQty,
          projectedRemaining,
          reorderLevel: supplier.reorderLevel,
          status,
          priorityScore,
        } satisfies IssuePlanningRow;
      })
      .filter((row) => {
        if (!normalizedSearch) {
          return true;
        }

        return (
          row.item.name.toLowerCase().includes(normalizedSearch) ||
          (row.item.given_id ?? '').toLowerCase().includes(normalizedSearch) ||
          row.supplier.supplierName.toLowerCase().includes(normalizedSearch) ||
          row.roomDemands.some((roomDemand) =>
            roomDemand.roomName.toLowerCase().includes(normalizedSearch)
          )
        );
      })
      .sort((left, right) => {
        if (left.priorityScore !== right.priorityScore) {
          return left.priorityScore - right.priorityScore;
        }

        if (left.shortageQty !== right.shortageQty) {
          return right.shortageQty - left.shortageQty;
        }

        if (left.totalOutstandingQty !== right.totalOutstandingQty) {
          return right.totalOutstandingQty - left.totalOutstandingQty;
        }

        return left.item.name.localeCompare(right.item.name, undefined, {
          sensitivity: 'base',
        });
      });
  }, [
    deferredWarehouseSearch,
    issuePlanOverrides,
    itemById,
    primarySupplierByItemId,
    purchaseOrderQtyByItemId,
    requestItems,
    requests,
    roomById,
    selectedWarehouseRoomId,
  ]);

  // Planning table rows after the "Shortages only" toggle. Search + room
  // filtering already happen inside issuePlanningRows (warehouseSearch).
  const visiblePlanningRows = useMemo(() => {
    if (!planningShortagesOnly) {
      return issuePlanningRows;
    }

    return issuePlanningRows.filter((row) => row.shortageQty > 0);
  }, [issuePlanningRows, planningShortagesOnly]);

  const pickListLines = useMemo(() => {
    return issuePlanningRows
      .map((row) => ({
        item: row.item,
        supplier: row.supplier,
        totalPickQty: row.plannedIssueQty,
        roomDemands: row.roomDemands.filter((roomDemand) => roomDemand.plannedIssueQty > 0),
      }))
      .filter((line): line is PickListLine => line.totalPickQty > 0 && line.roomDemands.length > 0);
  }, [issuePlanningRows]);

  const warehouseRows = useMemo(() => {
    const actionableRequests = requests.filter((request) => ROOM_REQUEST_STATUSES.has(request.status));
    const requestById = new Map(actionableRequests.map((request) => [request.id, request]));

    const requestRows = requestItems
      .map((requestItem) => {
        const request = requestById.get(requestItem.room_request_id);
        if (!request) {
          return null;
        }

        if (selectedWarehouseRoomId !== 'all' && request.room_id !== selectedWarehouseRoomId) {
          return null;
        }

        const item = itemById.get(requestItem.inventory_item_id);
        if (!item) {
          return null;
        }

        const supplier = primarySupplierByItemId.get(item.id) ?? {
          supplierId: null,
          supplierName: 'Supplier mapping needed',
          supplierEmail: null,
          reorderLevel: 0,
          minimumOrderQty: 0,
          leadTimeDays: null,
        };
        const room = roomById.get(request.room_id) ?? null;
        const relatedRequestItems = requestItems.filter((candidate) => {
          if (candidate.inventory_item_id !== item.id) {
            return false;
          }

          const parentRequest = requestById.get(candidate.room_request_id);
          if (!parentRequest) {
            return false;
          }

          return selectedWarehouseRoomId === 'all' || parentRequest.room_id === selectedWarehouseRoomId;
        });
        const requestedTotalForItem = relatedRequestItems.reduce(
          (sum, candidate) => sum + candidate.requested_qty,
          0
        );
        const projectedRemaining = item.warehouse_on_hand - requestedTotalForItem;
        const shortageQty = Math.max(requestedTotalForItem - item.warehouse_on_hand, 0);

        let priorityScore = 2;
        if (shortageQty > 0) {
          priorityScore = 0;
        } else if (supplier.reorderLevel > 0 && projectedRemaining <= supplier.reorderLevel) {
          priorityScore = 1;
        }

        return {
          id: requestItem.id,
          rowType: 'request',
          item,
          requestedQty: requestItem.requested_qty,
          requestedTotalForItem,
          projectedRemaining,
          shortageQty,
          reorderLevel: supplier.reorderLevel,
          supplier,
          requestItemIds: [requestItem.id],
          request,
          requestItem,
          room,
          priorityScore,
        } satisfies WarehouseRow;
      })
      .filter((row): row is WarehouseRow => Boolean(row));

    const monitoringRows =
      selectedWarehouseRoomId === 'all'
        ? items
            .filter((item) => {
              const requestedTotalForItem = requestRows
                .filter((row) => row.item.id === item.id)
                .reduce((sum, row) => sum + row.requestedQty, 0);
              const supplier = primarySupplierByItemId.get(item.id);

              return (
                item.is_active &&
                requestedTotalForItem === 0 &&
                (supplier?.reorderLevel ?? 0) > 0 &&
                item.warehouse_on_hand <= (supplier?.reorderLevel ?? 0)
              );
            })
            .map((item) => {
              const supplier = primarySupplierByItemId.get(item.id) ?? {
                supplierId: null,
                supplierName: 'Supplier mapping needed',
                supplierEmail: null,
                reorderLevel: 0,
                minimumOrderQty: 0,
                leadTimeDays: null,
              };

              return {
                id: `monitor-${item.id}`,
                rowType: 'monitoring',
                item,
                requestedQty: 0,
                requestedTotalForItem: 0,
                projectedRemaining: item.warehouse_on_hand,
                shortageQty: 0,
                reorderLevel: supplier.reorderLevel,
                supplier,
                requestItemIds: [],
                request: null,
                requestItem: null,
                room: null,
                priorityScore: 1,
              } satisfies WarehouseRow;
            })
        : [];

    return [...requestRows, ...monitoringRows]
      .filter((row) => {
        const normalizedSearch = deferredWarehouseSearch.trim().toLowerCase();
        if (!normalizedSearch) {
          return true;
        }

        return (
          row.item.name.toLowerCase().includes(normalizedSearch) ||
          (row.item.given_id ?? '').toLowerCase().includes(normalizedSearch) ||
          row.supplier.supplierName.toLowerCase().includes(normalizedSearch) ||
          (row.room?.name ?? '').toLowerCase().includes(normalizedSearch)
        );
      })
      .sort((left, right) => {
        if (left.priorityScore !== right.priorityScore) {
          return left.priorityScore - right.priorityScore;
        }

        if (left.shortageQty !== right.shortageQty) {
          return right.shortageQty - left.shortageQty;
        }

        if (left.projectedRemaining !== right.projectedRemaining) {
          return left.projectedRemaining - right.projectedRemaining;
        }

        if (left.room?.name !== right.room?.name) {
          return (left.room?.name ?? '').localeCompare(right.room?.name ?? '');
        }

        return left.item.name.localeCompare(right.item.name);
      });
  }, [
    deferredWarehouseSearch,
    itemById,
    items,
    primarySupplierByItemId,
    requestItems,
    requests,
    roomById,
    selectedWarehouseRoomId,
  ]);

  const selectedWarehouseRows = useMemo(() => {
    return warehouseRows.filter((row) => selectedWarehouseItems[row.id]);
  }, [selectedWarehouseItems, warehouseRows]);

  const selectedWarehouseRequestForm = useMemo(() => {
    return (
      warehouseRequestForms.find(({ request }) => request.id === selectedWarehouseRequestId) ??
      warehouseRequestForms[0] ??
      null
    );
  }, [selectedWarehouseRequestId, warehouseRequestForms]);

  const purchaseOrderCards = useMemo(() => {
    return purchaseOrders.map((purchaseOrder) => {
      const supplier = supplierById.get(purchaseOrder.supplier_id) ?? null;
      const lines = purchaseOrderItems
        .filter((item) => item.purchase_order_id === purchaseOrder.id)
        .map((item) => ({
          ...item,
          // PO lines created via the direct-PO dialog may have no FK to a
          // catalog item (free-text entry) — fall back to null in that case.
          inventoryItem: item.inventory_item_id
            ? itemById.get(item.inventory_item_id) ?? null
            : null,
        }));

      return {
        purchaseOrder,
        supplier,
        lines,
      };
    });
  }, [itemById, purchaseOrderItems, purchaseOrders, supplierById]);

  const filteredCatalogItems = useMemo(() => {
    const normalizedSearch = deferredCatalogSearch.trim().toLowerCase();

    return items.filter((item) => {
      if (!normalizedSearch) {
        return true;
      }

      return (
        item.name.toLowerCase().includes(normalizedSearch) ||
        (item.given_id ?? '').toLowerCase().includes(normalizedSearch) ||
        (item.category ?? '').toLowerCase().includes(normalizedSearch) ||
        (primarySupplierByItemId.get(item.id)?.supplierName ?? '')
          .toLowerCase()
          .includes(normalizedSearch)
      );
    });
  }, [deferredCatalogSearch, items, primarySupplierByItemId]);

  const sortedCatalogItems = useMemo(() => {
    const nextItems = [...filteredCatalogItems];

    if (catalogSortKey === 'default') {
      return nextItems.sort((left, right) =>
        left.name.localeCompare(right.name, undefined, { sensitivity: 'base' })
      );
    }

    return nextItems.sort((left, right) => {
      const leftValue =
        catalogSortKey === 'onHand'
          ? left.warehouse_on_hand
          : primarySupplierByItemId.get(left.id)?.reorderLevel ?? 0;
      const rightValue =
        catalogSortKey === 'onHand'
          ? right.warehouse_on_hand
          : primarySupplierByItemId.get(right.id)?.reorderLevel ?? 0;

      if (leftValue !== rightValue) {
        return catalogSortDirection === 'asc'
          ? leftValue - rightValue
          : rightValue - leftValue;
      }

      return left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });
    });
  }, [
    catalogSortDirection,
    catalogSortKey,
    filteredCatalogItems,
    primarySupplierByItemId,
  ]);

  const totalCatalogPages = Math.max(
    1,
    Math.ceil(sortedCatalogItems.length / CATALOG_ITEMS_PER_PAGE)
  );
  const catalogPageStartIndex = (currentCatalogPage - 1) * CATALOG_ITEMS_PER_PAGE;
  const catalogPageEndIndex = catalogPageStartIndex + CATALOG_ITEMS_PER_PAGE;
  const paginatedCatalogItems = useMemo(() => {
    return sortedCatalogItems.slice(catalogPageStartIndex, catalogPageEndIndex);
  }, [catalogPageEndIndex, catalogPageStartIndex, sortedCatalogItems]);

  const filteredSuppliers = useMemo(() => {
    const normalizedSearch = deferredSupplierSearch.trim().toLowerCase();

    return supplierCatalogSummaries.filter(({ supplier, mappedItems }) => {
      if (!normalizedSearch) {
        return true;
      }

      return (
        supplier.name.toLowerCase().includes(normalizedSearch) ||
        (supplier.email ?? '').toLowerCase().includes(normalizedSearch) ||
        (supplier.contact_person ?? '').toLowerCase().includes(normalizedSearch) ||
        mappedItems.some((item) =>
          item.name.toLowerCase().includes(normalizedSearch)
        )
      );
    });
  }, [deferredSupplierSearch, supplierCatalogSummaries]);

  const catalogHealthSummary = useMemo(() => {
    const requestableItems = items.filter((item) => item.is_requestable);
    const trackedItems = items.filter((item) => item.track_stock);
    const itemsWithSupplier = items.filter(
      (item) => (itemSupplierLinksByItemId.get(item.id)?.length ?? 0) > 0
    );
    const itemsWithPlatoSupplierReference = items.filter((item) =>
      Boolean(String(item.raw_plato_payload?.supplier ?? '').trim())
    );
    const itemsWithPrimarySupplier = items.filter((item) =>
      itemSupplierLinksByItemId.get(item.id)?.some((link) => link.isPrimary)
    );
    const itemsWithPlatoReferenceMissingSupplierRecord = items.filter((item) => {
      const platoSupplierId = String(item.raw_plato_payload?.supplier ?? '').trim();

      return Boolean(platoSupplierId) && (itemSupplierLinksByItemId.get(item.id)?.length ?? 0) === 0;
    });
    const lowStockItems = items.filter((item) => {
      const primarySupplier = primarySupplierByItemId.get(item.id);

      return (
        (primarySupplier?.reorderLevel ?? 0) > 0 &&
        item.warehouse_on_hand <= (primarySupplier?.reorderLevel ?? 0)
      );
    });
    const zeroOnHandItems = items.filter((item) => item.warehouse_on_hand <= 0);
    const itemsMissingContactableSupplier = items.filter((item) =>
      (itemSupplierLinksByItemId.get(item.id) ?? []).every(
        (link) => !link.supplierEmail && !link.supplierPhone
      )
    );
    const suppliersWithEmail = suppliers.filter((supplier) => supplier.email).length;
    const suppliersWithPhone = suppliers.filter(
      (supplier) => supplier.telephone || supplier.handphone
    ).length;
    const staleItems = items.filter((item) => {
      const stamp = item.last_plato_synced_at ?? item.last_plato_edited_at;
      if (!stamp) {
        return true;
      }

      const ageInDays =
        (Date.now() - parseISO(stamp).getTime()) / (1000 * 60 * 60 * 24);

      return ageInDays >= 14;
    });
    const categories = new Map<
      string,
      { total: number; lowStock: number; unmapped: number }
    >();

    items.forEach((item) => {
      const key = item.category ?? 'Uncategorized';
      const existing = categories.get(key) ?? { total: 0, lowStock: 0, unmapped: 0 };
      const primarySupplier = primarySupplierByItemId.get(item.id);
      const isLowStock =
        (primarySupplier?.reorderLevel ?? 0) > 0 &&
        item.warehouse_on_hand <= (primarySupplier?.reorderLevel ?? 0);
      const isUnmapped = (itemSupplierLinksByItemId.get(item.id)?.length ?? 0) === 0;

      categories.set(key, {
        total: existing.total + 1,
        lowStock: existing.lowStock + (isLowStock ? 1 : 0),
        unmapped: existing.unmapped + (isUnmapped ? 1 : 0),
      });
    });

    const categoryBreakdown = [...categories.entries()]
      .map(([category, summary]) => ({ category, ...summary }))
      .sort((left, right) => right.total - left.total)
      .slice(0, 6);

    return {
      requestableItems: requestableItems.length,
      trackedItems: trackedItems.length,
      itemsWithSupplier: itemsWithSupplier.length,
      itemsWithPlatoSupplierReference: itemsWithPlatoSupplierReference.length,
      itemsMissingPlatoSupplierReference: items.length - itemsWithPlatoSupplierReference.length,
      itemsWithPlatoReferenceMissingSupplierRecord:
        itemsWithPlatoReferenceMissingSupplierRecord.length,
      itemsWithPrimarySupplier: itemsWithPrimarySupplier.length,
      lowStockItems: lowStockItems.length,
      zeroOnHandItems: zeroOnHandItems.length,
      staleItems: staleItems.length,
      unmappedItems: items.length - itemsWithSupplier.length,
      itemsMissingContactableSupplier: itemsMissingContactableSupplier.length,
      suppliersWithEmail,
      suppliersWithPhone,
      coverageRate: items.length
        ? Math.round((itemsWithSupplier.length / items.length) * 100)
        : 0,
      primaryCoverageRate: items.length
        ? Math.round((itemsWithPrimarySupplier.length / items.length) * 100)
        : 0,
      supplierEmailRate: suppliers.length
        ? Math.round((suppliersWithEmail / suppliers.length) * 100)
        : 0,
      categoryBreakdown,
    };
  }, [
    itemSupplierLinksByItemId,
    items,
    primarySupplierByItemId,
    suppliers,
  ]);

  const requestSummary = useMemo(() => {
    const submittedCount = roomRequests.filter((request) => request.status !== 'cancelled').length;
    const requestedUnits = roomRequests.reduce((sum, request) => {
      const lines = requestItemsByRequestId.get(request.id) ?? [];
      return sum + lines.reduce((lineSum, line) => lineSum + line.requested_qty, 0);
    }, 0);

    return {
      submittedCount,
      requestedUnits,
      draftCount: draftLines.length,
      totalDraftQuantity: draftLines.reduce((sum, entry) => sum + entry.draft.quantity, 0),
      monthlyCount: monthlyRoomRequests.filter((request) => request.status !== 'cancelled').length,
      pendingOutstandingItems: roomPendingDemands.length,
      pendingOutstandingUnits: roomPendingDemands.reduce(
        (sum, demand) => sum + demand.outstandingQty,
        0
      ),
    };
  }, [draftLines, monthlyRoomRequests, requestItemsByRequestId, roomPendingDemands, roomRequests]);

  const warehouseSummary = useMemo(() => {
    const shortages = warehouseRows.filter((row) => row.shortageQty > 0);
    const belowThreshold = warehouseRows.filter(
      (row) => row.item.is_active && row.reorderLevel > 0 && row.projectedRemaining <= row.reorderLevel
    );

    return {
      shortages: shortages.length,
      belowThreshold: belowThreshold.length,
      withoutSupplier: warehouseRows.filter((row) => !row.supplier.supplierId).length,
      outstandingDemand: warehouseRows.reduce((sum, row) => sum + row.requestedQty, 0),
      planningItems: issuePlanningRows.length,
      plannedPickUnits: pickListLines.reduce((sum, line) => sum + line.totalPickQty, 0),
    };
  }, [issuePlanningRows, pickListLines, warehouseRows]);

  useEffect(() => {
    if (currentCatalogPage > totalCatalogPages) {
      setCurrentCatalogPage(totalCatalogPages);
    }
  }, [currentCatalogPage, totalCatalogPages]);

  useEffect(() => {
    setCurrentCatalogPage(1);
  }, [deferredCatalogSearch]);

  useEffect(() => {
    setOrderingPage(1);
  }, [warehouseSearch, selectedWarehouseRoomId]);

  useEffect(() => {
    if (!paginatedCatalogItems.length) {
      setSelectedCatalogItemId('');
      return;
    }

    if (
      !selectedCatalogItemId ||
      !paginatedCatalogItems.some((item) => item.id === selectedCatalogItemId)
    ) {
      setSelectedCatalogItemId(paginatedCatalogItems[0].id);
    }
  }, [paginatedCatalogItems, selectedCatalogItemId]);

  useEffect(() => {
    if (!filteredSuppliers.length) {
      setSelectedSupplierId('');
      return;
    }

    if (
      !selectedSupplierId ||
      !filteredSuppliers.some(({ supplier }) => supplier.id === selectedSupplierId)
    ) {
      setSelectedSupplierId(filteredSuppliers[0].supplier.id);
    }
  }, [filteredSuppliers, selectedSupplierId]);

  useEffect(() => {
    if (!catalogMetricModal) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setCatalogMetricModal(null);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [catalogMetricModal]);

  useEffect(() => {
    if (!warehouseRequestForms.length) {
      setSelectedWarehouseRequestId('');
      return;
    }

    if (
      !selectedWarehouseRequestId ||
      !warehouseRequestForms.some(({ request }) => request.id === selectedWarehouseRequestId)
    ) {
      setSelectedWarehouseRequestId(warehouseRequestForms[0].request.id);
    }
  }, [selectedWarehouseRequestId, warehouseRequestForms]);

  const selectedCatalogItem = useMemo(() => {
    return (
      paginatedCatalogItems.find((item) => item.id === selectedCatalogItemId) ??
      paginatedCatalogItems[0] ??
      null
    );
  }, [paginatedCatalogItems, selectedCatalogItemId]);

  const selectedCatalogItemSuppliers = useMemo(() => {
    if (!selectedCatalogItem) {
      return [];
    }

    return itemSupplierLinksByItemId.get(selectedCatalogItem.id) ?? [];
  }, [itemSupplierLinksByItemId, selectedCatalogItem]);

  /** Last N PO unit costs for the selected catalog item (backlog item #8). */
  const selectedCatalogItemPriceHistory = useMemo(() => {
    if (!selectedCatalogItem) {
      return [];
    }

    return (priceHistoryByItemId.get(selectedCatalogItem.id) ?? []).slice(0, 8);
  }, [priceHistoryByItemId, selectedCatalogItem]);

  // Seed the order-unit / pack-size edit drafts whenever the selected catalog
  // item changes (including after a snapshot refresh that returns new values).
  useEffect(() => {
    setOrderUnitDraft(selectedCatalogItem?.order_unit ?? '');
    setPackSizeDraft(
      selectedCatalogItem?.pack_size != null ? String(selectedCatalogItem.pack_size) : ''
    );
  }, [selectedCatalogItem?.id, selectedCatalogItem?.order_unit, selectedCatalogItem?.pack_size]);

  const selectedSupplierSummary = useMemo(() => {
    return (
      filteredSuppliers.find(({ supplier }) => supplier.id === selectedSupplierId) ??
      filteredSuppliers[0] ??
      null
    );
  }, [filteredSuppliers, selectedSupplierId]);

  const sortedCatalogItemsByName = useMemo(() => [...items].sort(byName), [items]);
  const sortedSuppliersByName = useMemo(() => [...supplierCatalogSummaries].sort((left, right) =>
    left.supplier.name.localeCompare(right.supplier.name, undefined, { sensitivity: 'base' })
  ), [supplierCatalogSummaries]);

  const requestableCatalogItems = useMemo(
    () => sortedCatalogItemsByName.filter((item) => item.is_requestable),
    [sortedCatalogItemsByName]
  );
  const trackedCatalogItems = useMemo(
    () => sortedCatalogItemsByName.filter((item) => item.track_stock),
    [sortedCatalogItemsByName]
  );
  const itemsWithSupplierLinks = useMemo(
    () =>
      sortedCatalogItemsByName.filter(
        (item) => (itemSupplierLinksByItemId.get(item.id)?.length ?? 0) > 0
      ),
    [itemSupplierLinksByItemId, sortedCatalogItemsByName]
  );
  const itemsWithPrimarySupplierLinks = useMemo(
    () =>
      sortedCatalogItemsByName.filter((item) =>
        itemSupplierLinksByItemId.get(item.id)?.some((link) => link.isPrimary)
      ),
    [itemSupplierLinksByItemId, sortedCatalogItemsByName]
  );
  const itemsMissingPlatoSupplierReferenceDetails = useMemo(
    () => sortedCatalogItemsByName.filter((item) => !getPlatoSupplierReference(item)),
    [sortedCatalogItemsByName]
  );
  const itemsWithPlatoReferenceMissingSupplierRecordDetails = useMemo(
    () =>
      sortedCatalogItemsByName.filter((item) => {
        const platoSupplierId = getPlatoSupplierReference(item);
        return Boolean(platoSupplierId) && (itemSupplierLinksByItemId.get(item.id)?.length ?? 0) === 0;
      }),
    [itemSupplierLinksByItemId, sortedCatalogItemsByName]
  );
  const lowStockCatalogItems = useMemo(
    () =>
      sortedCatalogItemsByName.filter((item) => {
        const primarySupplier = primarySupplierByItemId.get(item.id);
        return (
          (primarySupplier?.reorderLevel ?? 0) > 0 &&
          item.warehouse_on_hand <= (primarySupplier?.reorderLevel ?? 0)
        );
      }),
    [primarySupplierByItemId, sortedCatalogItemsByName]
  );
  const zeroOnHandCatalogItems = useMemo(
    () => sortedCatalogItemsByName.filter((item) => item.warehouse_on_hand <= 0),
    [sortedCatalogItemsByName]
  );
  const staleCatalogItems = useMemo(
    () =>
      sortedCatalogItemsByName.filter((item) => {
        const stamp = item.last_plato_synced_at ?? item.last_plato_edited_at;
        if (!stamp) {
          return true;
        }

        const ageInDays = (Date.now() - parseISO(stamp).getTime()) / (1000 * 60 * 60 * 24);
        return ageInDays >= 14;
      }),
    [sortedCatalogItemsByName]
  );
  const itemsMissingContactableSupplierDetails = useMemo(
    () =>
      sortedCatalogItemsByName.filter((item) =>
        (itemSupplierLinksByItemId.get(item.id) ?? []).every(
          (link) => !link.supplierEmail && !link.supplierPhone
        )
      ),
    [itemSupplierLinksByItemId, sortedCatalogItemsByName]
  );
  const suppliersWithEmailDetails = useMemo(
    () => sortedSuppliersByName.filter(({ supplier }) => Boolean(supplier.email)),
    [sortedSuppliersByName]
  );
  const suppliersWithPhoneDetails = useMemo(
    () =>
      sortedSuppliersByName.filter(
        ({ supplier }) => Boolean(supplier.telephone || supplier.handphone)
      ),
    [sortedSuppliersByName]
  );
  const suppliersMissingContactChannels = useMemo(
    () =>
      sortedSuppliersByName.filter(
        ({ supplier }) =>
          !supplier.email &&
          !supplier.telephone &&
          !supplier.handphone &&
          !supplier.contact_person
      ),
    [sortedSuppliersByName]
  );
  const suppliersWithMappings = useMemo(
    () => sortedSuppliersByName.filter((summary) => summary.totalMappings > 0),
    [sortedSuppliersByName]
  );

  const openCatalogMetricModal = useCallback(
    (id: CatalogMetricModalId, supplierId?: string) => {
      setCatalogMetricModal({ id, supplierId });
    },
    []
  );

  const activeMetricSupplierSummary = useMemo(() => {
    if (catalogMetricModal?.supplierId) {
      return (
        supplierCatalogSummaries.find(
          ({ supplier }) => supplier.id === catalogMetricModal.supplierId
        ) ?? null
      );
    }

    return selectedSupplierSummary;
  }, [catalogMetricModal?.supplierId, selectedSupplierSummary, supplierCatalogSummaries]);

  const activeSupplierLinkRows = useMemo(() => {
    if (!activeMetricSupplierSummary) {
      return [];
    }

    return itemSuppliers
      .filter((link) => link.inventory_supplier_id === activeMetricSupplierSummary.supplier.id)
      .map((link) => ({
        link,
        item: itemById.get(link.inventory_item_id) ?? null,
      }))
      .filter(
        (
          row
        ): row is {
          link: InventoryItemSupplier;
          item: InventoryItem;
        } => Boolean(row.item)
      )
      .sort((left, right) =>
        left.item.name.localeCompare(right.item.name, undefined, { sensitivity: 'base' })
      );
  }, [activeMetricSupplierSummary, itemById, itemSuppliers]);

  const renderMetricSummaries = useCallback(
    (cards: Array<{ label: string; value: React.ReactNode; detail: React.ReactNode }>) => (
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {cards.map((card) => (
          <CatalogMetricCard
            key={card.label}
            title={card.label}
            value={card.value}
            detail={card.detail}
          />
        ))}
      </div>
    ),
    []
  );

  const renderInventoryItemTable = useCallback((
    rows: InventoryItem[],
    options?: {
      emptyMessage?: string;
      includeCoverage?: boolean;
      includeStock?: boolean;
      includePlatoReference?: boolean;
      includeFreshness?: boolean;
    }
  ) => {
    const {
      emptyMessage = 'No inventory items match this metric right now.',
      includeCoverage = true,
      includeStock = true,
      includePlatoReference = false,
      includeFreshness = false,
    } = options ?? {};

    if (rows.length === 0) {
      return (
        <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-10 text-sm text-slate-500">
          {emptyMessage}
        </div>
      );
    }

    return (
      <div className="overflow-hidden rounded-3xl border border-slate-200">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead>Item</TableHead>
              {includeCoverage ? <TableHead>Coverage</TableHead> : null}
              {includeStock ? <TableHead className="text-right">On hand</TableHead> : null}
              {includeStock ? <TableHead className="text-right">Reorder</TableHead> : null}
              {includePlatoReference ? <TableHead>Plato supplier ref</TableHead> : null}
              {includeFreshness ? <TableHead>Sync freshness</TableHead> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((item) => {
              const supplierLinks = itemSupplierLinksByItemId.get(item.id) ?? [];
              const primarySupplier = primarySupplierByItemId.get(item.id);
              const platoSupplierReference = getPlatoSupplierReference(item);
              const syncStamp = item.last_plato_synced_at ?? item.last_plato_edited_at;

              return (
                <TableRow key={item.id}>
                  <TableCell>
                    <div className="space-y-1">
                      <p className="font-medium text-slate-950">{item.name}</p>
                      <p className="text-xs text-slate-500">
                        {item.given_id ?? 'No code'} · {item.category ?? 'Uncategorized'}
                      </p>
                    </div>
                  </TableCell>
                  {includeCoverage ? (
                    <TableCell>
                      <div className="space-y-1 text-sm text-slate-600">
                        <p>{supplierLinks.length > 0 ? `${supplierLinks.length} supplier link${supplierLinks.length === 1 ? '' : 's'}` : 'Unmapped'}</p>
                        <p className="text-xs text-slate-500">
                          {primarySupplier?.supplierName ?? 'No primary supplier'}
                        </p>
                      </div>
                    </TableCell>
                  ) : null}
                  {includeStock ? (
                    <TableCell className="text-right font-medium">{item.warehouse_on_hand}</TableCell>
                  ) : null}
                  {includeStock ? (
                    <TableCell className="text-right">
                      {primarySupplier?.reorderLevel ?? 0}
                    </TableCell>
                  ) : null}
                  {includePlatoReference ? (
                    <TableCell>
                      {platoSupplierReference ? (
                        <Badge variant="outline" className="font-mono text-[11px]">
                          {platoSupplierReference}
                        </Badge>
                      ) : (
                        <span className="text-sm text-slate-500">Missing in Plato</span>
                      )}
                    </TableCell>
                  ) : null}
                  {includeFreshness ? (
                    <TableCell>
                      <div className="space-y-1 text-sm text-slate-600">
                        <p>{formatAgo(syncStamp)}</p>
                        <p className="text-xs text-slate-500">{formatStamp(syncStamp)}</p>
                      </div>
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  }, [itemSupplierLinksByItemId, primarySupplierByItemId]);

  const renderSupplierTable = useCallback((
    rows: SupplierCatalogSummary[],
    options?: {
      emptyMessage?: string;
      includeAverages?: boolean;
    }
  ) => {
    const {
      emptyMessage = 'No supplier records match this metric right now.',
      includeAverages = true,
    } = options ?? {};

    if (rows.length === 0) {
      return (
        <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-10 text-sm text-slate-500">
          {emptyMessage}
        </div>
      );
    }

    return (
      <div className="overflow-hidden rounded-3xl border border-slate-200">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead>Supplier</TableHead>
              <TableHead>Contact readiness</TableHead>
              <TableHead className="text-right">Mappings</TableHead>
              <TableHead className="text-right">Primary</TableHead>
              <TableHead className="text-right">Low stock</TableHead>
              {includeAverages ? <TableHead className="text-right">Avg lead / reorder</TableHead> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.supplier.id}>
                <TableCell>
                  <div className="space-y-1">
                    <p className="font-medium text-slate-950">{row.supplier.name}</p>
                    <p className="text-xs text-slate-500">
                      {row.supplier.plato_supplier_id ?? 'No Plato supplier id'}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1 text-sm text-slate-600">
                    <p>{row.supplier.email || row.supplier.contact_person || 'No email or contact person'}</p>
                    <p className="text-xs text-slate-500">
                      {row.supplier.telephone || row.supplier.handphone || 'Phone missing'}
                    </p>
                  </div>
                </TableCell>
                <TableCell className="text-right">{row.totalMappings}</TableCell>
                <TableCell className="text-right">{row.primaryMappings}</TableCell>
                <TableCell className="text-right">{row.lowStockItems.length}</TableCell>
                {includeAverages ? (
                  <TableCell className="text-right text-sm text-slate-600">
                    {row.averageLeadTimeDays != null ? `${row.averageLeadTimeDays}d` : 'N/A'} /{' '}
                    {row.averageReorderLevel ?? 'N/A'}
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }, []);

  const renderSupplierLinkTable = useCallback((
    rows: CatalogSupplierLinkDetail[],
    emptyMessage = 'No supplier links are available for this item yet.'
  ) => {
    if (rows.length === 0) {
      return (
        <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-10 text-sm text-slate-500">
          {emptyMessage}
        </div>
      );
    }

    return (
      <div className="overflow-hidden rounded-3xl border border-slate-200">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead>Supplier</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead className="text-right">MOQ</TableHead>
              <TableHead className="text-right">Reorder</TableHead>
              <TableHead className="text-right">Lead time</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.linkId}>
                <TableCell>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-950">{row.supplierName}</p>
                      {row.isPrimary ? <Badge variant="secondary">Primary</Badge> : null}
                    </div>
                    <p className="text-xs text-slate-500">{row.supplierSku || 'Supplier SKU pending'}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1 text-sm text-slate-600">
                    <p>{row.contactPerson || row.supplierEmail || 'Contact details incomplete'}</p>
                    <p className="text-xs text-slate-500">{row.supplierPhone || 'Phone missing'}</p>
                  </div>
                </TableCell>
                <TableCell className="text-right">{row.minimumOrderQty || 0}</TableCell>
                <TableCell className="text-right">{row.reorderLevel}</TableCell>
                <TableCell className="text-right">
                  {row.leadTimeDays != null ? `${row.leadTimeDays}d` : 'Pending'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }, []);

  const metricDialogConfig = useMemo(() => {
    if (!catalogMetricModal) {
      return null;
    }

    switch (catalogMetricModal.id) {
      case 'catalog-items':
        return {
          title: 'Catalog items',
          description:
            'Full imported item roster with requestability, stock-tracking coverage, and current supplier readiness.',
          content: (
            <div className="space-y-4">
              {renderMetricSummaries([
                {
                  label: 'Total items',
                  value: items.length,
                  detail: `${requestableCatalogItems.length} requestable and ${trackedCatalogItems.length} stock-tracked.`,
                },
                {
                  label: 'Supplier-linked',
                  value: itemsWithSupplierLinks.length,
                  detail: `${items.length - itemsWithSupplierLinks.length} still need local supplier mapping.`,
                },
                {
                  label: 'Primary assigned',
                  value: itemsWithPrimarySupplierLinks.length,
                  detail: `${items.length - itemsWithPrimarySupplierLinks.length} still have no primary supplier.`,
                },
              ])}
              {renderInventoryItemTable(sortedCatalogItemsByName, {
                includeCoverage: true,
                includeStock: true,
              })}
            </div>
          ),
        };
      case 'supplier-coverage':
        return {
          title: 'Supplier coverage',
          description:
            'Coverage combines local item-to-supplier mappings with the upstream supplier reference carried by each Plato inventory item.',
          content: (
            <div className="space-y-4">
              {renderMetricSummaries([
                {
                  label: 'Mapped locally',
                  value: itemsWithSupplierLinks.length,
                  detail: `${catalogHealthSummary.coverageRate}% of the current catalog has at least one supplier link.`,
                },
                {
                  label: 'Missing in Plato',
                  value: itemsMissingPlatoSupplierReferenceDetails.length,
                  detail: 'These items arrived without a supplier reference in the Plato inventory payload.',
                },
                {
                  label: 'Plato ref missing master',
                  value: itemsWithPlatoReferenceMissingSupplierRecordDetails.length,
                  detail: 'Plato inventory referenced a supplier id, but the supplier feed did not return that record.',
                },
              ])}
              {renderInventoryItemTable(sortedCatalogItemsByName, {
                includeCoverage: true,
                includeStock: true,
                includePlatoReference: true,
              })}
            </div>
          ),
        };
      case 'primary-coverage':
        return {
          title: 'Primary supplier coverage',
          description:
            'Primary assignments determine which supplier is used by default for reorder thresholds and purchase-order grouping.',
          content: (
            <div className="space-y-4">
              {renderMetricSummaries([
                {
                  label: 'Primary assigned',
                  value: itemsWithPrimarySupplierLinks.length,
                  detail: `${catalogHealthSummary.primaryCoverageRate}% of the catalog has a default supplier.`,
                },
                {
                  label: 'Has links, no primary',
                  value: itemsWithSupplierLinks.length - itemsWithPrimarySupplierLinks.length,
                  detail: 'These items have supplier mappings, but no default supplier has been chosen.',
                },
                {
                  label: 'Fully unmapped',
                  value: catalogHealthSummary.unmappedItems,
                  detail: 'These items are still missing all supplier mappings.',
                },
              ])}
              {renderInventoryItemTable(sortedCatalogItemsByName, {
                includeCoverage: true,
                includeStock: true,
              })}
            </div>
          ),
        };
      case 'low-stock-watch':
        return {
          title: 'Low stock watch',
          description:
            'Items currently at or below reorder level, including those already at zero on-hand.',
          content: (
            <div className="space-y-4">
              {renderMetricSummaries([
                {
                  label: 'Low stock',
                  value: lowStockCatalogItems.length,
                  detail: 'At or below the configured reorder level for the primary supplier.',
                },
                {
                  label: 'Zero on hand',
                  value: zeroOnHandCatalogItems.length,
                  detail: 'Items already depleted in warehouse stock.',
                },
                {
                  label: 'Healthy stock',
                  value: items.length - lowStockCatalogItems.length,
                  detail: 'Items currently above reorder threshold or without a configured threshold.',
                },
              ])}
              {renderInventoryItemTable(lowStockCatalogItems, {
                includeCoverage: true,
                includeStock: true,
                emptyMessage: 'No items are currently below reorder level.',
              })}
            </div>
          ),
        };
      case 'supplier-contacts':
        return {
          title: 'Supplier contacts',
          description:
            'Contact readiness across the supplier directory, including which vendors can be emailed immediately and which still need manual cleanup.',
          content: (
            <div className="space-y-4">
              {renderMetricSummaries([
                {
                  label: 'Email ready',
                  value: suppliersWithEmailDetails.length,
                  detail: 'Suppliers with an email address ready for PO dispatch.',
                },
                {
                  label: 'Phone available',
                  value: suppliersWithPhoneDetails.length,
                  detail: 'Suppliers with a documented telephone or handphone fallback channel.',
                },
                {
                  label: 'No contact channels',
                  value: suppliersMissingContactChannels.length,
                  detail: 'Suppliers with no email, phone, or named contact person stored locally.',
                },
              ])}
              {renderSupplierTable(sortedSuppliersByName, { includeAverages: false })}
            </div>
          ),
        };
      case 'suppliers-total':
        return {
          title: 'Supplier directory',
          description:
            'Complete supplier roster imported from Plato, with contact readiness and current mapping load for each supplier.',
          content: (
            <div className="space-y-4">
              {renderMetricSummaries([
                {
                  label: 'Suppliers',
                  value: suppliers.length,
                  detail: 'Current supplier records in the local catalog directory.',
                },
                {
                  label: 'With mappings',
                  value: suppliersWithMappings.length,
                  detail: `${suppliers.length - suppliersWithMappings.length} suppliers are not linked to any item yet.`,
                },
                {
                  label: 'Email ready',
                  value: suppliersWithEmailDetails.length,
                  detail: 'Suppliers that can receive purchase orders without manual email lookup.',
                },
              ])}
              {renderSupplierTable(sortedSuppliersByName)}
            </div>
          ),
        };
      case 'item-mappings':
        return {
          title: 'Item mappings',
          description:
            'Supplier-to-item mapping depth, including primary assignments and low-stock exposure by supplier.',
          content: (
            <div className="space-y-4">
              {renderMetricSummaries([
                {
                  label: 'All mappings',
                  value: itemSuppliers.length,
                  detail: `${supplierCatalogSummaries.reduce((sum, row) => sum + row.primaryMappings, 0)} are marked as primary relationships.`,
                },
                {
                  label: 'Mapped suppliers',
                  value: suppliersWithMappings.length,
                  detail: 'Suppliers currently carrying at least one catalog item.',
                },
                {
                  label: 'Unmapped suppliers',
                  value: suppliers.length - suppliersWithMappings.length,
                  detail: 'Supplier records that exist locally but are not attached to any catalog item yet.',
                },
              ])}
              {renderSupplierTable(suppliersWithMappings)}
            </div>
          ),
        };
      case 'contactable-suppliers':
        return {
          title: 'Contactable suppliers',
          description:
            'Suppliers ready for outreach, plus the records still missing enough contact data to support fast PO follow-up.',
          content: (
            <div className="space-y-4">
              {renderMetricSummaries([
                {
                  label: 'Email ready',
                  value: suppliersWithEmailDetails.length,
                  detail: 'Can receive purchase-order emails directly from the directory.',
                },
                {
                  label: 'Phone fallback',
                  value: suppliersWithPhoneDetails.length,
                  detail: 'Useful for manual follow-up when email is missing or bounces.',
                },
                {
                  label: 'Items at risk',
                  value: itemsMissingContactableSupplierDetails.length,
                  detail: 'Mapped items whose linked suppliers still have no email or phone channel.',
                },
              ])}
              {renderSupplierTable(suppliersWithEmailDetails, {
                includeAverages: false,
                emptyMessage: 'No suppliers currently have an email address on file.',
              })}
            </div>
          ),
        };
      case 'supplier-search-results':
        return {
          title: 'Supplier search results',
          description:
            deferredSupplierSearch.trim()
              ? `Current roster slice for “${deferredSupplierSearch.trim()}”.`
              : 'Current filtered supplier roster. Use search to narrow by supplier, contact, or mapped item.',
          content: (
            <div className="space-y-4">
              {renderMetricSummaries([
                {
                  label: 'Results',
                  value: filteredSuppliers.length,
                  detail: 'Suppliers matching the current roster search.',
                },
                {
                  label: 'With email',
                  value: filteredSuppliers.filter(({ supplier }) => Boolean(supplier.email)).length,
                  detail: 'Search matches that are ready for direct email contact.',
                },
                {
                  label: 'Mapped results',
                  value: filteredSuppliers.filter((row) => row.totalMappings > 0).length,
                  detail: 'Search matches that already carry mapped catalog items.',
                },
              ])}
              {renderSupplierTable(filteredSuppliers, {
                includeAverages: false,
                emptyMessage: 'No suppliers match the current search.',
              })}
            </div>
          ),
        };
      case 'selected-item-stock-position':
        return {
          title: selectedCatalogItem ? `${selectedCatalogItem.name} stock position` : 'Item stock position',
          description:
            'Warehouse quantity, reorder threshold, and supplier context for the currently selected catalog item.',
          content: selectedCatalogItem ? (
            <div className="space-y-4">
              {renderMetricSummaries([
                {
                  label: 'On hand',
                  value: selectedCatalogItem.warehouse_on_hand,
                  detail: 'Current warehouse quantity imported from Plato.',
                },
                {
                  label: 'Reorder level',
                  value: primarySupplierByItemId.get(selectedCatalogItem.id)?.reorderLevel ?? 0,
                  detail: 'Threshold currently driving low-stock alerts for the primary supplier.',
                },
                {
                  label: 'Primary supplier',
                  value: primarySupplierByItemId.get(selectedCatalogItem.id)?.supplierName ?? 'Missing',
                  detail: 'Default supplier used for reorder grouping and PO workflows.',
                },
              ])}
              {renderInventoryItemTable([selectedCatalogItem], {
                includeCoverage: true,
                includeStock: true,
                includePlatoReference: true,
                includeFreshness: true,
              })}
            </div>
          ) : null,
        };
      case 'selected-item-supplier-coverage':
        return {
          title: selectedCatalogItem ? `${selectedCatalogItem.name} supplier coverage` : 'Item supplier coverage',
          description:
            'All supplier links for the selected item, including contactability, MOQ, reorder, and lead time details.',
          content: selectedCatalogItem ? (
            <div className="space-y-4">
              {renderMetricSummaries([
                {
                  label: 'Supplier links',
                  value: selectedCatalogItemSuppliers.length,
                  detail: `${selectedCatalogItemSuppliers.filter((link) => link.isPrimary).length} marked as primary.`,
                },
                {
                  label: 'Contactable links',
                  value: selectedCatalogItemSuppliers.filter((link) => link.supplierEmail || link.supplierPhone).length,
                  detail: 'Supplier links with at least one usable outreach channel.',
                },
                {
                  label: 'Missing contact',
                  value: selectedCatalogItemSuppliers.filter((link) => !link.supplierEmail && !link.supplierPhone).length,
                  detail: 'Linked suppliers still missing both email and phone details.',
                },
              ])}
              {renderSupplierLinkTable(selectedCatalogItemSuppliers)}
            </div>
          ) : null,
        };
      case 'selected-supplier-mapped-items':
        return {
          title: activeMetricSupplierSummary
            ? `${activeMetricSupplierSummary.supplier.name} mapped items`
            : 'Supplier mapped items',
          description:
            'All catalog items depending on the selected supplier, including current stock position and low-stock exposure.',
          content: activeMetricSupplierSummary ? (
            <div className="space-y-4">
              {renderMetricSummaries([
                {
                  label: 'Mapped items',
                  value: activeMetricSupplierSummary.mappedItems.length,
                  detail: `${activeMetricSupplierSummary.primaryMappings} currently use this supplier as the default.`,
                },
                {
                  label: 'Low stock',
                  value: activeMetricSupplierSummary.lowStockItems.length,
                  detail: 'Mapped items already at or below reorder level.',
                },
                {
                  label: 'Email readiness',
                  value: activeMetricSupplierSummary.supplier.email ? 'Ready' : 'Missing',
                  detail: activeMetricSupplierSummary.supplier.email
                    ? 'This supplier can be emailed directly for purchase orders.'
                    : 'Email is still missing, so PO follow-up stays manual.',
                },
              ])}
              {renderInventoryItemTable(activeMetricSupplierSummary.mappedItems, {
                includeCoverage: false,
                includeStock: true,
                emptyMessage: 'This supplier has no mapped catalog items yet.',
              })}
            </div>
          ) : null,
        };
      case 'selected-supplier-operational-averages':
        return {
          title: activeMetricSupplierSummary
            ? `${activeMetricSupplierSummary.supplier.name} operational averages`
            : 'Supplier operational averages',
          description:
            'Underlying mapping rows that drive the selected supplier’s average lead time and reorder position.',
          content: activeMetricSupplierSummary ? (
            <div className="space-y-4">
              {renderMetricSummaries([
                {
                  label: 'Avg lead time',
                  value: activeMetricSupplierSummary.averageLeadTimeDays != null
                    ? `${activeMetricSupplierSummary.averageLeadTimeDays}d`
                    : 'N/A',
                  detail: 'Average across mapped rows with a configured lead time.',
                },
                {
                  label: 'Avg reorder',
                  value: activeMetricSupplierSummary.averageReorderLevel ?? 'N/A',
                  detail: 'Average reorder level across mapped rows with a configured threshold.',
                },
                {
                  label: 'Mappings sampled',
                  value: activeSupplierLinkRows.length,
                  detail: 'Rows currently contributing to this supplier’s operational footprint.',
                },
              ])}
              {activeSupplierLinkRows.length > 0 ? (
                <div className="overflow-hidden rounded-3xl border border-slate-200">
                  <Table>
                    <TableHeader className="bg-slate-50">
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">MOQ</TableHead>
                        <TableHead className="text-right">Reorder</TableHead>
                        <TableHead className="text-right">Lead time</TableHead>
                        <TableHead className="text-right">Primary</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activeSupplierLinkRows.map(({ item, link }) => (
                        <TableRow key={link.id}>
                          <TableCell>
                            <div className="space-y-1">
                              <p className="font-medium text-slate-950">{item.name}</p>
                              <p className="text-xs text-slate-500">
                                {item.given_id ?? 'No code'} · {item.category ?? 'Uncategorized'}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{link.minimum_order_qty ?? 0}</TableCell>
                          <TableCell className="text-right">{link.reorder_level ?? 0}</TableCell>
                          <TableCell className="text-right">
                            {link.lead_time_days != null ? `${link.lead_time_days}d` : 'Pending'}
                          </TableCell>
                          <TableCell className="text-right">
                            {link.is_primary ? <Badge variant="secondary">Primary</Badge> : <span className="text-slate-500">No</span>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-10 text-sm text-slate-500">
                  This supplier has no operational mapping rows yet.
                </div>
              )}
            </div>
          ) : null,
        };
      default:
        return null;
    }
  }, [
    activeMetricSupplierSummary,
    activeSupplierLinkRows,
    catalogHealthSummary.coverageRate,
    catalogHealthSummary.primaryCoverageRate,
    catalogHealthSummary.unmappedItems,
    deferredSupplierSearch,
    filteredSuppliers,
    itemSuppliers.length,
    items.length,
    itemsMissingContactableSupplierDetails.length,
    itemsMissingPlatoSupplierReferenceDetails.length,
    itemsWithPlatoReferenceMissingSupplierRecordDetails.length,
    itemsWithPrimarySupplierLinks.length,
    itemsWithSupplierLinks.length,
    lowStockCatalogItems,
    primarySupplierByItemId,
    renderInventoryItemTable,
    renderMetricSummaries,
    renderSupplierLinkTable,
    renderSupplierTable,
    requestableCatalogItems.length,
    selectedCatalogItem,
    selectedCatalogItemSuppliers,
    sortedCatalogItemsByName,
    sortedSuppliersByName,
    suppliers.length,
    suppliersMissingContactChannels.length,
    suppliersWithEmailDetails,
    suppliersWithMappings,
    suppliersWithPhoneDetails.length,
    supplierCatalogSummaries,
    trackedCatalogItems.length,
    zeroOnHandCatalogItems.length,
    catalogMetricModal,
  ]);

  const handleCatalogSort = (nextKey: CatalogSortKey) => {
    startTransition(() => {
      setCurrentCatalogPage(1);
      setCatalogSortKey((currentKey) => {
        if (currentKey === nextKey) {
          setCatalogSortDirection((currentDirection) =>
            currentDirection === 'asc' ? 'desc' : 'asc'
          );
          return currentKey;
        }

        setCatalogSortDirection('desc');
        return nextKey;
      });
    });
  };

  const renderCatalogSortIcon = (key: CatalogSortKey) => {
    if (catalogSortKey !== key) {
      return <ArrowUpDown className="h-3.5 w-3.5 text-slate-400" />;
    }

    return catalogSortDirection === 'asc' ? (
      <ArrowUp className="h-3.5 w-3.5 text-slate-700" />
    ) : (
      <ArrowDown className="h-3.5 w-3.5 text-slate-700" />
    );
  };

  const handleIssuePlanQuantityChange = useCallback(
    (itemId: string, roomDemand: IssuePlanningRoomDemand, quantity: number) => {
      const safeQuantity = Number.isFinite(quantity)
        ? Math.min(Math.max(Math.floor(quantity), 0), roomDemand.outstandingQty)
        : 0;

      startTransition(() => {
        setIssuePlanOverrides((current) => ({
          ...current,
          [getIssuePlanKey(itemId, roomDemand.roomId, roomDemand.requestItemIds)]: safeQuantity,
        }));
      });
    },
    []
  );

  const handleUseRecommendedIssuePlan = useCallback((row: IssuePlanningRow) => {
    const totalRecommendedIssueQty = row.roomDemands.reduce(
      (sum, roomDemand) => sum + roomDemand.recommendedIssueQty,
      0
    );

    startTransition(() => {
      setIssuePlanOverrides((current) => {
        const next = { ...current };

        row.roomDemands.forEach((roomDemand) => {
          next[getIssuePlanKey(row.item.id, roomDemand.roomId, roomDemand.requestItemIds)] =
            roomDemand.recommendedIssueQty;
        });

        return next;
      });
    });

    toast({
      title: totalRecommendedIssueQty > 0 ? 'Fair split applied' : 'No stock to split',
      description:
        totalRecommendedIssueQty > 0
          ? `${totalRecommendedIssueQty} ${row.item.unit || row.item.order_unit || 'units'} planned across ${
              row.roomDemands.length
            } room${row.roomDemands.length === 1 ? '' : 's'}.`
          : `${row.item.name} has no warehouse stock available for this issue plan.`,
    });
  }, [toast]);

  const handleClearIssuePlan = useCallback((row: IssuePlanningRow) => {
    startTransition(() => {
      setIssuePlanOverrides((current) => {
        const next = { ...current };

        row.roomDemands.forEach((roomDemand) => {
          next[getIssuePlanKey(row.item.id, roomDemand.roomId, roomDemand.requestItemIds)] = 0;
        });

        return next;
      });
    });
  }, []);

  const handleDraftQuantityChange = (itemId: string, quantity: number) => {
    startTransition(() => {
      setRequestDraft((current) => {
        const next = { ...current };
        const existing = next[itemId] ?? { quantity: 0, note: '' };

        if (quantity <= 0) {
          delete next[itemId];
          return next;
        }

        next[itemId] = { ...existing, quantity };
        return next;
      });
    });
  };

  const handleDraftNoteChange = (itemId: string, note: string) => {
    startTransition(() => {
      setRequestDraft((current) => ({
        ...current,
        [itemId]: {
          quantity: current[itemId]?.quantity ?? 1,
          note,
        },
      }));
    });
  };

  const queueInactiveRequestConfirmation = useCallback((itemId: string, quantity: number) => {
    setPendingInactiveRequest({ itemId, quantity });
  }, []);

  const handleRequestQuantityIntent = useCallback(
    (item: InventoryItem, quantity: number) => {
      const existingQuantity = requestDraft[item.id]?.quantity ?? 0;
      const needsConfirmation = !item.is_active && existingQuantity === 0 && quantity > 0;

      if (needsConfirmation) {
        queueInactiveRequestConfirmation(item.id, quantity);
        return;
      }

      handleDraftQuantityChange(item.id, quantity);
    },
    [queueInactiveRequestConfirmation, requestDraft]
  );

  const confirmInactiveRequest = useCallback(() => {
    if (!pendingInactiveRequest) {
      return;
    }

    handleDraftQuantityChange(
      pendingInactiveRequest.itemId,
      pendingInactiveRequest.quantity
    );
    setPendingInactiveRequest(null);
  }, [pendingInactiveRequest]);

  const handleInventoryItemActiveToggle = useCallback(
    async (item: InventoryItem, nextActive: boolean) => {
      try {
        setUpdatingItemIds((current) => ({ ...current, [item.id]: true }));
        await inventoryService.updateInventoryItemActiveState(item.id, nextActive);
        await loadSnapshot();
        toast({
          title: `Item marked ${inventoryActivationLabel(nextActive).toLowerCase()}`,
          description: `${item.name} is now ${inventoryActivationLabel(nextActive).toLowerCase()} for warehouse stock monitoring.`,
        });
      } catch (error) {
        console.error('Failed to update inventory item active state:', error);
        toast({
          title: 'Item status update failed',
          description: `The item could not be marked active or inactive. ${
            error instanceof Error ? error.message : String(error)
          }`,
          variant: 'destructive',
        });
      } finally {
        setUpdatingItemIds((current) => {
          const next = { ...current };
          delete next[item.id];
          return next;
        });
      }
    },
    [loadSnapshot, toast]
  );

  const handleSubmitRoomRequest = async () => {
    if (submittingRequestRef.current) {
      return;
    }

    if (!selectedRoom || !currentUser) {
      toast({
        title: 'Room request blocked',
        description: 'A room and signed-in user are required before submitting.',
        variant: 'destructive',
      });
      return;
    }

    if (draftLines.length === 0) {
      toast({
        title: 'No items selected',
        description: 'Add at least one item to the request cart first.',
        variant: 'destructive',
      });
      return;
    }

    try {
      submittingRequestRef.current = true;
      setSubmittingRequest(true);
      await inventoryService.submitRoomRequest({
        roomId: selectedRoom.id,
        requestedBy: currentUser.id,
        requestedByEmail: currentUser.email,
        requestNotes,
        items: draftLines.map((entry) => ({
          inventoryItemId: entry.item.id,
          requestedQty: entry.draft.quantity,
          itemNote: entry.draft.note,
        })),
      });

      setRequestDraft({});
      setRequestNotes('');
      toast({
        title: 'Room request submitted',
        description: `${draftLines.length} item lines were sent to warehouse review.`,
      });
      await loadSnapshot();
    } catch (error) {
      console.error('Failed to submit room request:', error);
      toast({
        title: 'Submit failed',
        description: `The room request could not be saved. ${
          error instanceof Error ? error.message : String(error)
        }`,
        variant: 'destructive',
      });
    } finally {
      submittingRequestRef.current = false;
      setSubmittingRequest(false);
    }
  };

  const handleCreatePurchaseOrders = async () => {
    if (!currentUser) {
      toast({
        title: 'No user session',
        description: 'Warehouse actions require a signed-in user.',
        variant: 'destructive',
      });
      return;
    }

    const orderableRows = selectedWarehouseRows.filter(
      (row) => row.shortageQty > 0 && row.supplier.supplierId
    );

    if (orderableRows.length === 0) {
      toast({
        title: 'Nothing to draft',
        description: 'Select shortage rows with supplier mappings first.',
        variant: 'destructive',
      });
      return;
    }

    const groupedBySupplier = new Map<string, WarehouseRow[]>();
    orderableRows.forEach((row) => {
      const supplierId = row.supplier.supplierId;
      if (!supplierId) {
        return;
      }

      const current = groupedBySupplier.get(supplierId) ?? [];
      current.push(row);
      groupedBySupplier.set(supplierId, current);
    });

    try {
      setCreatingOrders(true);

      for (const [supplierId, rows] of groupedBySupplier.entries()) {
        const consolidatedItems = new Map<
          string,
          {
            inventoryItemId: string;
            selectedRequestedQty: number;
            warehouseOnHand: number;
            minimumOrderQty: number;
            unitCostSnapshot: number | null;
            sourceRequestItemIds: string[];
          }
        >();

        rows.forEach((row) => {
          const current = consolidatedItems.get(row.item.id) ?? {
            inventoryItemId: row.item.id,
            selectedRequestedQty: 0,
            warehouseOnHand: row.item.warehouse_on_hand,
            minimumOrderQty: row.supplier.minimumOrderQty || 0,
            unitCostSnapshot: row.item.cost_price ?? null,
            sourceRequestItemIds: [],
          };

          current.selectedRequestedQty += row.requestedQty;
          current.minimumOrderQty = Math.max(current.minimumOrderQty, row.supplier.minimumOrderQty || 0);
          current.sourceRequestItemIds.push(...row.requestItemIds);
          consolidatedItems.set(row.item.id, current);
        });

        const draftItems = [...consolidatedItems.values()]
          .map((item) => {
            const shortageForSelection = Math.max(
              item.selectedRequestedQty - item.warehouseOnHand,
              0
            );

            return {
              inventoryItemId: item.inventoryItemId,
              qtyToOrder:
                shortageForSelection > 0
                  ? Math.max(shortageForSelection, item.minimumOrderQty, 1)
                  : 0,
              unitCostSnapshot: item.unitCostSnapshot,
              sourceRequestItemIds: item.sourceRequestItemIds,
            };
          })
          .filter((item) => item.qtyToOrder > 0);

        if (draftItems.length === 0) {
          continue;
        }

        await inventoryService.createPurchaseOrderDraft({
          supplierId,
          createdBy: currentUser.id,
          createdByEmail: currentUser.email,
          items: draftItems,
        });
      }

      setSelectedWarehouseItems({});
      setActiveTab('purchase-orders');
      toast({
        title: 'PO drafts created',
        description: `${groupedBySupplier.size} supplier draft${groupedBySupplier.size === 1 ? '' : 's'} created for review.`,
      });
      await loadSnapshot();
    } catch (error) {
      console.error('Failed to create purchase orders:', error);
      toast({
        title: 'PO draft creation failed',
        description: `The warehouse selections could not be converted into purchase orders. ${
          error instanceof Error ? error.message : String(error)
        }`,
        variant: 'destructive',
      });
    } finally {
      setCreatingOrders(false);
    }
  };

  // Backlog #14 "simple logic first": create a draft PO for a single low-stock item using
  // the last-known supplier + last-known order qty + last-known unit price. No usage or
  // lead-time forecasting in v1. Always requires a user click — no auto-creation.
  const handleSuggestPurchaseOrder = async (item: InventoryItem) => {
    if (!currentUser) {
      toast({
        title: 'No user session',
        description: 'Creating a purchase order requires a signed-in user.',
        variant: 'destructive',
      });
      return;
    }

    if (suggestingPoItemIds[item.id]) {
      return;
    }

    const lastLine = lastPurchaseOrderLineByItemId.get(item.id);
    const primarySupplier = primarySupplierByItemId.get(item.id);
    const supplierId = lastLine?.supplierId ?? primarySupplier?.supplierId ?? null;

    if (!supplierId) {
      toast({
        title: 'No supplier on record',
        description:
          'This item has no prior PO and no primary supplier mapping — assign a supplier before drafting a PO.',
        variant: 'destructive',
      });
      return;
    }

    const minimumOrderQty = primarySupplier?.minimumOrderQty ?? 0;
    const lastQty = lastLine?.qtyToOrder ?? 0;
    const fallbackQty = Math.max(
      primarySupplier?.reorderLevel ?? 0,
      minimumOrderQty,
      1
    );
    const qtyToOrder = Math.max(lastQty > 0 ? lastQty : fallbackQty, minimumOrderQty, 1);
    const unitCostSnapshot = lastLine?.unitCostSnapshot ?? item.cost_price ?? null;

    try {
      setSuggestingPoItemIds((prev) => ({ ...prev, [item.id]: true }));

      await inventoryService.createPurchaseOrderDraft({
        supplierId,
        createdBy: currentUser.id,
        createdByEmail: currentUser.email,
        items: [
          {
            inventoryItemId: item.id,
            qtyToOrder,
            unitCostSnapshot,
          },
        ],
      });

      toast({
        title: 'PO draft created',
        description: `${item.name} drafted with qty ${qtyToOrder}${lastQty > 0 ? ' (last-known order qty)' : ''}. Review on the Purchase Orders tab before sending.`,
      });

      setActiveTab('purchase-orders');
      await loadSnapshot();
    } catch (error) {
      console.error('Failed to suggest purchase order draft:', error);
      toast({
        title: 'PO draft failed',
        description:
          error instanceof Error
            ? error.message
            : 'The suggested purchase order could not be created.',
        variant: 'destructive',
      });
    } finally {
      setSuggestingPoItemIds((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
    }
  };

  const handleCreateIssueBatch = async () => {
    if (!currentUser) {
      toast({
        title: 'No user session',
        description: 'Warehouse actions require a signed-in user.',
        variant: 'destructive',
      });
      return;
    }

    const plannedLines = pickListLines.flatMap((line) =>
      line.roomDemands.map((roomDemand) => ({
        inventoryItemId: line.item.id,
        roomId: roomDemand.roomId,
        requestedQty: roomDemand.outstandingQty,
        plannedQty: roomDemand.plannedIssueQty,
        requestItemIds: roomDemand.requestItemIds,
      }))
    );

    if (plannedLines.length === 0) {
      toast({
        title: 'Nothing to pick',
        description: 'Use the planning view to set issue quantities first.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setCreatingIssueBatch(true);
      const issueBatch = await inventoryService.createIssueBatchFromPlan({
        createdBy: currentUser.id,
        createdByEmail: currentUser.email,
        issueNotes: 'Created from warehouse issue planning.',
        items: plannedLines,
      });

      setSelectedIssueBatchId(issueBatch.id);
      setWarehouseSubview('pick-list');
      setConfirmIssueQuantities({});
      setConfirmIssueReasons({});
      toast({
        title: 'Issue batch ready',
        description: `${issueBatch.batch_number} was created for picking and confirmation.`,
      });
      await loadSnapshot();
    } catch (error) {
      console.error('Failed to create issue batch:', error);
      toast({
        title: 'Issue batch failed',
        description: error instanceof Error ? error.message : 'The issue batch could not be created.',
        variant: 'destructive',
      });
    } finally {
      setCreatingIssueBatch(false);
    }
  };

  const handleConfirmIssueBatch = async () => {
    if (!currentUser || !selectedIssueBatch) {
      toast({
        title: 'No issue batch selected',
        description: 'Select an issue batch before confirming.',
        variant: 'destructive',
      });
      return;
    }

    const lines = selectedIssueBatchItems.map((item) => ({
      batchItemId: item.id,
      issuedQty: Math.max(0, Number(confirmIssueQuantities[item.id] ?? item.planned_qty ?? 0)),
      issueReason: confirmIssueReasons[item.id],
    }));
    const totalIssued = lines.reduce((sum, line) => sum + line.issuedQty, 0);

    if (totalIssued <= 0) {
      toast({
        title: 'No issued quantities',
        description: 'Enter at least one issued quantity before confirming stock movement.',
        variant: 'destructive',
      });
      return;
    }

    const batchId = selectedIssueBatch.id;
    if (!issueBatchIdempotencyKeys.current[batchId]) {
      issueBatchIdempotencyKeys.current[batchId] =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : '';
    }

    try {
      setConfirmingIssueBatch(true);
      const result = await inventoryService.confirmIssueBatch({
        batchId,
        confirmedBy: currentUser.id,
        confirmedByEmail: currentUser.email,
        idempotencyKey: issueBatchIdempotencyKeys.current[batchId] || undefined,
        lines,
      });
      const failedCount = Number(result.platoAdjustment?.failedCount ?? 0);

      toast({
        title: failedCount > 0 ? 'Issue confirmed, Plato retry needed' : 'Issue confirmed',
        description: failedCount > 0
          ? 'Local warehouse stock was deducted, but the Plato adjustment failed and can be retried.'
          : 'Local stock was deducted and the Plato stock adjustment was submitted.',
        variant: failedCount > 0 ? 'destructive' : 'default',
      });
      await loadSnapshot();
    } catch (error) {
      console.error('Failed to confirm issue batch:', error);
      toast({
        title: 'Confirm issue failed',
        description: error instanceof Error ? error.message : 'The issue confirmation could not be saved.',
        variant: 'destructive',
      });
    } finally {
      setConfirmingIssueBatch(false);
    }
  };

  const handleRetryPlatoAdjustment = async (batch: InventoryIssueBatch) => {
    try {
      setRetryingIssueBatchId(batch.id);
      const result = await inventoryService.retryPlatoAdjustment(batch.id);
      const failedCount = Number(result.failedCount ?? 0);

      toast({
        title: failedCount > 0 ? 'Plato retry failed' : 'Plato adjustment synced',
        description: failedCount > 0
          ? result.error ?? 'Plato still rejected the adjustment.'
          : 'The confirmed issue batch is now synced to Plato.',
        variant: failedCount > 0 ? 'destructive' : 'default',
      });
      await loadSnapshot();
    } catch (error) {
      console.error('Failed to retry Plato adjustment:', error);
      toast({
        title: 'Retry failed',
        description: error instanceof Error ? error.message : 'The Plato adjustment retry could not run.',
        variant: 'destructive',
      });
    } finally {
      setRetryingIssueBatchId(null);
    }
  };

  const handleUpdatePurchaseOrderStatus = async (
    purchaseOrderId: string,
    status: InventoryPurchaseOrder['status']
  ) => {
    try {
      await inventoryService.updatePurchaseOrderStatus(purchaseOrderId, status);
      toast({
        title: 'PO updated',
        description: `Purchase order marked ${titleCaseStatus(status)}.`,
      });
      await loadSnapshot();
    } catch (error) {
      console.error('Failed to update purchase order status:', error);
      toast({
        title: 'PO update failed',
        description: `The purchase order status could not be changed. ${
          error instanceof Error ? error.message : String(error)
        }`,
        variant: 'destructive',
      });
    }
  };

  // Backlog item #16, print half: printable/savable PO document. Most
  // suppliers have no email, so a PDF the clinic can WhatsApp is the
  // critical path for actually sending POs.
  const handlePrintPurchaseOrder = (
    card: (typeof purchaseOrderCards)[number]
  ) => {
    const { purchaseOrder, supplier, lines } = card;
    const opened = openPurchaseOrderPrintView({
      poNumber: purchaseOrder.po_number,
      orderDate: purchaseOrder.created_at,
      // Expected-delivery is not captured on inventory_purchase_orders yet —
      // the print view simply omits the row when absent.
      expectedDeliveryDate:
        (purchaseOrder as { expected_delivery_date?: string | null }).expected_delivery_date ?? null,
      supplierName: supplier?.name ?? 'Supplier record missing',
      supplierContactPerson: supplier?.contact_person ?? null,
      supplierPhone: supplier?.handphone ?? supplier?.telephone ?? null,
      supplierEmail: supplier?.email ?? null,
      notes: purchaseOrder.supplier_message,
      lines: lines.map((line) => ({
        itemCode: line.inventoryItem?.given_id ?? null,
        description:
          line.inventoryItem?.name ?? line.item_description ?? 'Unknown item',
        uom: line.uom ?? line.inventoryItem?.order_unit ?? line.inventoryItem?.unit ?? null,
        qty: line.qty_to_order,
        unitPrice: line.unit_price ?? line.unit_cost_snapshot ?? null,
      })),
    });

    if (!opened) {
      toast({
        title: 'Pop-up blocked',
        description:
          'The browser blocked the print window. Allow pop-ups for this site and try again.',
        variant: 'destructive',
      });
    }
  };

  const handleSaveOrderConfig = async () => {
    if (!selectedCatalogItem) {
      return;
    }

    const trimmedPackSize = packSizeDraft.trim();
    const parsedPackSize = trimmedPackSize ? Number(trimmedPackSize) : null;

    if (
      parsedPackSize != null &&
      (!Number.isFinite(parsedPackSize) || parsedPackSize <= 0)
    ) {
      toast({
        title: 'Invalid pack size',
        description: 'Pack size must be a number greater than zero, or left empty.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSavingOrderConfig(true);
      await inventoryService.updateInventoryItemOrderConfig(selectedCatalogItem.id, {
        orderUnit: orderUnitDraft.trim() || null,
        packSize: parsedPackSize,
      });
      toast({
        title: 'Order settings saved',
        description: `${selectedCatalogItem.name} order unit and pack size were updated.`,
      });
      await loadSnapshot();
    } catch (error) {
      console.error('Failed to save order unit / pack size:', error);
      toast({
        title: 'Save failed',
        description: `The order settings could not be saved. ${
          error instanceof Error ? error.message : String(error)
        }`,
        variant: 'destructive',
      });
    } finally {
      setSavingOrderConfig(false);
    }
  };

  const handleSyncCatalog = async () => {
    try {
      setSyncingCatalog(true);
      setSyncProgress(null);
      const result = await inventoryService.syncPlatoInventory((progress) => {
        setSyncProgress(progress);
      });
      await loadSnapshot();

      toast({
        title: 'Plato inventory synced',
        description: [
          result.importedItems > 0
            ? `${result.mode === 'incremental' ? 'Incremental' : 'Full'} sync refreshed ${result.importedItems} inventory item record${result.importedItems === 1 ? '' : 's'}.`
            : 'No inventory master records changed in Plato.',
          result.syncedStockItems > 0
            ? `Stock snapshot updated ${result.syncedStockItems} item${result.syncedStockItems === 1 ? '' : 's'}${result.stockLocation && result.stockDate ? ` from ${result.stockLocation} on ${result.stockDate}` : ''}.`
            : 'No stock values were updated from the Plato stock snapshot.',
          `Current Plato count: ${result.platoRecordCount}.`,
        ].join(' '),
      });
    } catch (error) {
      console.error('Failed to sync Plato inventory:', error);
      toast({
        title: 'Sync failed',
        description: `The Plato inventory sync could not complete. ${
          error instanceof Error ? error.message : String(error)
        }`,
        variant: 'destructive',
      });
    } finally {
      setSyncingCatalog(false);
    }
  };

  const handleSyncSuppliers = async () => {
    try {
      setSyncingSuppliers(true);
      setSyncProgress(null);
      const result = await inventoryService.syncPlatoSuppliers((progress) => {
        setSyncProgress(progress);
      });
      await loadSnapshot();

      toast({
        title: 'Plato suppliers synced',
        description:
          result.importedSuppliers > 0
            ? `Supplier sync refreshed ${result.importedSuppliers} supplier record(s) from Plato. Current Plato supplier count: ${result.platoRecordCount}.`
            : `No supplier records were returned from Plato. Current Plato supplier count: ${result.platoRecordCount}.`,
      });
    } catch (error) {
      console.error('Failed to sync Plato suppliers:', error);
      toast({
        title: 'Supplier sync failed',
        description: `The Plato supplier sync could not complete. ${
          error instanceof Error ? error.message : String(error)
        }`,
        variant: 'destructive',
      });
    } finally {
      setSyncingSuppliers(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-40 w-full rounded-2xl" />
        <div className="grid gap-4 md:grid-cols-3">
          <Skeleton className="h-28 rounded-3xl" />
          <Skeleton className="h-28 rounded-3xl" />
          <Skeleton className="h-28 rounded-3xl" />
        </div>
        <Skeleton className="h-[540px] rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {!canSeeWarehouse && accessibleRooms.length === 0 ? (
        <Alert className="border-amber-300 bg-amber-50">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Room access needs setup</AlertTitle>
          <AlertDescription>
            No room assignment exists yet for this user. The new request flow is live, but room-to-user access still needs to be configured in `inventory_room_user_access`.
          </AlertDescription>
        </Alert>
      ) : null}

      <Tabs value={activeTab} className="space-y-4">
        <div
          className={cn(
            'grid h-auto gap-2 rounded-2xl border border-slate-200 bg-white p-2',
            canSeeCatalog ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 sm:grid-cols-3'
          )}
        >
          <Button
            variant={activeTab === 'room-requests' ? 'default' : 'ghost'}
            className="rounded-xl px-4 py-3"
            asChild
          >
            <NavLink to={INVENTORY_TAB_ROUTES['room-requests']}>Room Requests</NavLink>
          </Button>
          {canSeeWarehouse ? (
            <Button
              variant={activeTab === 'warehouse' ? 'default' : 'ghost'}
              className="rounded-xl px-4 py-3"
              asChild
            >
              <NavLink to={INVENTORY_TAB_ROUTES.warehouse}>Warehouse</NavLink>
            </Button>
          ) : null}
          {canSeeWarehouse ? (
            <Button
              variant={activeTab === 'purchase-orders' ? 'default' : 'ghost'}
              className="rounded-xl px-4 py-3"
              asChild
            >
              <NavLink to={INVENTORY_TAB_ROUTES['purchase-orders']}>Purchase Orders</NavLink>
            </Button>
          ) : null}
          {canSeeCatalog ? (
            <Button
              variant={activeTab === 'catalog' ? 'default' : 'ghost'}
              className="rounded-xl px-4 py-3"
              asChild
            >
              <NavLink to={INVENTORY_TAB_ROUTES.catalog}>Setup (Admin)</NavLink>
            </Button>
          ) : null}
        </div>

        <TabsContent value="room-requests" className="space-y-4">
          <TabPurposeBanner>
            Need supplies for your room? Search an item, set the quantity, then tap Submit request.
          </TabPurposeBanner>
          <div className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
            <div className="space-y-4">
              <Card className="rounded-2xl border-slate-200 shadow-sm">
                <CardHeader className="space-y-3 pb-3">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                      <CardTitle className="text-xl text-slate-950">Room request pad</CardTitle>
                      <CardDescription className="mt-1 text-sm leading-5">
                        Built for quick iPad use: search a product, tap the quantity, and send the cart to warehouse.
                      </CardDescription>
                    </div>
                    <div className="min-w-[240px] space-y-2">
                      <Label htmlFor="room-selector">Active room</Label>
                      <Select
                        value={selectedRoom?.id ?? ''}
                        onValueChange={setSelectedRoomId}
                        disabled={!canSeeWarehouse}
                      >
                        <SelectTrigger id="room-selector">
                          <SelectValue placeholder="Select room" />
                        </SelectTrigger>
                        <SelectContent>
                          {accessibleRooms.map((room) => (
                            <SelectItem key={room.id} value={room.id}>
                              {room.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!canSeeWarehouse && accessibleRooms.length > 0 ? (
                        <p className="text-xs text-slate-500">
                          No explicit room mapping exists yet, so the request pad is using the best clinic-room fallback.
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-600">
                    <span><span className="font-semibold text-slate-950">{requestSummary.draftCount}</span> request line{requestSummary.draftCount === 1 ? '' : 's'}</span>
                    <span className="text-slate-300">·</span>
                    <span><span className="font-semibold text-slate-950">{requestSummary.totalDraftQuantity}</span> in cart</span>
                    <span className="text-slate-300">·</span>
                    <span><span className="font-semibold text-slate-950">{requestSummary.pendingOutstandingItems}</span> pending</span>
                    <span className="text-slate-300">·</span>
                    <span><span className="font-semibold text-slate-950">{requestSummary.monthlyCount}</span> this month</span>
                  </div>
                </CardHeader>
              </Card>

              <Card className="rounded-2xl border-slate-200 shadow-sm">
                <CardHeader className="space-y-3 pb-3">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <CardTitle className="text-lg text-slate-950">Requestable items</CardTitle>
                      <CardDescription className="mt-0.5">
                        Searching across the live Plato catalog imported into warehouse inventory.
                      </CardDescription>
                    </div>
                    <div className="relative w-full lg:max-w-sm">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <Input
                        value={roomSearch}
                        onChange={(event) => setRoomSearch(event.target.value)}
                        className="pl-9"
                        placeholder="Search by item, code, category, manufacturer"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[460px] pr-4">
                    <div className="space-y-2">
                      {filteredRequestableItems.map((item) => {
                        const draft = requestDraft[item.id] ?? { quantity: 0, note: '' };
                        const supplier = primarySupplierByItemId.get(item.id);

                        return (
                          <div
                            key={item.id}
                            className="rounded-3xl border border-slate-200 bg-white p-4 transition-colors hover:border-slate-300 hover:bg-slate-50/70"
                          >
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                              <div className="space-y-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="text-base font-semibold text-slate-950">{item.name}</p>
                                  {item.category ? <Badge variant="outline">{item.category}</Badge> : null}
                                  {!item.is_active ? (
                                    <Badge className="border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-50">
                                      Inactive
                                    </Badge>
                                  ) : null}
                                  {item.given_id ? (
                                    <Badge variant="secondary" className="font-mono">
                                      {item.given_id}
                                    </Badge>
                                  ) : null}
                                </div>
                                <p className="text-sm text-slate-600">
                                  {item.manufacturer || 'Manufacturer not set'} ·{' '}
                                  {item.unit || item.order_unit || 'Unit pending'} ·{' '}
                                  {item.warehouse_on_hand} on hand
                                </p>
                                <p className="text-xs text-slate-500">
                                  {supplier?.supplierName ?? 'Supplier mapping pending'} · synced{' '}
                                  {formatAgo(item.last_plato_synced_at)}
                                </p>
                                {!item.is_active ? (
                                  <p className="text-xs text-amber-700">
                                    Inactive items stay requestable, but they are excluded from normal warehouse low-stock monitoring.
                                  </p>
                                ) : null}
                              </div>

                              <div className="flex flex-wrap items-center gap-2">
                                {[1, 2, 3, 5].map((quickQty) => (
                                  <Button
                                    key={quickQty}
                                    type="button"
                                    variant={draft.quantity === quickQty ? 'default' : 'outline'}
                                    className="h-11 rounded-2xl px-4"
                                    onClick={() => handleRequestQuantityIntent(item, quickQty)}
                                  >
                                    {quickQty}
                                  </Button>
                                ))}
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="h-11 rounded-2xl px-4"
                                  onClick={() => handleRequestQuantityIntent(item, Math.max(draft.quantity - 1, 0))}
                                  aria-label={`Decrease ${item.name}`}
                                >
                                  <Minus className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="h-11 rounded-2xl px-4"
                                  onClick={() => handleRequestQuantityIntent(item, draft.quantity + 1 || 1)}
                                  aria-label={`Increase ${item.name}`}
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>

                            {draft.quantity > 0 ? (
                              <div className="mt-4 space-y-2 rounded-2xl border border-amber-200 bg-amber-50 p-3">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="font-medium text-amber-900">
                                    {draft.quantity} unit{draft.quantity === 1 ? '' : 's'} in request cart
                                  </span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-auto px-2 py-1 text-amber-900"
                                    onClick={() => handleDraftQuantityChange(item.id, 0)}
                                  >
                                    Remove
                                  </Button>
                                </div>
                                <Textarea
                                  value={draft.note}
                                  onChange={(event) => handleDraftNoteChange(item.id, event.target.value)}
                                  rows={2}
                                  placeholder="Optional note for warehouse"
                                  className="resize-none bg-white"
                                />
                              </div>
                            ) : null}
                          </div>
                        );
                      })}

                      {filteredRequestableItems.length === 0 ? (
                        <div className="rounded-3xl border border-dashed border-slate-300 px-6 py-16 text-center">
                          <p className="text-lg font-medium text-slate-900">No items matched the request search.</p>
                          <p className="mt-2 text-sm text-slate-500">
                            Try item name, Plato code, category, or manufacturer.
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-4">
              <Card className="rounded-2xl border-slate-200 shadow-sm">
                <CardHeader className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <CardTitle className="text-xl text-slate-950">Request cart</CardTitle>
                      <CardDescription className="mt-1">
                        Final review before this room sends the request batch to warehouse.
                      </CardDescription>
                    </div>
                    <ShoppingCart className="h-5 w-5 text-slate-400" />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {draftLines.length > 0 ? (
                    <div className="space-y-3">
                      {draftLines.map(({ item, draft }) => (
                        <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-medium text-slate-950">{item.name}</p>
                                {!item.is_active ? (
                                  <Badge className="border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-50">
                                    Inactive
                                  </Badge>
                                ) : null}
                              </div>
                              <p className="mt-1 text-sm text-slate-600">
                                Qty {draft.quantity} · {item.unit || item.order_unit || 'units'}
                              </p>
                              {draft.note ? (
                                <p className="mt-1 text-xs text-slate-500">{draft.note}</p>
                              ) : null}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 rounded-xl"
                                onClick={() => handleDraftQuantityChange(item.id, Math.max(draft.quantity - 1, 0))}
                                aria-label={`Decrease ${item.name}`}
                              >
                                <Minus className="h-3.5 w-3.5" />
                              </Button>
                              <Input
                                value={draft.quantity}
                                onChange={(event) => {
                                  const quantity = Number(event.target.value);
                                  handleDraftQuantityChange(item.id, Number.isFinite(quantity) ? quantity : 0);
                                }}
                                className="h-8 w-16 rounded-xl text-center"
                                inputMode="numeric"
                                aria-label={`${item.name} request quantity`}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 rounded-xl"
                                onClick={() => handleRequestQuantityIntent(item, draft.quantity + 1)}
                                aria-label={`Increase ${item.name}`}
                              >
                                <Plus className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 rounded-xl text-slate-500"
                                onClick={() => handleDraftQuantityChange(item.id, 0)}
                                aria-label={`Remove ${item.name}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-3xl border border-dashed border-slate-300 px-6 py-14 text-center">
                      <p className="text-lg font-medium text-slate-900">No items in the request cart yet.</p>
                      <p className="mt-2 text-sm text-slate-500">
                        Tap quantities on the left and they will collect here for the room to submit.
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="room-request-notes">Batch note</Label>
                    <Textarea
                      id="room-request-notes"
                      value={requestNotes}
                      onChange={(event) => setRequestNotes(event.target.value)}
                      rows={3}
                      placeholder="Optional note for the warehouse team"
                      className="resize-none"
                    />
                  </div>

                  <Button
                    className="h-12 w-full rounded-2xl"
                    disabled={submittingRequest || draftLines.length === 0 || !selectedRoom}
                    onClick={handleSubmitRoomRequest}
                  >
                    {submittingRequest ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending request
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Submit room request
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              <Card className="rounded-2xl border-slate-200 shadow-sm">
                <details className="group">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-6 py-4">
                    <div>
                      <p className="text-base font-semibold text-slate-950">Pending items for this room</p>
                      <p className="mt-0.5 text-xs text-slate-500">What warehouse still owes this room ({roomPendingDemands.length})</p>
                    </div>
                    <ChevronDown className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-open:rotate-180" />
                  </summary>
                <CardContent>
                  {roomPendingDemands.length > 0 ? (
                    <div className="space-y-3">
                      {roomPendingDemands.slice(0, 8).map((demand) => (
                        <div key={demand.item.id} className="rounded-2xl border border-slate-200 px-4 py-3">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="min-w-0 space-y-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-medium text-slate-950">{demand.item.name}</p>
                                {demand.item.given_id ? (
                                  <Badge variant="secondary" className="font-mono">
                                    {demand.item.given_id}
                                  </Badge>
                                ) : null}
                              </div>
                              <p className="text-xs text-slate-500">
                                Requested {demand.requestedQty} · issued {demand.fulfilledQty} ·{' '}
                                {demand.requestCount} pending line{demand.requestCount === 1 ? '' : 's'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-semibold text-slate-950">{demand.outstandingQty}</p>
                              <p className="text-xs text-slate-500">{demand.item.unit || demand.item.order_unit || 'units'} outstanding</p>
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            {[...demand.statuses].map((status) => (
                              <Badge key={status} variant="outline" className="capitalize">
                                {titleCaseStatus(status)}
                              </Badge>
                            ))}
                            <span className="text-xs text-slate-500">
                              Latest {formatAgo(demand.latestSubmittedAt)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-3xl border border-dashed border-slate-300 px-6 py-12 text-center">
                      <p className="text-lg font-medium text-slate-900">No pending items for this room.</p>
                      <p className="mt-2 text-sm text-slate-500">
                        Submitted items will appear here until warehouse issues or closes them.
                      </p>
                    </div>
                  )}
                </CardContent>
                </details>
              </Card>

              <Card className="rounded-2xl border-slate-200 shadow-sm">
                <details className="group">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-6 py-4">
                    <div>
                      <p className="text-base font-semibold text-slate-950">This month's request history</p>
                      <p className="mt-0.5 text-xs text-slate-500">Submitted batches for this room this month ({monthlyRoomRequests.length})</p>
                    </div>
                    <ChevronDown className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-open:rotate-180" />
                  </summary>
                <CardContent>
                  <div className="space-y-3">
                    {monthlyRoomRequests.slice(0, 6).map((request) => {
                      const lines = requestItemsByRequestId.get(request.id) ?? [];

                      return (
                        <div key={request.id} className="rounded-3xl border border-slate-200 px-4 py-4">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="space-y-1">
                              <p className="font-medium text-slate-950">
                                {lines.length} line{lines.length === 1 ? '' : 's'} ·{' '}
                                {lines.reduce((sum, line) => sum + line.requested_qty, 0)} requested
                              </p>
                              <p className="text-sm text-slate-500">
                                {formatStamp(request.submitted_at || request.created_at)}
                              </p>
                            </div>
                            <Badge className={cn('capitalize', REQUEST_STATUS_META[request.status])}>
                              {titleCaseStatus(request.status)}
                            </Badge>
                          </div>
                          {request.request_notes ? (
                            <p className="mt-3 text-sm text-slate-600">{request.request_notes}</p>
                          ) : null}
                        </div>
                      );
                    })}

                    {monthlyRoomRequests.length === 0 ? (
                      <div className="rounded-3xl border border-dashed border-slate-300 px-6 py-14 text-center">
                        <p className="text-lg font-medium text-slate-900">No requests this month.</p>
                        <p className="mt-2 text-sm text-slate-500">
                          The first request batch will appear here right after submission.
                        </p>
                      </div>
                    ) : null}
                  </div>
                </CardContent>
                </details>
              </Card>
            </div>
          </div>
          {/* iPad / narrow-width: keep the primary submit action in view */}
          {draftLines.length > 0 ? (
            <>
              <div className="h-20 lg:hidden" aria-hidden="true" />
              <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-4px_16px_rgba(15,23,42,0.08)] backdrop-blur lg:hidden">
                <Button
                  className="h-12 w-full rounded-2xl"
                  disabled={submittingRequest || draftLines.length === 0 || !selectedRoom}
                  onClick={handleSubmitRoomRequest}
                >
                  {submittingRequest ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending request
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Submit request ({draftLines.length} item{draftLines.length === 1 ? '' : 's'})
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : null}
        </TabsContent>

        {canSeeWarehouse ? (
          <TabsContent value="warehouse" className="space-y-4">
            <TabPurposeBanner>
              Hand out requested items to rooms, and see what&apos;s running low to reorder.
            </TabPurposeBanner>
            {warehouseSummary.planningItems === 0 &&
            warehouseSummary.plannedPickUnits === 0 &&
            warehouseSummary.withoutSupplier === 0 &&
            warehouseSummary.outstandingDemand === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
                Nothing waiting in the warehouse right now — no items to hand out and nothing still owed to rooms.
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-600">
                <span><span className="font-semibold text-slate-950">{warehouseSummary.planningItems}</span> to hand out</span>
                <span className="text-slate-300">·</span>
                <span><span className="font-semibold text-slate-950">{warehouseSummary.plannedPickUnits}</span> pick units</span>
                <span className="text-slate-300">·</span>
                <span><span className="font-semibold text-slate-950">{warehouseSummary.withoutSupplier}</span> without supplier</span>
                <span className="text-slate-300">·</span>
                <span><span className="font-semibold text-slate-950">{warehouseSummary.outstandingDemand}</span> still owed to rooms</span>
              </div>
            )}

            <Card className="rounded-2xl border-slate-200 shadow-sm">
              <CardHeader className="space-y-3 pb-3">
                <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                  <div>
                    <CardTitle className="text-lg text-slate-950">Warehouse workflow</CardTitle>
                    <CardDescription className="mt-1 max-w-3xl text-sm leading-5">
                      Plan issue by item across all rooms first, then use the pick list, room forms, and PO shortlist as supporting views.
                    </CardDescription>
                  </div>
                  <div className="flex w-full flex-col gap-3 xl:w-auto xl:flex-row">
                    <Select value={selectedWarehouseRoomId} onValueChange={setSelectedWarehouseRoomId}>
                      <SelectTrigger className="min-w-[220px] rounded-2xl">
                        <SelectValue placeholder="Filter by room" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All rooms</SelectItem>
                        {warehouseFilterRooms.map((room) => (
                          <SelectItem key={room.id} value={room.id}>
                            {room.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="relative xl:w-80">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <Input
                        value={warehouseSearch}
                        onChange={(event) => setWarehouseSearch(event.target.value)}
                        className="pl-9"
                        placeholder="Search items"
                      />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs
                  value={warehouseSubview}
                  onValueChange={(value) => setWarehouseSubview(value as WarehouseSubview)}
                  className="space-y-4"
                >
                  <TabsList className="h-auto flex-wrap justify-start rounded-2xl bg-slate-100 p-1">
                    <TabsTrigger value="planning" className="rounded-2xl px-4 py-2.5">
                      Hand out supplies
                    </TabsTrigger>
                    <TabsTrigger value="ordering" className="rounded-2xl px-4 py-2.5">
                      Low stock — order these
                      {suggestedReorders.length > 0 ? (
                        <Badge className="ml-2 border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-50">
                          {suggestedReorders.length}
                        </Badge>
                      ) : null}
                    </TabsTrigger>
                    <TabsTrigger value="pick-list" className="rounded-2xl px-4 py-2.5">
                      Pick list
                    </TabsTrigger>
                    <TabsTrigger value="requests" className="rounded-2xl px-4 py-2.5">
                      Room request forms
                    </TabsTrigger>
                    <TabsTrigger value="stock-take" className="rounded-2xl px-4 py-2.5">
                      Stock-take
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="planning" className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="planning-shortages-only"
                            checked={planningShortagesOnly}
                            onCheckedChange={(checked) => setPlanningShortagesOnly(checked === true)}
                          />
                          <Label
                            htmlFor="planning-shortages-only"
                            className="cursor-pointer text-sm font-normal text-slate-600"
                          >
                            Shortages only
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="planning-stock-details"
                            checked={planningShowStockDetails}
                            onCheckedChange={(checked) => setPlanningShowStockDetails(checked === true)}
                          />
                          <Label
                            htmlFor="planning-stock-details"
                            className="cursor-pointer text-sm font-normal text-slate-600"
                          >
                            Show stock details
                          </Label>
                        </div>
                      </div>
                      <Button
                        onClick={handleCreateIssueBatch}
                        disabled={creatingIssueBatch || pickListLines.length === 0}
                        className="rounded-2xl"
                      >
                        {creatingIssueBatch ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Creating batch
                          </>
                        ) : (
                          <>
                            <ClipboardList className="mr-2 h-4 w-4" />
                            Hand out items
                          </>
                        )}
                      </Button>
                    </div>
                    {visiblePlanningRows.length === 0 ? (
                      <div className="rounded-3xl border border-dashed border-slate-300 px-6 py-16 text-center">
                        <p className="text-lg font-medium text-slate-900">
                          {planningShortagesOnly && issuePlanningRows.length > 0
                            ? 'No shortage rows right now.'
                            : 'No open room demand to plan.'}
                        </p>
                        <p className="mt-2 text-sm text-slate-500">
                          {planningShortagesOnly && issuePlanningRows.length > 0
                            ? 'Untick "Shortages only" to see all planned items.'
                            : 'Submitted room requests will consolidate by item here.'}
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-hidden rounded-3xl border border-slate-200">
                        <Table>
                          <TableHeader className="bg-slate-50">
                            <TableRow>
                              <TableHead className="min-w-[260px]">Item</TableHead>
                              <TableHead className="min-w-[320px]">
                                <LabelWithHelp
                                  align="start"
                                  label="Room demand"
                                  help="Each room's open demand for this item, with planned issue quantity per room. Click a row to expand."
                                />
                              </TableHead>
                              {planningShowStockDetails ? (
                                <>
                                  <TableHead className="text-right">
                                    <LabelWithHelp
                                      label="Open"
                                      help="Total open quantity requested across all rooms for this item."
                                    />
                                  </TableHead>
                                  <TableHead className="text-right">
                                    <LabelWithHelp
                                      label="On hand"
                                      help="Current warehouse stock-on-hand for this item."
                                    />
                                  </TableHead>
                                  <TableHead className="text-right">
                                    <LabelWithHelp
                                      label="Planned issue"
                                      help="Recommended issue quantity per room, computed by fair-share distribution of available stock across all rooms requesting this item. A room may show 0 if other rooms with outstanding demand should be filled first."
                                    />
                                  </TableHead>
                                  <TableHead className="text-right">
                                    <LabelWithHelp
                                      label="On order"
                                      help="Quantity already on an open purchase order to a supplier."
                                    />
                                  </TableHead>
                                  <TableHead className="text-right">
                                    <LabelWithHelp
                                      label="Shortage"
                                      help="Amount that cannot be fulfilled from current stock + on-order — needs a new PO."
                                    />
                                  </TableHead>
                                </>
                              ) : null}
                              <TableHead className="text-right">Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {visiblePlanningRows.map((row) => (
                              <TableRow
                                key={row.item.id}
                                className={cn(row.shortageQty > 0 ? 'bg-rose-50/40' : undefined)}
                              >
                                <TableCell>
                                  <div className="space-y-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <p className="font-medium text-slate-950">{row.item.name}</p>
                                      {!row.item.is_active ? (
                                        <Badge className="border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-50">
                                          Inactive item
                                        </Badge>
                                      ) : null}
                                    </div>
                                    <p className="text-xs text-slate-500">
                                      {row.item.given_id ?? 'No code'} · {row.item.unit || row.item.order_unit || 'units'}
                                    </p>
                                    <p className="text-xs text-slate-500">{row.supplier.supplierName}</p>
                                    <div className="flex flex-wrap gap-2 pt-2">
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="h-8 rounded-xl px-3 text-xs"
                                        onClick={() => handleUseRecommendedIssuePlan(row)}
                                      >
                                        <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                                        Fair split
                                      </Button>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 rounded-xl px-3 text-xs text-slate-600"
                                        onClick={() => handleClearIssuePlan(row)}
                                      >
                                        Clear
                                      </Button>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="grid gap-2 sm:grid-cols-2">
                                    {row.roomDemands.map((roomDemand) => (
                                      <div
                                        key={roomDemand.roomId}
                                        className="rounded-2xl border border-slate-200 bg-white px-3 py-2"
                                      >
                                        <div className="flex items-center justify-between gap-2">
                                          <span className="truncate text-sm font-medium text-slate-900">
                                            {roomDemand.roomName}
                                          </span>
                                          <div className="flex items-center gap-2">
                                            {roomDemand.isPlanEdited ? (
                                              <Badge variant="outline" className="rounded-lg px-2 py-0 text-[11px]">
                                                Edited
                                              </Badge>
                                            ) : null}
                                            <span className="text-sm font-semibold text-slate-950">
                                              {roomDemand.outstandingQty}
                                            </span>
                                          </div>
                                        </div>
                                        <div className="mt-2 space-y-2">
                                          <div className="space-y-1 text-xs text-slate-500">
                                            <p>Requested {roomDemand.requestedQty}</p>
                                            <p>Recommended {roomDemand.recommendedIssueQty}</p>
                                            <p className="font-medium text-rose-700">
                                              Short {roomDemand.shortageQty}
                                            </p>
                                          </div>
                                          <div>
                                            <Label
                                              htmlFor={`issue-plan-${row.item.id}-${roomDemand.roomId}`}
                                              className="sr-only"
                                            >
                                              Planned issue for {roomDemand.roomName}
                                            </Label>
                                            <Input
                                              id={`issue-plan-${row.item.id}-${roomDemand.roomId}`}
                                              type="number"
                                              inputMode="numeric"
                                              min={0}
                                              max={roomDemand.outstandingQty}
                                              value={roomDemand.plannedIssueQty}
                                              onChange={(event) =>
                                                handleIssuePlanQuantityChange(
                                                  row.item.id,
                                                  roomDemand,
                                                  Number(event.target.value)
                                                )
                                              }
                                              className="h-9 w-full rounded-xl text-right"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </TableCell>
                                {planningShowStockDetails ? (
                                  <>
                                    <TableCell className="text-right font-medium">{row.totalOutstandingQty}</TableCell>
                                    <TableCell className="text-right">{row.item.warehouse_on_hand}</TableCell>
                                    <TableCell className="text-right font-semibold text-slate-950">{row.plannedIssueQty}</TableCell>
                                    <TableCell className="text-right">{row.onOrderQty}</TableCell>
                                    <TableCell className="text-right font-semibold text-rose-700">{row.shortageQty}</TableCell>
                                  </>
                                ) : null}
                                <TableCell className="text-right">
                                  <Badge
                                    className={cn(
                                      'capitalize',
                                      row.status === 'hard_shortage'
                                        ? 'border-rose-300 bg-rose-50 text-rose-900'
                                        : row.status === 'over_planned'
                                          ? 'border-rose-300 bg-rose-50 text-rose-900'
                                          : row.status === 'on_order'
                                            ? 'border-blue-300 bg-blue-50 text-blue-900'
                                            : row.status === 'partial_plan'
                                              ? 'border-amber-300 bg-amber-50 text-amber-900'
                                              : row.status === 'monitoring'
                                                ? 'border-violet-300 bg-violet-50 text-violet-900'
                                                : 'border-emerald-300 bg-emerald-50 text-emerald-900'
                                    )}
                                  >
                                    {titleCaseStatus(row.status)}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="ordering" className="space-y-4">
                    {/* B (PO builder): the reorder board is a READ-ONLY
                        reference checklist. Nothing here is ordered until a
                        draft PO is built from the catalog/warehouse flow. */}
                    <div className="flex items-start gap-2 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3">
                      <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" aria-hidden="true" />
                      <p className="text-xs leading-relaxed text-sky-900">
                        <span className="font-semibold">Suggestions only — nothing is ordered until you build an order.</span>{' '}
                        This board is a reference checklist of what stock is low. Use <span className="font-medium">Add to PO</span> on
                        any row to start a draft purchase order; quantities are pre-filled MOQ-aware and stay editable on the draft.
                      </p>
                    </div>
                    {/* Suggested reorders (backlog item #14): stock-level
                        watchlist independent of room-request demand. */}
                    <div className="space-y-3 rounded-3xl border border-slate-200 bg-white px-5 py-5">
                      <div className="flex flex-wrap items-center gap-2">
                        <ShoppingCart className="h-4 w-4 text-slate-500" />
                        <p className="text-sm font-medium text-slate-950">Suggested reorders</p>
                        <Badge
                          className={cn(
                            suggestedReorders.length > 0
                              ? 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-50'
                              : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-50'
                          )}
                        >
                          {suggestedReorders.length} item{suggestedReorders.length === 1 ? '' : 's'} at or below reorder level
                        </Badge>
                      </div>
                      {suggestedReorders.length === 0 ? (
                        <p className="text-sm text-slate-500">
                          Every mapped item with a reorder level is still above it. Items appear here the moment
                          warehouse stock falls to or below the supplier reorder level.
                        </p>
                      ) : (
                        <div className="overflow-hidden rounded-2xl border border-slate-200">
                          <Table className="[&_td]:py-2 [&_th]:h-9">
                            <TableHeader className="bg-slate-50">
                              <TableRow>
                                <TableHead>Item</TableHead>
                                <TableHead>Suggested supplier</TableHead>
                                <TableHead className="text-right">On hand</TableHead>
                                <TableHead className="text-right">
                                  <LabelWithHelp
                                    label="Reorder level"
                                    help="Reorder threshold set on the item's supplier mapping. Items show here once on-hand stock is at or below this level."
                                  />
                                </TableHead>
                                <TableHead className="text-right">
                                  <LabelWithHelp
                                    label="Last unit cost"
                                    help="Most recent unit cost recorded on a purchase order line for this item."
                                  />
                                </TableHead>
                                <TableHead className="text-right">Action</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {(suggestedReordersExpanded
                                ? suggestedReorders
                                : suggestedReorders.slice(0, SUGGESTED_REORDERS_COLLAPSED)
                              ).map((row) => (
                                <TableRow key={row.item.id} className="bg-amber-50/30">
                                  <TableCell>
                                    <div className="space-y-1">
                                      <p className="font-medium text-slate-950">{row.item.name}</p>
                                      <p className="text-xs text-slate-500">
                                        {row.item.given_id ?? 'No code'} · {row.item.unit || row.item.order_unit || 'units'}
                                      </p>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="space-y-1">
                                      <p className="font-medium text-slate-900">{row.supplier.supplierName}</p>
                                      <p className="text-xs text-slate-500">
                                        {row.supplier.leadTimeDays != null
                                          ? `${row.supplier.leadTimeDays} day lead time`
                                          : 'Lead time not set'}
                                      </p>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-right font-semibold text-amber-900">
                                    {row.item.warehouse_on_hand}
                                  </TableCell>
                                  <TableCell className="text-right">{row.supplier.reorderLevel}</TableCell>
                                  <TableCell className="text-right">
                                    {row.lastCostCents != null ? formatCentsSGD(row.lastCostCents) : '—'}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="rounded-xl"
                                      onClick={() => {
                                        setDirectPoInitialDraft({
                                          supplierId: row.supplier.supplierId ?? undefined,
                                          lines: [{ inventoryItemId: row.item.id }],
                                        });
                                        setDirectPoDialogOpen(true);
                                      }}
                                    >
                                      <Plus className="mr-1.5 h-3.5 w-3.5" />
                                      Add to PO
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                      {suggestedReorders.length > SUGGESTED_REORDERS_COLLAPSED ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="rounded-xl"
                          onClick={() => setSuggestedReordersExpanded((value) => !value)}
                        >
                          {suggestedReordersExpanded
                            ? 'Show fewer'
                            : `Show all ${suggestedReorders.length} suggestions`}
                        </Button>
                      ) : null}
                    </div>

                    {/* A3: items with a supplier but no reorder rule — surfaced
                        explicitly so an UNSET threshold isn't mistaken for
                        "nothing to order". */}
                    {reorderNeedsSetup.length > 0 && (
                      <div className="space-y-3 rounded-3xl border border-slate-200 bg-white px-5 py-5">
                        <div className="flex flex-wrap items-center gap-2">
                          <Info className="h-4 w-4 text-slate-500" aria-hidden="true" />
                          <p className="text-sm font-medium text-slate-950">Needs reorder setup</p>
                          <Badge className="border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-50">
                            {reorderNeedsSetup.length} item{reorderNeedsSetup.length === 1 ? '' : 's'} with no reorder level
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500">
                          These items have a primary supplier but no reorder level set, so they never appear in the
                          suggestions above. Set a reorder level on the item's supplier mapping to start tracking them.
                        </p>
                        <div className="overflow-hidden rounded-2xl border border-slate-200">
                          <Table className="[&_td]:py-2 [&_th]:h-9">
                            <TableHeader className="bg-slate-50">
                              <TableRow>
                                <TableHead>Item</TableHead>
                                <TableHead>Primary supplier</TableHead>
                                <TableHead className="text-right">On hand</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {(reorderNeedsSetupExpanded
                                ? reorderNeedsSetup
                                : reorderNeedsSetup.slice(0, SUGGESTED_REORDERS_COLLAPSED)
                              ).map((row) => (
                                <TableRow key={row.item.id}>
                                  <TableCell>
                                    <div className="space-y-1">
                                      <p className="font-medium text-slate-950">{row.item.name}</p>
                                      <p className="text-xs text-slate-500">
                                        {row.item.given_id ?? 'No code'} · {row.item.unit || row.item.order_unit || 'units'}
                                      </p>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-slate-700">{row.supplier.supplierName}</TableCell>
                                  <TableCell className="text-right text-slate-700">{row.item.warehouse_on_hand}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                        {reorderNeedsSetup.length > SUGGESTED_REORDERS_COLLAPSED ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="rounded-xl"
                            onClick={() => setReorderNeedsSetupExpanded((value) => !value)}
                          >
                            {reorderNeedsSetupExpanded
                              ? 'Show fewer'
                              : `Show all ${reorderNeedsSetup.length} items`}
                          </Button>
                        ) : null}
                      </div>
                    )}

                    <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                      <p className="max-w-3xl text-sm leading-6 text-slate-600">
                        Shortages and low-cover items stay here for PO drafting while issue quantities are planned in the item view.
                      </p>
                      <div className="flex flex-col gap-3 sm:flex-row">
                        <Button
                          variant="outline"
                          className="rounded-2xl"
                          onClick={() => {
                            const nextState: Record<string, boolean> = {};
                            warehouseRows
                              .filter((row) => row.shortageQty > 0 && row.supplier.supplierId)
                              .forEach((row) => {
                                nextState[row.id] = true;
                              });
                            setSelectedWarehouseItems(nextState);
                          }}
                        >
                          Select all shortages
                        </Button>
                        <Button
                          className="rounded-2xl"
                          disabled={creatingOrders || selectedWarehouseRows.length === 0}
                          onClick={handleCreatePurchaseOrders}
                        >
                          {creatingOrders ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Building PO drafts
                            </>
                          ) : (
                            <>
                              <PackageCheck className="mr-2 h-4 w-4" />
                              Create PO drafts
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                    {warehouseRows.length === 0 ? (
                      <div className="rounded-3xl border border-dashed border-slate-300 px-6 py-16 text-center">
                        <p className="text-lg font-medium text-slate-900">Warehouse is clear right now.</p>
                        <p className="mt-2 text-sm text-slate-500">
                          Once rooms submit demand, the priority board will sort shortages and low-cover items here.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                      {(() => {
                        const attentionRows = warehouseRows.filter(
                          (row) =>
                            row.shortageQty > 0 ||
                            (row.reorderLevel > 0 && row.projectedRemaining <= row.reorderLevel)
                        );
                        const hasHiddenRows = !orderingShowAll && attentionRows.length < warehouseRows.length;
                        const displayWarehouseRows =
                          orderingShowAll || attentionRows.length === 0 ? warehouseRows : attentionRows;
                        const orderingTotalPages = Math.max(
                          1,
                          Math.ceil(displayWarehouseRows.length / ORDERING_ITEMS_PER_PAGE)
                        );
                        const safeOrderingPage = Math.min(orderingPage, orderingTotalPages);
                        const orderingStart = (safeOrderingPage - 1) * ORDERING_ITEMS_PER_PAGE;
                        const pagedWarehouseRows = displayWarehouseRows.slice(
                          orderingStart,
                          orderingStart + ORDERING_ITEMS_PER_PAGE
                        );
                        return (
                      <>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs text-slate-500">
                          Showing {displayWarehouseRows.length} of {warehouseRows.length} item
                          {warehouseRows.length === 1 ? '' : 's'}
                          {!orderingShowAll && attentionRows.length > 0 && attentionRows.length < warehouseRows.length
                            ? ' that need ordering attention'
                            : ''}
                          .
                        </p>
                        {attentionRows.length > 0 && attentionRows.length < warehouseRows.length ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="rounded-xl"
                            onClick={() => {
                              setOrderingShowAll((value) => !value);
                              setOrderingPage(1);
                            }}
                          >
                            {orderingShowAll ? 'Show only low stock' : `Show all items (${warehouseRows.length})`}
                          </Button>
                        ) : null}
                      </div>
                      <div className="overflow-hidden rounded-2xl border border-slate-200">
                        <Table className="[&_td]:py-2 [&_th]:h-9">
                          <TableHeader className="bg-slate-50">
                            <TableRow>
                              <TableHead className="w-12">
                                <Checkbox
                                  checked={
                                    warehouseRows.length > 0 &&
                                    warehouseRows.every((row) => selectedWarehouseItems[row.id])
                                  }
                                  onCheckedChange={(checked) => {
                                    if (!checked) {
                                      setSelectedWarehouseItems({});
                                      return;
                                    }

                                    const nextState: Record<string, boolean> = {};
                                    warehouseRows.forEach((row) => {
                                      nextState[row.id] = true;
                                    });
                                    setSelectedWarehouseItems(nextState);
                                  }}
                                />
                              </TableHead>
                              <TableHead>Room</TableHead>
                              <TableHead>Item</TableHead>
                              <TableHead>Supplier</TableHead>
                              <TableHead className="text-right">
                                <LabelWithHelp
                                  label="On hand"
                                  help="Current warehouse stock-on-hand for this item, pulled from Plato."
                                />
                              </TableHead>
                              <TableHead className="text-right">
                                <LabelWithHelp
                                  label="Requested"
                                  help="Quantity requested by this specific room request line."
                                />
                              </TableHead>
                              <TableHead className="text-right">
                                <LabelWithHelp
                                  label="Item demand"
                                  help="Sum of all open room requests for this same item across every room. May exceed the single-row Requested value when multiple rooms have ordered the same item."
                                />
                              </TableHead>
                              <TableHead className="text-right">
                                <LabelWithHelp
                                  label="Remaining"
                                  help="Outstanding quantity still pending fulfilment after planned issues are accounted for."
                                />
                              </TableHead>
                              <TableHead className="text-right">
                                <LabelWithHelp
                                  label="Shortage"
                                  help="Amount that cannot be fulfilled from current warehouse stock — needs a purchase order."
                                />
                              </TableHead>
                              <TableHead className="text-right">Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {pagedWarehouseRows.map((row) => (
                              <TableRow key={row.id} className={cn(row.shortageQty > 0 ? 'bg-rose-50/50' : undefined)}>
                                <TableCell>
                                  <Checkbox
                                    checked={Boolean(selectedWarehouseItems[row.id])}
                                    onCheckedChange={(checked) =>
                                      setSelectedWarehouseItems((current) => ({
                                        ...current,
                                        [row.id]: Boolean(checked),
                                      }))
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <div className="space-y-1">
                                    <p className="font-medium text-slate-950">
                                      {row.room?.name ?? 'Warehouse monitoring'}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                      {row.request
                                        ? formatStamp(row.request.submitted_at || row.request.created_at)
                                        : 'No room request'}
                                    </p>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="space-y-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <p className="font-medium text-slate-950">{row.item.name}</p>
                                      {row.rowType === 'monitoring' ? (
                                        <Badge variant="outline">Monitoring</Badge>
                                      ) : row.shortageQty > 0 ? (
                                        <Badge className="border-rose-300 bg-rose-50 text-rose-900">
                                          Hard shortage
                                        </Badge>
                                      ) : row.reorderLevel > 0 && row.projectedRemaining <= row.reorderLevel ? (
                                        <Badge className="border-amber-300 bg-amber-50 text-amber-900">
                                          Reorder next
                                        </Badge>
                                      ) : (
                                        <Badge variant="outline">Requested</Badge>
                                      )}
                                      {!row.item.is_active ? (
                                        <Badge className="border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-50">
                                          Inactive item
                                        </Badge>
                                      ) : null}
                                    </div>
                                    <p className="text-xs text-slate-500">
                                      {row.item.given_id ?? 'No code'} · {row.item.unit || row.item.order_unit || 'units'}
                                    </p>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="space-y-1">
                                    <p className="font-medium text-slate-900">{row.supplier.supplierName}</p>
                                    <p className="text-xs text-slate-500">
                                      {row.supplier.leadTimeDays != null
                                        ? `${row.supplier.leadTimeDays} day lead time`
                                        : 'Lead time not set'}
                                    </p>
                                  </div>
                                </TableCell>
                                <TableCell className="text-right font-medium">{row.item.warehouse_on_hand}</TableCell>
                                <TableCell className="text-right">{row.requestedQty}</TableCell>
                                <TableCell className="text-right">{row.requestedTotalForItem}</TableCell>
                                <TableCell className="text-right">{row.projectedRemaining}</TableCell>
                                <TableCell className="text-right font-semibold text-rose-700">{row.shortageQty}</TableCell>
                                <TableCell className="text-right">
                                  <Badge variant="outline" className="capitalize">
                                    {row.requestItem ? titleCaseStatus(row.requestItem.status) : 'Monitoring'}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      {orderingTotalPages > 1 ? (
                        <div className="flex items-center justify-between gap-2 pt-1">
                          <p className="text-xs text-slate-500">
                            Page {safeOrderingPage} of {orderingTotalPages}
                          </p>
                          <Pagination className="mx-0 w-auto justify-end">
                            <PaginationContent>
                              <PaginationItem>
                                <PaginationPrevious
                                  onClick={() => setOrderingPage((prev) => Math.max(1, prev - 1))}
                                  className={
                                    safeOrderingPage === 1
                                      ? 'pointer-events-none cursor-not-allowed opacity-50'
                                      : 'cursor-pointer'
                                  }
                                />
                              </PaginationItem>
                              <PaginationItem>
                                <PaginationNext
                                  onClick={() =>
                                    setOrderingPage((prev) => Math.min(orderingTotalPages, prev + 1))
                                  }
                                  className={
                                    safeOrderingPage === orderingTotalPages
                                      ? 'pointer-events-none cursor-not-allowed opacity-50'
                                      : 'cursor-pointer'
                                  }
                                />
                              </PaginationItem>
                            </PaginationContent>
                          </Pagination>
                        </div>
                      ) : null}
                      </>
                        );
                      })()}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="pick-list" className="space-y-4">
                    {issueBatches.length > 0 ? (
                      <div className="grid gap-3 lg:grid-cols-3">
                        {issueBatches.slice(0, 9).map((batch) => {
                          const lines = issueBatchItemsByBatchId.get(batch.id) ?? [];
                          const plannedQty = lines.reduce((sum, line) => sum + line.planned_qty, 0);
                          const issuedQty = lines.reduce((sum, line) => sum + line.issued_qty, 0);
                          const isSelected = selectedIssueBatch?.id === batch.id;
                          const needsRetry = batch.plato_adjustment_status === 'failed' || batch.status === 'plato_failed';

                          return (
                            <button
                              key={batch.id}
                              type="button"
                              onClick={() => setSelectedIssueBatchId(batch.id)}
                              className={cn(
                                'rounded-2xl border bg-white p-4 text-left transition-colors hover:border-slate-300',
                                isSelected ? 'border-slate-400 shadow-sm' : 'border-slate-200'
                              )}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="font-semibold text-slate-950">{batch.batch_number}</p>
                                  <p className="mt-1 text-xs text-slate-500">{formatStamp(batch.created_at)}</p>
                                </div>
                                <Badge variant={needsRetry ? 'destructive' : 'outline'} className="capitalize">
                                  {needsRetry ? 'Plato failed' : titleCaseStatus(batch.status)}
                                </Badge>
                              </div>
                              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <p className="text-xs text-slate-500">Planned</p>
                                  <p className="font-semibold text-slate-950">{plannedQty}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-slate-500">Issued</p>
                                  <p className="font-semibold text-slate-950">{issuedQty}</p>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    ) : null}

                    {selectedIssueBatch ? (
                      <div className="overflow-hidden rounded-3xl border border-slate-200">
                        <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50 px-4 py-4 md:flex-row md:items-center md:justify-between">
                          <div>
                            <p className="font-semibold text-slate-950">Confirm {selectedIssueBatch.batch_number}</p>
                            <p className="mt-1 text-sm text-slate-500">
                              Review picked quantities per room. This is the stock-deduction step.
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {(selectedIssueBatch.plato_adjustment_status === 'failed' ||
                              selectedIssueBatch.status === 'plato_failed') ? (
                              <Button
                                variant="outline"
                                className="rounded-2xl"
                                disabled={retryingIssueBatchId === selectedIssueBatch.id}
                                onClick={() => void handleRetryPlatoAdjustment(selectedIssueBatch)}
                              >
                                {retryingIssueBatchId === selectedIssueBatch.id ? (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <RotateCcw className="mr-2 h-4 w-4" />
                                )}
                                Retry Plato
                              </Button>
                            ) : null}
                            <Button
                              className="rounded-2xl"
                              disabled={confirmingIssueBatch || Boolean(selectedIssueBatch.confirmed_at)}
                              onClick={handleConfirmIssueBatch}
                            >
                              {confirmingIssueBatch ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <CheckCircle2 className="mr-2 h-4 w-4" />
                              )}
                              Confirm issue
                            </Button>
                          </div>
                        </div>
                        <Table>
                          <TableHeader className="bg-slate-50">
                            <TableRow>
                              <TableHead>Item</TableHead>
                              <TableHead>Room</TableHead>
                              <TableHead className="text-right">Requested</TableHead>
                              <TableHead className="text-right">Planned</TableHead>
                              <TableHead className="w-[130px] text-right">Issued</TableHead>
                              <TableHead className="min-w-[180px]">Reason</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {selectedIssueBatchItems.map((line) => {
                              const item = itemById.get(line.inventory_item_id);
                              const room = roomById.get(line.room_id);
                              const isConfirmed = Boolean(selectedIssueBatch.confirmed_at);

                              return (
                                <TableRow key={line.id}>
                                  <TableCell>
                                    <div className="space-y-1">
                                      <p className="font-medium text-slate-950">{item?.name ?? 'Unknown item'}</p>
                                      <p className="text-xs text-slate-500">
                                        {item?.given_id ?? 'No code'} · {item?.unit || item?.order_unit || 'units'}
                                      </p>
                                    </div>
                                  </TableCell>
                                  <TableCell>{room?.name ?? 'Unknown room'}</TableCell>
                                  <TableCell className="text-right">{line.requested_qty}</TableCell>
                                  <TableCell className="text-right">{line.planned_qty}</TableCell>
                                  <TableCell>
                                    <Input
                                      type="number"
                                      min={0}
                                      max={line.requested_qty}
                                      disabled={isConfirmed}
                                      value={isConfirmed ? line.issued_qty : confirmIssueQuantities[line.id] ?? line.planned_qty}
                                      onChange={(event) =>
                                        setConfirmIssueQuantities((current) => ({
                                          ...current,
                                          [line.id]: Number(event.target.value),
                                        }))
                                      }
                                      className="text-right"
                                      aria-label={`Issued quantity for ${item?.name ?? 'item'}`}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Input
                                      value={isConfirmed ? line.issue_reason ?? '' : confirmIssueReasons[line.id] ?? ''}
                                      disabled={isConfirmed}
                                      onChange={(event) =>
                                        setConfirmIssueReasons((current) => ({
                                          ...current,
                                          [line.id]: event.target.value,
                                        }))
                                      }
                                      placeholder="Optional"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className="capitalize">
                                      {titleCaseStatus(line.status)}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    ) : pickListLines.length === 0 ? (
                      <div className="rounded-3xl border border-dashed border-slate-300 px-6 py-16 text-center">
                        <p className="text-lg font-medium text-slate-900">No items ready for picking.</p>
                        <p className="mt-2 text-sm text-slate-500">
                          Planned issue quantities from the planning view will appear here.
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-hidden rounded-3xl border border-slate-200">
                        <Table>
                          <TableHeader className="bg-slate-50">
                            <TableRow>
                              <TableHead>Item</TableHead>
                              <TableHead>Pack for rooms</TableHead>
                              <TableHead>Supplier</TableHead>
                              <TableHead className="text-right">Pick total</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {pickListLines.map((line) => (
                              <TableRow key={line.item.id}>
                                <TableCell>
                                  <div className="space-y-1">
                                    <p className="font-medium text-slate-950">{line.item.name}</p>
                                    <p className="text-xs text-slate-500">
                                      {line.item.given_id ?? 'No code'} · {line.item.unit || line.item.order_unit || 'units'}
                                    </p>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-wrap gap-2">
                                    {line.roomDemands.map((roomDemand) => (
                                      <Badge key={roomDemand.roomId} variant="outline" className="rounded-xl px-3 py-1.5">
                                        {roomDemand.roomName}: {roomDemand.plannedIssueQty}
                                      </Badge>
                                    ))}
                                  </div>
                                </TableCell>
                                <TableCell>{line.supplier.supplierName}</TableCell>
                                <TableCell className="text-right text-lg font-semibold text-slate-950">
                                  {line.totalPickQty}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="requests" className="space-y-4">
                    {warehouseRequestForms.length === 0 ? (
                      <div className="rounded-3xl border border-dashed border-slate-300 px-6 py-16 text-center">
                        <p className="text-lg font-medium text-slate-900">No submitted room forms in this view.</p>
                        <p className="mt-2 text-sm text-slate-500">
                          Submitted room request batches will appear here once rooms send them in.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-4">
                          <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                            <div>
                              <p className="text-base font-semibold text-slate-950">Simplified by room</p>
                              <p className="mt-1 text-sm text-slate-500">
                                Scan each room first, then open the exact form below if you need line detail.
                              </p>
                            </div>
                            <Badge variant="outline" className="w-fit rounded-xl bg-white">
                              {warehouseRequestRoomGroups.length} room{warehouseRequestRoomGroups.length === 1 ? '' : 's'}
                            </Badge>
                          </div>

                          <div className="grid gap-3 lg:grid-cols-2 2xl:grid-cols-3">
                            {warehouseRequestRoomGroups.map((group) => {
                              const isCurrentRoom =
                                selectedWarehouseRequestForm?.request.room_id === group.roomId;
                              const topItems = group.itemSummaries.slice(0, 4);
                              const hiddenItemCount = Math.max(group.itemSummaries.length - topItems.length, 0);

                              return (
                                <button
                                  key={group.roomId}
                                  type="button"
                                  onClick={() => setSelectedWarehouseRequestId(group.forms[0]?.request.id ?? '')}
                                  className={cn(
                                    'rounded-2xl border bg-white p-4 text-left transition-colors hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2',
                                    isCurrentRoom ? 'border-slate-400 shadow-sm' : 'border-slate-200'
                                  )}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="truncate font-semibold text-slate-950">
                                        {group.room?.name ?? 'Unknown room'}
                                      </p>
                                      <p className="mt-1 text-xs text-slate-500">
                                        Latest {formatStamp(group.latestSubmittedAt)}
                                      </p>
                                    </div>
                                    <Badge
                                      className={cn(
                                        group.totalOutstandingQty > 0
                                          ? 'border-amber-300 bg-amber-50 text-amber-900'
                                          : 'border-emerald-300 bg-emerald-50 text-emerald-900'
                                      )}
                                    >
                                      {group.totalOutstandingQty} open
                                    </Badge>
                                  </div>

                                  <div className="mt-4 grid grid-cols-3 gap-2">
                                    <div className="rounded-xl bg-slate-50 px-3 py-2">
                                      <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500">Forms</p>
                                      <p className="mt-1 text-lg font-semibold text-slate-950">{group.forms.length}</p>
                                    </div>
                                    <div className="rounded-xl bg-slate-50 px-3 py-2">
                                      <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500">Lines</p>
                                      <p className="mt-1 text-lg font-semibold text-slate-950">{group.totalLines}</p>
                                    </div>
                                    <div className="rounded-xl bg-slate-50 px-3 py-2">
                                      <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500">Qty</p>
                                      <p className="mt-1 text-lg font-semibold text-slate-950">{group.totalRequestedQty}</p>
                                    </div>
                                  </div>

                                  <div className="mt-4 space-y-2">
                                    {topItems.map((item) => (
                                      <div
                                        key={item.itemId}
                                        className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-3 py-2"
                                      >
                                        <div className="min-w-0">
                                          <p className="truncate text-sm font-medium text-slate-800">{item.itemName}</p>
                                          <p className="text-xs text-slate-500">{item.givenId ?? 'No code'}</p>
                                        </div>
                                        <div className="text-right">
                                          <p className="text-sm font-semibold text-slate-950">{item.outstandingQty}</p>
                                          <p className="text-[11px] text-slate-500">open</p>
                                        </div>
                                      </div>
                                    ))}
                                    {hiddenItemCount > 0 ? (
                                      <p className="px-1 text-xs text-slate-500">
                                        +{hiddenItemCount} more item{hiddenItemCount === 1 ? '' : 's'}
                                      </p>
                                    ) : null}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="grid gap-4 xl:grid-cols-[1.1fr,0.9fr]">
                          <div className="overflow-hidden rounded-3xl border border-slate-200">
                          <Table>
                            <TableHeader className="bg-slate-50">
                              <TableRow>
                                <TableHead>Room</TableHead>
                                <TableHead>Submitted</TableHead>
                                <TableHead className="text-right">Lines</TableHead>
                                <TableHead className="text-right">Qty</TableHead>
                                <TableHead className="text-right">Short</TableHead>
                                <TableHead className="text-right">Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {warehouseRequestForms.map(({ request, room, lines, totalRequestedQty, shortageLineCount }) => {
                                const isSelected = selectedWarehouseRequestForm?.request.id === request.id;

                                return (
                                  <TableRow
                                    key={request.id}
                                    className={cn(
                                      'cursor-pointer transition-colors hover:bg-slate-50',
                                      isSelected ? 'bg-slate-100/80' : undefined
                                    )}
                                    onClick={() => setSelectedWarehouseRequestId(request.id)}
                                  >
                                    <TableCell>
                                      <div className="space-y-1">
                                        <p className="font-medium text-slate-950">{room?.name ?? 'Unknown room'}</p>
                                        <p className="text-xs text-slate-500">Request {request.id.slice(0, 8)}</p>
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-sm text-slate-600">
                                      {formatStamp(request.submitted_at || request.created_at)}
                                    </TableCell>
                                    <TableCell className="text-right">{lines.length}</TableCell>
                                    <TableCell className="text-right">{totalRequestedQty}</TableCell>
                                    <TableCell className="text-right">{shortageLineCount}</TableCell>
                                    <TableCell className="text-right">
                                      <Badge className={cn('capitalize', REQUEST_STATUS_META[request.status])}>
                                        {titleCaseStatus(request.status)}
                                      </Badge>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>

                        <Card className="rounded-2xl border-slate-200">
                          <CardHeader>
                            <CardTitle className="text-xl text-slate-950">Selected room form</CardTitle>
                            <CardDescription>
                              Open any submitted room request to inspect the exact line items before you move to ordering.
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            {selectedWarehouseRequestForm ? (
                              <>
                                <div className="rounded-3xl border border-slate-200 bg-slate-50 px-4 py-4">
                                  <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="space-y-1">
                                      <p className="text-lg font-semibold text-slate-950">
                                        {selectedWarehouseRequestForm.room?.name ?? 'Unknown room'}
                                      </p>
                                      <p className="text-sm text-slate-500">
                                        {formatStamp(
                                          selectedWarehouseRequestForm.request.submitted_at ||
                                            selectedWarehouseRequestForm.request.created_at
                                        )}
                                      </p>
                                    </div>
                                    <Badge
                                      className={cn(
                                        'capitalize',
                                        REQUEST_STATUS_META[selectedWarehouseRequestForm.request.status]
                                      )}
                                    >
                                      {titleCaseStatus(selectedWarehouseRequestForm.request.status)}
                                    </Badge>
                                  </div>
                                  {selectedWarehouseRequestForm.request.request_notes ? (
                                    <p className="mt-3 text-sm leading-6 text-slate-600">
                                      {selectedWarehouseRequestForm.request.request_notes}
                                    </p>
                                  ) : null}
                                  {['submitted', 'reviewing', 'partially_fulfilled'].includes(
                                    selectedWarehouseRequestForm.request.status
                                  ) ? (
                                    <div className="mt-3">
                                      <Button
                                        className="rounded-2xl"
                                        onClick={() =>
                                          setConfirmIssueRequest({
                                            id: selectedWarehouseRequestForm.request.id,
                                            roomName:
                                              selectedWarehouseRequestForm.room?.name ?? 'Unknown room',
                                          })
                                        }
                                      >
                                        <PackageCheck className="mr-2 h-4 w-4" />
                                        Confirm issue
                                      </Button>
                                    </div>
                                  ) : null}
                                </div>

                                <div className="space-y-3">
                                  {selectedWarehouseRequestForm.lines.map((line) => (
                                    <div
                                      key={line.id}
                                      className="rounded-3xl border border-slate-200 px-4 py-4"
                                    >
                                      <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div className="space-y-1">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <p className="font-medium text-slate-950">
                                              {line.item?.name ?? 'Unknown item'}
                                            </p>
                                            {!line.item?.is_active ? (
                                              <Badge className="border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-50">
                                                Inactive item
                                              </Badge>
                                            ) : null}
                                          </div>
                                          <p className="text-xs text-slate-500">
                                            {line.item?.given_id ?? 'No code'} · Requested {line.requested_qty}
                                          </p>
                                        </div>
                                        <Badge variant="outline">{titleCaseStatus(line.status)}</Badge>
                                      </div>
                                      <div className="mt-3 grid gap-3 sm:grid-cols-3">
                                        <div className="rounded-2xl bg-slate-50 px-3 py-3">
                                          <p className="text-xs uppercase tracking-[0.14em] text-slate-500">
                                            Approved
                                          </p>
                                          <p className="mt-1 text-lg font-semibold text-slate-950">
                                            {line.approved_qty ?? 0}
                                          </p>
                                        </div>
                                        <div className="rounded-2xl bg-slate-50 px-3 py-3">
                                          <p className="text-xs uppercase tracking-[0.14em] text-slate-500">
                                            Fulfilled
                                          </p>
                                          <p className="mt-1 text-lg font-semibold text-slate-950">
                                            {line.fulfilled_qty}
                                          </p>
                                        </div>
                                        <div className="rounded-2xl bg-slate-50 px-3 py-3">
                                          <p className="text-xs uppercase tracking-[0.14em] text-slate-500">
                                            Shortage
                                          </p>
                                          <p className="mt-1 text-lg font-semibold text-rose-700">
                                            {line.shortage_qty}
                                          </p>
                                        </div>
                                      </div>
                                      {line.item_note ? (
                                        <p className="mt-3 text-sm leading-6 text-slate-600">{line.item_note}</p>
                                      ) : null}
                                    </div>
                                  ))}
                                </div>
                              </>
                            ) : null}
                          </CardContent>
                        </Card>
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="stock-take" className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Stock-take / cycle count</p>
                        <p className="text-xs text-slate-500">
                          Count what is physically on the shelf, then apply the variances to correct on-hand.
                        </p>
                      </div>
                      <Button
                        type="button"
                        onClick={() => void handleCreateStockTakeSession()}
                        disabled={creatingStockTake}
                      >
                        {creatingStockTake ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Plus className="mr-2 h-4 w-4" />
                        )}
                        Start new stock-take
                      </Button>
                    </div>

                    {/* ABC cycle-count worklist — today's ~8 most-due items.
                        Counting routes through the guarded stock-take above. */}
                    {cycleCountDue.length > 0 ? (
                      <div className="rounded-2xl border border-sky-200 bg-sky-50/60 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-sky-900">
                              Today's cycle count
                            </p>
                            <LabelWithHelp
                              label=""
                              align="start"
                              help="ABC schedule: high-value (A) items are counted monthly, B quarterly, C semi-annually. These are the items due today — count them in an open stock-take session, then apply the variances. The schedule rolls forward automatically once counted."
                            />
                          </div>
                          <Badge className="border-sky-300 bg-sky-100 text-sky-900 hover:bg-sky-100">
                            {cycleCountDue.length} due
                          </Badge>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {cycleCountDue.map((due) => (
                            <button
                              key={due.id}
                              type="button"
                              onClick={() => setStockTakeFilter(due.name)}
                              className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white px-3 py-1 text-xs text-slate-700 transition hover:border-sky-400 hover:bg-sky-50"
                              title={
                                due.days_overdue > 0
                                  ? `${due.days_overdue} day(s) overdue`
                                  : 'Due today'
                              }
                            >
                              <span
                                className={cn(
                                  'inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold',
                                  due.abc_class === 'A'
                                    ? 'bg-red-100 text-red-700'
                                    : due.abc_class === 'B'
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'bg-slate-100 text-slate-600'
                                )}
                              >
                                {due.abc_class}
                              </span>
                              <span className="font-medium">{due.name}</span>
                              {due.days_overdue > 0 ? (
                                <span className="text-red-600">
                                  +{due.days_overdue}d
                                </span>
                              ) : null}
                            </button>
                          ))}
                        </div>
                        <p className="mt-2 text-[11px] text-slate-500">
                          Tap an item to filter the count sheet. Count it in an open session
                          and apply — the schedule advances automatically.
                        </p>
                      </div>
                    ) : null}

                    <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
                      {/* Session list */}
                      <div className="space-y-2">
                        {stockTakeSessions.length === 0 ? (
                          <div className="rounded-2xl border border-dashed border-slate-200 p-4 text-xs text-slate-500">
                            No stock-take sessions yet. Start one to begin a count.
                          </div>
                        ) : (
                          stockTakeSessions.map((session) => {
                            const isSelected = session.id === selectedStockTakeSessionId;
                            return (
                              <button
                                key={session.id}
                                type="button"
                                onClick={() => setSelectedStockTakeSessionId(session.id)}
                                className={cn(
                                  'w-full rounded-2xl border bg-white p-3 text-left transition-colors hover:border-slate-300',
                                  isSelected ? 'border-slate-400 shadow-sm' : 'border-slate-200'
                                )}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <p className="text-sm font-semibold text-slate-900">
                                    {formatStamp(session.created_at)}
                                  </p>
                                  <Badge
                                    variant={session.status === 'open' ? 'outline' : 'secondary'}
                                    className="capitalize"
                                  >
                                    {session.status}
                                  </Badge>
                                </div>
                                {session.location ? (
                                  <p className="mt-1 text-xs text-slate-500">{session.location}</p>
                                ) : null}
                              </button>
                            );
                          })
                        )}
                      </div>

                      {/* Count sheet */}
                      <div className="space-y-3">
                        {!selectedStockTakeSession ? (
                          <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-sm text-slate-500">
                            Select or start a stock-take session to enter counts.
                          </div>
                        ) : (
                          <>
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <Input
                                placeholder="Filter items by name, category or ID…"
                                value={stockTakeFilter}
                                onChange={(event) => setStockTakeFilter(event.target.value)}
                                className="max-w-xs"
                              />
                              <div className="flex items-center gap-3">
                                {stockTakeVarianceCount > 0 ? (
                                  <Badge className="border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-50">
                                    {stockTakeVarianceCount} variance{stockTakeVarianceCount === 1 ? '' : 's'}
                                  </Badge>
                                ) : null}
                                {selectedStockTakeSession.status === 'open' ? (
                                  <Button
                                    type="button"
                                    onClick={() => void openApplyStockTakeConfirm()}
                                    disabled={stockTakeLines.length === 0}
                                  >
                                    Apply adjustments
                                  </Button>
                                ) : (
                                  <Badge variant="secondary">Completed</Badge>
                                )}
                              </div>
                            </div>

                            <div className="rounded-2xl border border-slate-200">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Item</TableHead>
                                    <TableHead className="text-right">
                                      <LabelWithHelp
                                        label="System on-hand"
                                        help="The on-hand quantity the system currently believes is in the warehouse."
                                      />
                                    </TableHead>
                                    <TableHead className="text-right">
                                      <LabelWithHelp
                                        label="Counted"
                                        help="Enter the quantity you physically counted on the shelf, then save the line."
                                      />
                                    </TableHead>
                                    <TableHead className="text-right">
                                      <LabelWithHelp
                                        label="Variance"
                                        help="Counted minus system on-hand. Non-zero variances are highlighted; applying sets on-hand to the counted quantity."
                                      />
                                    </TableHead>
                                    <TableHead className="text-right">Action</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {stockTakeCatalogItems.slice(0, 200).map((item) => {
                                    const line = stockTakeLineByItemId.get(item.id);
                                    const draftValue = stockTakeDraft[item.id] ?? '';
                                    const countedNum =
                                      draftValue.trim() === '' ? null : Number(draftValue);
                                    const liveVariance =
                                      countedNum != null && Number.isFinite(countedNum)
                                        ? countedNum - item.warehouse_on_hand
                                        : null;
                                    // MAJOR = abs delta > 10 OR > 50% of system on-hand
                                    // (mirrors apply_stock_take defaults).
                                    const isMajorVariance =
                                      liveVariance != null &&
                                      liveVariance !== 0 &&
                                      (Math.abs(liveVariance) > 10 ||
                                        (item.warehouse_on_hand !== 0 &&
                                          Math.abs(liveVariance) /
                                            Math.abs(item.warehouse_on_hand) >
                                            0.5));
                                    const isOpen = selectedStockTakeSession.status === 'open';
                                    return (
                                      <TableRow key={item.id}>
                                        <TableCell>
                                          <p className="font-medium text-slate-900">{item.name}</p>
                                          <p className="text-xs text-slate-500">
                                            {item.category ?? '—'}
                                            {item.given_id ? ` · ${item.given_id}` : ''}
                                          </p>
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">
                                          {item.warehouse_on_hand}
                                        </TableCell>
                                        <TableCell className="text-right">
                                          <Input
                                            type="number"
                                            min={0}
                                            inputMode="numeric"
                                            value={draftValue}
                                            disabled={!isOpen}
                                            onChange={(event) =>
                                              setStockTakeDraft((current) => ({
                                                ...current,
                                                [item.id]: event.target.value,
                                              }))
                                            }
                                            className="ml-auto w-24 text-right"
                                          />
                                        </TableCell>
                                        <TableCell
                                          className={cn(
                                            'text-right tabular-nums font-medium',
                                            isMajorVariance
                                              ? 'text-red-700'
                                              : liveVariance != null && liveVariance !== 0
                                                ? 'text-amber-700'
                                                : 'text-slate-400'
                                          )}
                                        >
                                          {liveVariance == null
                                            ? '—'
                                            : liveVariance > 0
                                              ? `+${liveVariance}`
                                              : liveVariance}
                                          {isMajorVariance ? (
                                            <span className="ml-1 text-[10px] font-semibold uppercase tracking-wide text-red-600">
                                              major
                                            </span>
                                          ) : null}
                                        </TableCell>
                                        <TableCell className="text-right">
                                          {isOpen ? (
                                            <Button
                                              type="button"
                                              variant="outline"
                                              size="sm"
                                              disabled={
                                                savingStockTakeItemId === item.id ||
                                                draftValue.trim() === '' ||
                                                (line != null &&
                                                  String(line.counted_qty) === draftValue.trim())
                                              }
                                              onClick={() => void handleSaveStockTakeLine(item)}
                                            >
                                              {savingStockTakeItemId === item.id ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                              ) : line ? (
                                                'Update'
                                              ) : (
                                                'Save'
                                              )}
                                            </Button>
                                          ) : line ? (
                                            <span className="text-xs text-slate-500">Counted</span>
                                          ) : (
                                            <span className="text-xs text-slate-300">—</span>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <Dialog
                      open={confirmApplyStockTakeOpen}
                      onOpenChange={setConfirmApplyStockTakeOpen}
                    >
                      <DialogContent className="max-w-lg">
                        <DialogHeader>
                          <DialogTitle>Apply stock-take adjustments?</DialogTitle>
                          <DialogDescription>
                            This sets each counted item's warehouse on-hand to the counted
                            quantity, writes an adjustment to the inventory audit log, and closes
                            this stock-take session. {stockTakeVarianceCount} item(s) will change.
                            This cannot be undone from here.
                          </DialogDescription>
                        </DialogHeader>

                        {loadingStockTakePreview ? (
                          <div className="flex items-center gap-2 py-3 text-sm text-slate-500">
                            <Loader2 className="h-4 w-4 animate-spin" /> Checking variances…
                          </div>
                        ) : stockTakeMajorPreview.length > 0 ? (
                          <Alert className="border-red-300 bg-red-50">
                            <AlertTitle className="text-red-800">
                              {stockTakeMajorPreview.length} major variance
                              {stockTakeMajorPreview.length === 1 ? '' : 's'} need
                              {stockTakeMajorPreview.length === 1 ? 's' : ''} confirmation
                            </AlertTitle>
                            <AlertDescription className="text-red-700">
                              <ul className="mt-1 space-y-1 text-xs">
                                {stockTakeMajorPreview.slice(0, 6).map((row) => (
                                  <li key={row.line_id}>
                                    <span className="font-medium">{row.item_name}</span>: you
                                    counted {row.counted_qty} but system shows {row.system_qty}{' '}
                                    (
                                    {row.variance > 0 ? '+' : ''}
                                    {row.variance}
                                    {row.variance_pct != null
                                      ? `, ${Math.round(row.variance_pct * 100)}%`
                                      : ''}
                                    )
                                  </li>
                                ))}
                              </ul>
                              {!isManager ? (
                                <p className="mt-2 font-medium">
                                  Only an admin or operations manager can confirm major
                                  variances. Ask a manager to apply, or recount these items.
                                </p>
                              ) : (
                                <p className="mt-2">
                                  As a manager you may force-apply these. Double-check the counts
                                  first — this overwrites real on-hand.
                                </p>
                              )}
                            </AlertDescription>
                          </Alert>
                        ) : null}

                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setConfirmApplyStockTakeOpen(false)}
                            disabled={applyingStockTake}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            variant={
                              stockTakeMajorPreview.length > 0 ? 'destructive' : 'default'
                            }
                            onClick={() => void handleApplyStockTake()}
                            disabled={
                              applyingStockTake ||
                              loadingStockTakePreview ||
                              (stockTakeMajorPreview.length > 0 && !isManager)
                            }
                          >
                            {applyingStockTake ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : null}
                            {stockTakeMajorPreview.length > 0
                              ? 'Force-apply & close'
                              : 'Apply & close session'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>
        ) : null}

        {canSeeWarehouse ? (
          <TabsContent value="purchase-orders" className="space-y-4">
            <TabPurposeBanner>
              Create and send orders to suppliers, and receive stock when it arrives.
            </TabPurposeBanner>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-950">Purchase orders</h2>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                  Create purchase orders, print or save them as PDFs, send them to suppliers via WhatsApp, and receive goods into stock when deliveries arrive.
                </p>
              </div>
              <div className="flex flex-col items-stretch gap-3 lg:items-end">
                <Button
                  type="button"
                  className="rounded-2xl"
                  onClick={() => setDirectPoDialogOpen(true)}
                >
                  <Plus className="mr-1.5 h-4 w-4" /> New PO
                </Button>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <Card className="rounded-2xl border-slate-200">
                <CardContent className="px-5 py-5">
                  <p className="text-xs uppercase tracking-[0.16em] text-slate-500">Drafts</p>
                  <p className="mt-2 text-3xl font-semibold text-slate-950">
                    {purchaseOrders.filter((order) => order.status === 'draft').length}
                  </p>
                </CardContent>
              </Card>
              <Card className="rounded-2xl border-slate-200">
                <CardContent className="px-5 py-5">
                  <p className="text-xs uppercase tracking-[0.16em] text-slate-500">Ready to send</p>
                  <p className="mt-2 text-3xl font-semibold text-slate-950">
                    {purchaseOrders.filter((order) => order.status === 'ready').length}
                  </p>
                </CardContent>
              </Card>
              <Card className="rounded-2xl border-slate-200">
                <CardContent className="px-5 py-5">
                  <p className="text-xs uppercase tracking-[0.16em] text-slate-500">Sent / acknowledged</p>
                  <p className="mt-2 text-3xl font-semibold text-slate-950">
                    {
                      purchaseOrders.filter((order) =>
                        ['sent', 'acknowledged'].includes(order.status)
                      ).length
                    }
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-4">
              {purchaseOrderCards.map(({ purchaseOrder, supplier, lines }) => (
                <Card key={purchaseOrder.id} className="rounded-2xl border-slate-200 shadow-sm">
                  <CardHeader className="space-y-4">
                    <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <CardTitle className="text-xl text-slate-950">{purchaseOrder.po_number}</CardTitle>
                          <Badge className={cn('capitalize', PO_STATUS_META[purchaseOrder.status])}>
                            {titleCaseStatus(purchaseOrder.status)}
                          </Badge>
                        </div>
                        <CardDescription className="text-sm leading-6">
                          {supplier?.name ?? 'Supplier record missing'}
                          {supplier?.email ? ` · ${supplier.email}` : ''} · created{' '}
                          {formatAgo(purchaseOrder.created_at)}
                        </CardDescription>
                      </div>
                      {(() => {
                        // WhatsApp send link (presentation only — same logic,
                        // now surfaced through the overflow menu).
                        const poTotal = lines.reduce((sum, l) => {
                          const price = l.unit_cost_snapshot ?? l.unit_price ?? 0;
                          return sum + (l.qty_to_order * price);
                        }, 0);
                        const publicUrl = `${window.location.origin}/po/${(purchaseOrder as any).public_token || ''}`;
                        const waText = [
                          `Hi ${supplier?.name ?? ''},`,
                          '',
                          `We'd like to place the following order.`,
                          `Please review the PO here: ${publicUrl}`,
                          '',
                          `PO: ${purchaseOrder.po_number}`,
                          `Items: ${lines.length}`,
                          `Total: $${poTotal.toFixed(2)}`,
                          '',
                          'Thank you,',
                          'Twin City Endodontics',
                        ].join('\n');
                        const phone = (supplier?.handphone ?? supplier?.telephone ?? '').replace(/[^0-9]/g, '');
                        const waLink = phone
                          ? `https://wa.me/${phone.startsWith('65') ? phone : '65' + phone}?text=${encodeURIComponent(waText)}`
                          : null;
                        const canWhatsApp = ['draft', 'ready'].includes(purchaseOrder.status) && supplier;
                        return (
                          <div className="flex flex-wrap items-center gap-2">
                            {/* Primary action — one per PO state */}
                            {purchaseOrder.status === 'draft' ? (
                              <Button
                                className="rounded-2xl"
                                onClick={() => handleUpdatePurchaseOrderStatus(purchaseOrder.id, 'ready')}
                              >
                                <Send className="mr-2 h-4 w-4" />
                                Send
                              </Button>
                            ) : null}
                            {purchaseOrder.status === 'ready' ? (
                              <Button
                                className="rounded-2xl"
                                onClick={() => handleUpdatePurchaseOrderStatus(purchaseOrder.id, 'sent')}
                              >
                                <Send className="mr-2 h-4 w-4" />
                                Mark sent
                              </Button>
                            ) : null}
                            {['sent', 'acknowledged', 'partially_received'].includes(purchaseOrder.status) ? (
                              <Button
                                className="rounded-2xl"
                                onClick={() => {
                                  setReceivingPO({ id: purchaseOrder.id, poNumber: purchaseOrder.po_number, supplierName: supplier?.name || '' });
                                }}
                              >
                                <PackageCheck className="mr-2 h-4 w-4" />
                                Receive Goods
                              </Button>
                            ) : null}

                            {/* Secondary actions tucked into an overflow menu */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="icon" className="rounded-2xl" aria-label="More actions">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-52">
                                <DropdownMenuItem
                                  onClick={() => handlePrintPurchaseOrder({ purchaseOrder, supplier, lines })}
                                >
                                  <Printer className="mr-2 h-4 w-4" />
                                  Print / Save PDF
                                </DropdownMenuItem>
                                {purchaseOrder.status === 'draft' ? (
                                  <DropdownMenuItem
                                    onClick={() => handleUpdatePurchaseOrderStatus(purchaseOrder.id, 'ready')}
                                  >
                                    <CheckCircle2 className="mr-2 h-4 w-4" />
                                    Ready to send
                                  </DropdownMenuItem>
                                ) : null}
                                {canWhatsApp ? (
                                  waLink ? (
                                    <DropdownMenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(waLink, '_blank');
                                        handleUpdatePurchaseOrderStatus(purchaseOrder.id, 'sent');
                                      }}
                                    >
                                      <Mail className="mr-2 h-4 w-4" />
                                      Send via WhatsApp
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem disabled>
                                      <Mail className="mr-2 h-4 w-4" />
                                      No phone — add in Catalog
                                    </DropdownMenuItem>
                                  )
                                ) : null}
                                {canWhatsApp ? (
                                  supplier?.email ? (
                                    <DropdownMenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(
                                          `mailto:${supplier.email}?subject=${encodeURIComponent(
                                            `Purchase Order ${purchaseOrder.po_number} - Twin City Endodontics`
                                          )}&body=${encodeURIComponent(waText)}`,
                                          '_blank'
                                        );
                                        handleUpdatePurchaseOrderStatus(purchaseOrder.id, 'sent');
                                      }}
                                    >
                                      <Mail className="mr-2 h-4 w-4" />
                                      Send via Email
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem disabled>
                                      <Mail className="mr-2 h-4 w-4" />
                                      No email — add in Catalog
                                    </DropdownMenuItem>
                                  )
                                ) : null}
                                {canWhatsApp ? (
                                  <DropdownMenuItem
                                    onClick={() => handleUpdatePurchaseOrderStatus(purchaseOrder.id, 'sent')}
                                  >
                                    <CheckCircle2 className="mr-2 h-4 w-4" />
                                    Self-purchase (mark sent)
                                  </DropdownMenuItem>
                                ) : null}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        );
                      })()}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-hidden rounded-3xl border border-slate-200">
                      <Table>
                        <TableHeader className="bg-slate-50">
                          <TableRow>
                            <TableHead>Item</TableHead>
                            <TableHead className="text-right">Qty</TableHead>
                            <TableHead className="text-right">Unit cost snapshot</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {lines.map((line) => {
                            // Free-text direct-PO lines have no catalog FK,
                            // so render the typed description instead.
                            const displayName =
                              line.inventoryItem?.name ?? line.item_description ?? 'Unknown item';
                            const displayCode =
                              line.inventoryItem?.given_id
                              ?? (line.item_description ? 'Free-text item' : 'No code');
                            return (
                              <TableRow key={line.id}>
                                <TableCell>
                                  <div className="space-y-1">
                                    <p className="font-medium text-slate-950">{displayName}</p>
                                    <p className="text-xs text-slate-500">
                                      {displayCode}
                                      {line.uom ? ` · ${line.uom}` : ''}
                                    </p>
                                  </div>
                                </TableCell>
                                <TableCell className="text-right font-medium">{line.qty_to_order}</TableCell>
                                <TableCell className="text-right">
                                  {line.unit_cost_snapshot != null
                                    ? line.unit_cost_snapshot.toFixed(2)
                                    : line.unit_price != null
                                      ? line.unit_price.toFixed(2)
                                      : 'Pending'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {purchaseOrderCards.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 px-6 py-20 text-center">
                  <p className="text-lg font-medium text-slate-900">No orders yet</p>
                  <p className="mt-2 text-sm text-slate-500">
                    Create one from the low-stock list in Warehouse → &ldquo;Low stock — order these&rdquo;, or tap
                    &ldquo;New PO&rdquo; above.
                  </p>
                </div>
              ) : null}
            </div>
          </TabsContent>
        ) : null}

        {canSeeCatalog ? (
          <TabsContent value="catalog" className="space-y-4">
            <TabPurposeBanner>
              Admin setup — product list, suppliers, and the movement history.
            </TabPurposeBanner>
            <Card className="rounded-2xl border-slate-200 shadow-sm">
              <CardHeader className="space-y-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1 space-y-1">
                    <CardTitle className="text-xl text-slate-950">Catalog & Suppliers</CardTitle>
                    <CardDescription className="text-sm text-slate-500">
                      Manage catalog quality, item coverage, and supplier readiness.
                    </CardDescription>
                  </div>
                  <div className="flex shrink-0 justify-end">
                    <div className="flex flex-col items-end gap-2 text-right">
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          className="rounded-2xl"
                          onClick={handleSyncSuppliers}
                          disabled={syncingCatalog || syncingSuppliers}
                        >
                          {syncingSuppliers ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="mr-2 h-4 w-4" />
                          )}
                          Sync suppliers
                        </Button>
                        <Button
                          className="rounded-2xl"
                          onClick={handleSyncCatalog}
                          disabled={syncingCatalog || syncingSuppliers}
                        >
                          {syncingCatalog ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="mr-2 h-4 w-4" />
                          )}
                          Sync inventory + suppliers
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">
                        {latestSyncLog
                          ? `Synced ${formatAgo(latestSyncLog.created_at)}`
                          : 'Not synced yet'}
                      </p>
                    </div>
                  </div>
                </div>

                <Tabs
                  value={catalogSubview}
                  onValueChange={(value) => setCatalogSubview(value as CatalogSubview)}
                  className="space-y-4"
                >
                  <TabsList className="grid h-auto grid-cols-4 gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-2">
                    <TabsTrigger value="health" className="rounded-xl px-4 py-3">
                      Catalog Health
                    </TabsTrigger>
                    <TabsTrigger value="browser" className="rounded-xl px-4 py-3">
                      Catalog Browser
                    </TabsTrigger>
                    <TabsTrigger value="suppliers" className="rounded-xl px-4 py-3">
                      Suppliers
                    </TabsTrigger>
                    <TabsTrigger value="ledger" className="rounded-xl px-4 py-3">
                      Dispense Ledger
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="health" className="space-y-4">
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                      <CatalogMetricCard
                        title="Catalog items"
                        value={items.length}
                        detail={`${catalogHealthSummary.requestableItems} requestable · ${catalogHealthSummary.trackedItems} stock-tracked`}
                        onClick={() => openCatalogMetricModal('catalog-items')}
                      />
                      <CatalogMetricCard
                        title="Supplier coverage"
                        value={`${catalogHealthSummary.coverageRate}%`}
                        detail={`${catalogHealthSummary.itemsWithSupplier} linked locally · ${catalogHealthSummary.itemsMissingPlatoSupplierReference} missing from Plato`}
                        onClick={() => openCatalogMetricModal('supplier-coverage')}
                      />
                      <CatalogMetricCard
                        title="Primary coverage"
                        value={`${catalogHealthSummary.primaryCoverageRate}%`}
                        detail={`${catalogHealthSummary.itemsWithPrimarySupplier} items have a default supplier`}
                        onClick={() => openCatalogMetricModal('primary-coverage')}
                      />
                      <CatalogMetricCard
                        title="Low stock watch"
                        value={catalogHealthSummary.lowStockItems}
                        detail={`${catalogHealthSummary.zeroOnHandItems} already at zero on-hand`}
                        onClick={() => openCatalogMetricModal('low-stock-watch')}
                      />
                      <CatalogMetricCard
                        title="Supplier contacts"
                        value={`${catalogHealthSummary.supplierEmailRate}%`}
                        detail={`${catalogHealthSummary.suppliersWithEmail} with email · ${catalogHealthSummary.suppliersWithPhone} with phone`}
                        onClick={() => openCatalogMetricModal('supplier-contacts')}
                      />
                    </div>

                    {latestSyncLog ? (
                      <Alert className="border-slate-200">
                        <ArrowUpRight className="h-4 w-4" />
                        <AlertTitle>{latestSyncLog.endpoint}</AlertTitle>
                        <AlertDescription className="space-y-1 text-sm leading-6">
                          <p>{latestSyncLog.summary}</p>
                          {latestSyncLog.error_message ? (
                            <p className="text-rose-700">Last error: {latestSyncLog.error_message}</p>
                          ) : null}
                        </AlertDescription>
                      </Alert>
                    ) : null}

                  {syncingCatalog || syncingSuppliers ? (
                    <Alert className="border-blue-200 bg-blue-50">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <AlertTitle>Sync in progress</AlertTitle>
                      <AlertDescription className="space-y-3">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-3 text-xs font-medium uppercase tracking-[0.16em] text-blue-900/70">
                            <span>
                              {syncProgress
                                  ? `${formatSyncResource(syncProgress.resource)} · ${formatSyncMode(syncProgress.mode)} sync · ${syncProgress.phase.replace(/-/g, ' ')}`
                                : 'Preparing sync'}
                            </span>
                              <span>{Math.round(syncProgress?.progressPercent ?? 0)}%</span>
                          </div>
                          <Progress
                              value={syncProgress?.progressPercent ?? 8}
                              className="h-2 bg-blue-100 [&>*]:bg-blue-600"
                            />
                          </div>
                        <div className="space-y-1 text-sm leading-6">
                          <p>
                            {syncProgress?.detail ??
                                'Contacting Plato and importing the latest records into the database.'}
                          </p>
                          {syncProgress ? (
                              <p className="text-xs text-blue-900/70">
                                Plato {syncProgress.resource === 'supplier' ? 'supplier' : 'inventory'} count {syncProgress.platoRecordCount} · supplier records {syncProgress.importedSuppliers} · inventory records {syncProgress.importedItems} · stock updates {syncProgress.syncedStockItems} · supplier links {syncProgress.linkedSupplierCount}{syncProgress.stockLocation ? ` · stock source ${syncProgress.stockLocation}` : ''}{syncProgress.unmatchedStockItems ? ` · unmatched stock rows ${syncProgress.unmatchedStockItems}` : ''}
                              </p>
                            ) : null}
                        </div>
                      </AlertDescription>
                    </Alert>
                    ) : null}

                    <div className="grid gap-4 xl:grid-cols-[0.95fr_1.05fr]">
                      <Card className="rounded-2xl border-slate-200 shadow-sm">
                        <CardHeader>
                          <CardTitle className="text-xl text-slate-950">Operational concerns</CardTitle>
                          <CardDescription className="mt-2 text-sm leading-6">
                            These are the data-quality gaps and stock signals that will block reliable request, reorder, and supplier communication flows.
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4">
                            <div className="flex items-start gap-3">
                              <CircleAlert className="mt-0.5 h-4 w-4 text-amber-700" />
                              <div className="space-y-1">
                                <p className="font-medium text-amber-950">Plato items without supplier reference</p>
                                <p className="text-sm text-amber-900/80">
                                  {catalogHealthSummary.itemsMissingPlatoSupplierReference} catalog item{catalogHealthSummary.itemsMissingPlatoSupplierReference === 1 ? '' : 's'} currently arrive from Plato without any supplier reference, so they cannot be auto-grouped into supplier-ready purchase drafts until a mapping is added.
                                </p>
                              </div>
                            </div>
                          </div>
                          {catalogHealthSummary.itemsWithPlatoReferenceMissingSupplierRecord > 0 ? (
                            <div className="rounded-2xl border border-orange-200 bg-orange-50 px-4 py-4">
                              <div className="flex items-start gap-3">
                                <Truck className="mt-0.5 h-4 w-4 text-orange-700" />
                                <div className="space-y-1">
                                  <p className="font-medium text-orange-950">Plato reference missing supplier master record</p>
                                  <p className="text-sm text-orange-900/80">
                                    {catalogHealthSummary.itemsWithPlatoReferenceMissingSupplierRecord} item{catalogHealthSummary.itemsWithPlatoReferenceMissingSupplierRecord === 1 ? '' : 's'} carry a supplier ID in Plato inventory, but that supplier ID did not come back from Plato&apos;s supplier feed.
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4">
                            <div className="flex items-start gap-3">
                              <PackageCheck className="mt-0.5 h-4 w-4 text-rose-700" />
                              <div className="space-y-1">
                                <p className="font-medium text-rose-950">Items at or below reorder level</p>
                                <p className="text-sm text-rose-900/80">
                                  {catalogHealthSummary.lowStockItems} item{catalogHealthSummary.lowStockItems === 1 ? '' : 's'} are already on the low-stock watchlist, including {catalogHealthSummary.zeroOnHandItems} with zero stock remaining.
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-4">
                            <div className="flex items-start gap-3">
                              <Mail className="mt-0.5 h-4 w-4 text-blue-700" />
                              <div className="space-y-1">
                                <p className="font-medium text-blue-950">Items linked to non-contactable suppliers</p>
                                <p className="text-sm text-blue-900/80">
                                  {catalogHealthSummary.itemsMissingContactableSupplier} mapped item{catalogHealthSummary.itemsMissingContactableSupplier === 1 ? '' : 's'} still lack both supplier email and phone details, which weakens PO follow-through.
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                            <div className="flex items-start gap-3">
                              <TimerReset className="mt-0.5 h-4 w-4 text-slate-700" />
                              <div className="space-y-1">
                                <p className="font-medium text-slate-950">Potentially stale sync records</p>
                                <p className="text-sm text-slate-600">
                                  {catalogHealthSummary.staleItems} item{catalogHealthSummary.staleItems === 1 ? '' : 's'} have not been refreshed from Plato in at least 14 days or do not yet carry sync timestamps.
                                </p>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="rounded-2xl border-slate-200 shadow-sm">
                        <CardHeader>
                          <CardTitle className="text-xl text-slate-950">Category health breakdown</CardTitle>
                          <CardDescription className="mt-2 text-sm leading-6">
                            Top catalog categories ranked by item count, with quick visibility into low-stock hotspots and mapping gaps.
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="overflow-hidden rounded-3xl border border-slate-200">
                            <Table>
                              <TableHeader className="bg-slate-50">
                                <TableRow>
                                  <TableHead>Category</TableHead>
                                  <TableHead className="text-right">Items</TableHead>
                                  <TableHead className="text-right">Low stock</TableHead>
                                  <TableHead className="text-right">Unmapped</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {catalogHealthSummary.categoryBreakdown.map((category) => (
                                  <TableRow key={category.category}>
                                    <TableCell className="font-medium text-slate-950">{category.category}</TableCell>
                                    <TableCell className="text-right">{category.total}</TableCell>
                                    <TableCell className="text-right">{category.lowStock}</TableCell>
                                    <TableCell className="text-right">{category.unmapped}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>

                          <div className="grid gap-3 md:grid-cols-2">
                            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                              <div className="flex items-center gap-2">
                                <Activity className="h-4 w-4 text-slate-500" />
                                <p className="text-sm font-medium text-slate-900">Coverage baseline</p>
                              </div>
                              <p className="mt-3 text-sm leading-6 text-slate-600">
                                Local supplier mappings cover {catalogHealthSummary.coverageRate}% of the catalog. Plato itself only provides supplier references on {catalogHealthSummary.itemsWithPlatoSupplierReference} item{catalogHealthSummary.itemsWithPlatoSupplierReference === 1 ? '' : 's'}, so the biggest gap is upstream data completeness rather than sync freshness.
                              </p>
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                              <div className="flex items-center gap-2">
                                <Building2 className="h-4 w-4 text-slate-500" />
                                <p className="text-sm font-medium text-slate-900">Supplier readiness</p>
                              </div>
                              <p className="mt-3 text-sm leading-6 text-slate-600">
                                {catalogHealthSummary.suppliersWithEmail} of {suppliers.length} supplier records can receive email, and {catalogHealthSummary.suppliersWithPhone} have a phone channel documented for follow-up.
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>

                  <TabsContent value="browser" className="space-y-4">
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <CatalogMetricCard
                        title="Low stock alerts"
                        value={lowStockCatalogItems.length}
                        detail={
                          lowStockCatalogItems.length > 0
                            ? `${lowStockCatalogItems.length} item${lowStockCatalogItems.length === 1 ? '' : 's'} at or below reorder level — use "Suggest PO" on each row to draft an order.`
                            : 'No items currently at or below reorder level.'
                        }
                        valueClassName={lowStockCatalogItems.length > 0 ? 'text-rose-700' : undefined}
                        onClick={() => openCatalogMetricModal('low-stock-watch')}
                      />
                      <CatalogMetricCard
                        title="Zero on hand"
                        value={zeroOnHandCatalogItems.length}
                        detail="Items already depleted in warehouse stock."
                        valueClassName={zeroOnHandCatalogItems.length > 0 ? 'text-rose-700' : undefined}
                      />
                      <CatalogMetricCard
                        title="Catalog items"
                        value={items.length}
                        detail={`${catalogHealthSummary.requestableItems} requestable · ${catalogHealthSummary.trackedItems} stock-tracked`}
                      />
                      <CatalogMetricCard
                        title="Primary coverage"
                        value={`${catalogHealthSummary.primaryCoverageRate}%`}
                        detail={`${catalogHealthSummary.itemsWithPrimarySupplier} items have a default supplier`}
                      />
                    </div>

                    <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
                      <Card className="rounded-2xl border-slate-200 shadow-sm">
                        <CardHeader className="space-y-4">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                              <CardTitle className="text-xl text-slate-950">Catalog browser</CardTitle>
                              <CardDescription className="mt-2 text-sm leading-6">
                                Browse the live imported catalog, see which items are purchase-ready, and drill into one item at a time for the full operational context.
                              </CardDescription>
                            </div>
                            <div className="relative w-full lg:max-w-sm">
                              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                              <Input
                                value={catalogSearch}
                                onChange={(event) => setCatalogSearch(event.target.value)}
                                className="pl-9"
                                placeholder="Search item, code, category, supplier"
                              />
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs">
                            <Badge variant="secondary" className="rounded-full px-3 py-1">
                              {filteredCatalogItems.length} total result{filteredCatalogItems.length === 1 ? '' : 's'}
                            </Badge>
                            <Badge variant="outline" className="rounded-full px-3 py-1">
                              Page {currentCatalogPage} of {totalCatalogPages}
                            </Badge>
                            <Badge variant="outline" className="rounded-full px-3 py-1">
                              {filteredCatalogItems.filter((item) => (itemSupplierLinksByItemId.get(item.id)?.length ?? 0) === 0).length} unmapped in current results
                            </Badge>
                            <Badge variant="outline" className="rounded-full px-3 py-1">
                              {filteredCatalogItems.filter((item) => {
                                const supplier = primarySupplierByItemId.get(item.id);
                                return (supplier?.reorderLevel ?? 0) > 0 && item.warehouse_on_hand <= (supplier?.reorderLevel ?? 0);
                              }).length} low-stock in current results
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <ScrollArea className="h-[560px] pr-4">
                            <div className="overflow-hidden rounded-2xl border border-slate-200">
                              <Table className="[&_td]:py-2 [&_th]:h-9">
                                <TableHeader className="bg-slate-50">
                                  <TableRow>
                                    <TableHead>Item</TableHead>
                                    <TableHead>Coverage</TableHead>
                                    <TableHead className="text-right">Status</TableHead>
                                    <TableHead className="text-right">
                                      <button
                                        type="button"
                                        onClick={() => handleCatalogSort('onHand')}
                                        className="ml-auto inline-flex items-center gap-1 text-right font-medium text-slate-700 transition-colors hover:text-slate-950"
                                      >
                                        <span>On hand</span>
                                        {renderCatalogSortIcon('onHand')}
                                      </button>
                                    </TableHead>
                                    <TableHead className="text-right">
                                      <button
                                        type="button"
                                        onClick={() => handleCatalogSort('reorderLevel')}
                                        className="ml-auto inline-flex items-center gap-1 text-right font-medium text-slate-700 transition-colors hover:text-slate-950"
                                      >
                                        <span>Reorder</span>
                                        {renderCatalogSortIcon('reorderLevel')}
                                      </button>
                                    </TableHead>
                                    <TableHead className="text-right">Lead time</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {paginatedCatalogItems.map((item) => {
                                    const supplier = primarySupplierByItemId.get(item.id);
                                    const allSupplierLinks = itemSupplierLinksByItemId.get(item.id) ?? [];
                                    const isSelected = item.id === selectedCatalogItem?.id;
                                    const isLowStock =
                                      (supplier?.reorderLevel ?? 0) > 0 &&
                                      item.warehouse_on_hand <= (supplier?.reorderLevel ?? 0);

                                    return (
                                      <TableRow
                                        key={item.id}
                                        className={cn(
                                          'cursor-pointer transition-colors hover:bg-slate-50',
                                          isSelected ? 'bg-slate-50' : ''
                                        )}
                                        onClick={() => setSelectedCatalogItemId(item.id)}
                                      >
                                        <TableCell>
                                          <div className="space-y-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                              <p className="font-medium text-slate-950">{item.name}</p>
                                              {!item.is_active ? (
                                                <Badge className="border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-50">
                                                  Inactive
                                                </Badge>
                                              ) : null}
                                              {isLowStock ? (
                                                <Badge
                                                  variant="destructive"
                                                  className="border-rose-300 bg-rose-50 text-rose-900 hover:bg-rose-50"
                                                >
                                                  <AlertTriangle className="mr-1 h-3 w-3" />
                                                  Reorder needed
                                                </Badge>
                                              ) : null}
                                            </div>
                                            <p className="text-xs text-slate-500">
                                              {item.given_id ?? 'No code'} · {item.category ?? 'Uncategorized'}
                                            </p>
                                            {isLowStock ? (
                                              <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                className="h-8 rounded-xl border-rose-200 px-3 text-xs text-rose-900 hover:bg-rose-50"
                                                disabled={Boolean(suggestingPoItemIds[item.id])}
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  void handleSuggestPurchaseOrder(item);
                                                }}
                                              >
                                                {suggestingPoItemIds[item.id] ? (
                                                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                ) : (
                                                  <ShoppingCart className="mr-1 h-3 w-3" />
                                                )}
                                                Suggest PO
                                              </Button>
                                            ) : null}
                                          </div>
                                        </TableCell>
                                        <TableCell>
                                          <div className="space-y-1">
                                            <p className="text-sm text-slate-900">
                                              {allSupplierLinks.length > 0
                                                ? `${allSupplierLinks.length} supplier${allSupplierLinks.length === 1 ? '' : 's'}`
                                                : 'Supplier mapping needed'}
                                            </p>
                                            <p className="text-xs text-slate-500">
                                              {supplier?.supplierName ?? 'No primary supplier'}
                                            </p>
                                          </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                          <Button
                                            type="button"
                                            variant={item.is_active ? 'default' : 'outline'}
                                            size="sm"
                                            className="h-9 rounded-xl px-3"
                                            disabled={Boolean(updatingItemIds[item.id])}
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              void handleInventoryItemActiveToggle(item, !item.is_active);
                                            }}
                                          >
                                            {updatingItemIds[item.id] ? (
                                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            ) : null}
                                            {inventoryActivationLabel(item.is_active)}
                                          </Button>
                                        </TableCell>
                                        <TableCell className="text-right">
                                          <span className={cn('font-medium', isLowStock ? 'text-rose-700' : 'text-slate-900')}>
                                            {item.warehouse_on_hand}
                                          </span>
                                        </TableCell>
                                        <TableCell className="text-right">{supplier?.reorderLevel ?? 0}</TableCell>
                                        <TableCell className="text-right">
                                          {supplier?.leadTimeDays != null ? `${supplier.leadTimeDays}d` : 'Pending'}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          </ScrollArea>

                          {filteredCatalogItems.length > 0 ? (
                            <div className="mt-6 border-t pt-4">
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <p className="text-sm text-slate-500">
                                  Showing {catalogPageStartIndex + 1} to{' '}
                                  {Math.min(catalogPageEndIndex, filteredCatalogItems.length)} of{' '}
                                  {filteredCatalogItems.length} inventory item
                                  {filteredCatalogItems.length === 1 ? '' : 's'}
                                </p>
                                {totalCatalogPages > 1 ? (
                                  <div className="overflow-x-auto pb-1">
                                    <Pagination>
                                      <PaginationContent>
                                        <PaginationItem>
                                          <PaginationPrevious
                                            onClick={() =>
                                              setCurrentCatalogPage((prev) => Math.max(1, prev - 1))
                                            }
                                            className={
                                              currentCatalogPage === 1
                                                ? 'pointer-events-none cursor-not-allowed opacity-50'
                                                : 'cursor-pointer'
                                            }
                                          />
                                        </PaginationItem>

                                        {currentCatalogPage > 2 ? (
                                          <>
                                            <PaginationItem>
                                              <PaginationLink
                                                onClick={() => setCurrentCatalogPage(1)}
                                                className="cursor-pointer"
                                              >
                                                1
                                              </PaginationLink>
                                            </PaginationItem>
                                            {currentCatalogPage > 3 ? (
                                              <PaginationItem>
                                                <PaginationEllipsis />
                                              </PaginationItem>
                                            ) : null}
                                          </>
                                        ) : null}

                                        {Array.from({ length: totalCatalogPages }, (_, index) => index + 1)
                                          .filter((page) => {
                                            return (
                                              page === currentCatalogPage ||
                                              page === currentCatalogPage - 1 ||
                                              page === currentCatalogPage + 1
                                            );
                                          })
                                          .map((page) => (
                                            <PaginationItem key={page}>
                                              <PaginationLink
                                                onClick={() => setCurrentCatalogPage(page)}
                                                isActive={currentCatalogPage === page}
                                                className="cursor-pointer"
                                              >
                                                {page}
                                              </PaginationLink>
                                            </PaginationItem>
                                          ))}

                                        {currentCatalogPage < totalCatalogPages - 1 ? (
                                          <>
                                            {currentCatalogPage < totalCatalogPages - 2 ? (
                                              <PaginationItem>
                                                <PaginationEllipsis />
                                              </PaginationItem>
                                            ) : null}
                                            <PaginationItem>
                                              <PaginationLink
                                                onClick={() => setCurrentCatalogPage(totalCatalogPages)}
                                                className="cursor-pointer"
                                              >
                                                {totalCatalogPages}
                                              </PaginationLink>
                                            </PaginationItem>
                                          </>
                                        ) : null}

                                        <PaginationItem>
                                          <PaginationNext
                                            onClick={() =>
                                              setCurrentCatalogPage((prev) =>
                                                Math.min(totalCatalogPages, prev + 1)
                                              )
                                            }
                                            className={
                                              currentCatalogPage === totalCatalogPages
                                                ? 'pointer-events-none cursor-not-allowed opacity-50'
                                                : 'cursor-pointer'
                                            }
                                          />
                                        </PaginationItem>
                                      </PaginationContent>
                                    </Pagination>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          ) : (
                            <div className="mt-6 rounded-2xl border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-500">
                              No inventory items match the current catalog search.
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      <Card className="rounded-2xl border-slate-200 shadow-sm">
                        <CardHeader>
                          <div className="flex items-center gap-2">
                            <PackageSearch className="h-5 w-5 text-slate-500" />
                            <div>
                              <CardTitle className="text-xl text-slate-950">Item detail</CardTitle>
                              <CardDescription className="mt-1 text-sm leading-6">
                                Deep view of the selected catalog item, including supplier links, inventory rules, and Plato sync metadata.
                              </CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-5">
                          {selectedCatalogItem ? (
                            <>
                              <div className="space-y-3">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="text-xl font-semibold text-slate-950">{selectedCatalogItem.name}</p>
                                  {selectedCatalogItem.category ? <Badge variant="outline">{selectedCatalogItem.category}</Badge> : null}
                                  <Badge
                                    className={cn(
                                      selectedCatalogItem.is_active
                                        ? 'border-emerald-200 bg-emerald-50 text-emerald-900 hover:bg-emerald-50'
                                        : 'border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-50'
                                    )}
                                  >
                                    {inventoryActivationLabel(selectedCatalogItem.is_active)}
                                  </Badge>
                                  {selectedCatalogItem.given_id ? (
                                    <Badge variant="secondary" className="font-mono">
                                      {selectedCatalogItem.given_id}
                                    </Badge>
                                  ) : null}
                                </div>
                                <p className="text-sm leading-6 text-slate-600">
                                  {selectedCatalogItem.description || 'No description has been imported for this item yet.'}
                                </p>
                                <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                                  <div className="space-y-1">
                                    <p className="text-sm font-medium text-slate-950">Warehouse monitoring</p>
                                    <p className="text-xs leading-5 text-slate-500">
                                      Active items appear in warehouse low-stock monitoring. Inactive items stay requestable, but only surface in warehouse when rooms actually request them.
                                    </p>
                                  </div>
                                  <Button
                                    type="button"
                                    variant={selectedCatalogItem.is_active ? 'default' : 'outline'}
                                    size="sm"
                                    className="ml-auto h-10 rounded-xl px-4"
                                    disabled={Boolean(updatingItemIds[selectedCatalogItem.id])}
                                    onClick={() =>
                                      void handleInventoryItemActiveToggle(
                                        selectedCatalogItem,
                                        !selectedCatalogItem.is_active
                                      )
                                    }
                                  >
                                    {updatingItemIds[selectedCatalogItem.id] ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : null}
                                    Mark as {selectedCatalogItem.is_active ? 'Inactive' : 'Active'}
                                  </Button>
                                </div>
                              </div>

                              <div className="grid gap-3 sm:grid-cols-2">
                                <CatalogMetricCard
                                  title="Stock position"
                                  value={selectedCatalogItem.warehouse_on_hand}
                                  detail={`Reorder level ${primarySupplierByItemId.get(selectedCatalogItem.id)?.reorderLevel ?? 0}`}
                                  onClick={() => openCatalogMetricModal('selected-item-stock-position')}
                                />
                                <CatalogMetricCard
                                  title="Supplier coverage"
                                  value={selectedCatalogItemSuppliers.length}
                                  detail={
                                    selectedCatalogItemSuppliers.some((link) => link.isPrimary)
                                      ? 'Primary supplier assigned'
                                      : 'Primary supplier still needed'
                                  }
                                  onClick={() => openCatalogMetricModal('selected-item-supplier-coverage')}
                                />
                              </div>

                              <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-3 rounded-2xl border border-slate-200 bg-white px-4 py-4">
                                  <p className="text-sm font-medium text-slate-950">Product profile</p>
                                  <div className="grid gap-2 text-sm text-slate-600">
                                    <div className="flex items-center justify-between gap-3">
                                      <span>Manufacturer</span>
                                      <span className="text-right text-slate-950">{selectedCatalogItem.manufacturer || 'Not set'}</span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                      <span>Unit (dispense)</span>
                                      <span className="text-right text-slate-950">
                                        {selectedCatalogItem.unit || 'N/A'}
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                      <Label
                                        htmlFor={`order-unit-${selectedCatalogItem.id}`}
                                        className="font-normal text-slate-600"
                                      >
                                        Order unit
                                      </Label>
                                      <Input
                                        id={`order-unit-${selectedCatalogItem.id}`}
                                        value={orderUnitDraft}
                                        onChange={(event) => setOrderUnitDraft(event.target.value)}
                                        placeholder="e.g. box"
                                        className="h-8 w-32 rounded-xl text-right"
                                      />
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                      <Label
                                        htmlFor={`pack-size-${selectedCatalogItem.id}`}
                                        className="font-normal text-slate-600"
                                      >
                                        Pack size (units per order unit)
                                      </Label>
                                      <Input
                                        id={`pack-size-${selectedCatalogItem.id}`}
                                        type="number"
                                        inputMode="decimal"
                                        min={0}
                                        step="any"
                                        value={packSizeDraft}
                                        onChange={(event) => setPackSizeDraft(event.target.value)}
                                        placeholder="e.g. 50"
                                        className="h-8 w-32 rounded-xl text-right"
                                      />
                                    </div>
                                    {(orderUnitDraft.trim() || '') !== (selectedCatalogItem.order_unit ?? '') ||
                                    (packSizeDraft.trim() || '') !==
                                      (selectedCatalogItem.pack_size != null
                                        ? String(selectedCatalogItem.pack_size)
                                        : '') ? (
                                      <div className="flex justify-end pt-1">
                                        <Button
                                          type="button"
                                          size="sm"
                                          className="h-8 rounded-xl px-4"
                                          disabled={savingOrderConfig}
                                          onClick={() => void handleSaveOrderConfig()}
                                        >
                                          {savingOrderConfig ? (
                                            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                                          ) : null}
                                          Save order settings
                                        </Button>
                                      </div>
                                    ) : null}
                                    <div className="flex items-center justify-between gap-3">
                                      <span>Inventory type</span>
                                      <span className="text-right text-slate-950">{selectedCatalogItem.inventory_type || 'Not set'}</span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                      <span>Track stock</span>
                                      <span className="text-right text-slate-950">{selectedCatalogItem.track_stock ? 'Yes' : 'No'}</span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                      <span>Warehouse active</span>
                                      <span className="text-right text-slate-950">
                                        {inventoryActivationLabel(selectedCatalogItem.is_active)}
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                      <span>Requestable</span>
                                      <span className="text-right text-slate-950">{selectedCatalogItem.is_requestable ? 'Yes' : 'No'}</span>
                                    </div>
                                  </div>
                                </div>

                                <div className="space-y-3 rounded-2xl border border-slate-200 bg-white px-4 py-4">
                                  <p className="text-sm font-medium text-slate-950">Commercial profile</p>
                                  <div className="grid gap-2 text-sm text-slate-600">
                                    <div className="flex items-center justify-between gap-3">
                                      <span>Cost price</span>
                                      <span className="text-right text-slate-950">
                                        {selectedCatalogItem.cost_price != null ? selectedCatalogItem.cost_price.toFixed(2) : 'Pending'}
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                      <span>Selling price</span>
                                      <span className="text-right text-slate-950">
                                        {selectedCatalogItem.selling_price != null ? selectedCatalogItem.selling_price.toFixed(2) : 'Pending'}
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                      <span>Last synced from Plato</span>
                                      <span className="text-right text-slate-950">{formatStamp(selectedCatalogItem.last_plato_synced_at)}</span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                      <span>Last edited in Plato</span>
                                      <span className="text-right text-slate-950">{formatStamp(selectedCatalogItem.last_plato_edited_at)}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Price-change tracking (backlog item #8): unit-cost
                                  snapshots off this item's PO lines, newest first. */}
                              <div className="space-y-3 rounded-2xl border border-slate-200 bg-white px-4 py-4">
                                <p className="text-sm font-medium text-slate-950">Price history</p>
                                {selectedCatalogItemPriceHistory.length === 0 ? (
                                  <p className="text-sm text-slate-500">
                                    No price history yet. Unit costs are recorded automatically each time this item
                                    goes onto a purchase order.
                                  </p>
                                ) : (
                                  <div className="grid gap-2 text-sm text-slate-600">
                                    {selectedCatalogItemPriceHistory.map((entry, index) => {
                                      const previous = selectedCatalogItemPriceHistory[index + 1] ?? null;
                                      const deltaCents =
                                        previous != null ? entry.unitCostCents - previous.unitCostCents : null;
                                      return (
                                        <div
                                          key={`${entry.purchaseOrderId}-${index}`}
                                          className="flex flex-wrap items-center justify-between gap-3"
                                        >
                                          <span>
                                            {format(parseISO(entry.recordedAt), 'dd MMM yyyy')} ·{' '}
                                            {supplierById.get(entry.supplierId)?.name ?? 'Unknown supplier'} ·{' '}
                                            {entry.poNumber}
                                          </span>
                                          <span className="text-right">
                                            <span className="font-medium text-slate-950">
                                              {formatCentsSGD(entry.unitCostCents)}
                                            </span>
                                            {deltaCents != null && deltaCents !== 0 ? (
                                              <span
                                                className={cn(
                                                  'ml-2 text-xs font-medium',
                                                  deltaCents > 0 ? 'text-rose-600' : 'text-emerald-600'
                                                )}
                                              >
                                                {deltaCents > 0 ? '+' : '-'}
                                                {formatCentsSGD(deltaCents)} (
                                                {previous && previous.unitCostCents > 0
                                                  ? `${deltaCents > 0 ? '+' : '-'}${(
                                                      (Math.abs(deltaCents) / previous.unitCostCents) *
                                                      100
                                                    ).toFixed(1)}%`
                                                  : 'n/a'}
                                                )
                                              </span>
                                            ) : deltaCents === 0 ? (
                                              <span className="ml-2 text-xs text-slate-400">no change</span>
                                            ) : null}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>

                              <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                  <Truck className="h-4 w-4 text-slate-500" />
                                  <p className="text-sm font-medium text-slate-900">Supplier links</p>
                                </div>
                                <div className="space-y-3">
                                  {selectedCatalogItemSuppliers.length > 0 ? (
                                    selectedCatalogItemSuppliers.map((link) => (
                                      <div key={link.linkId} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                                        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                          <div className="space-y-1">
                                            <div className="flex items-center gap-2">
                                              <p className="font-medium text-slate-950">{link.supplierName}</p>
                                              {link.isPrimary ? <Badge variant="secondary">Primary</Badge> : null}
                                            </div>
                                            <p className="text-sm text-slate-600">
                                              {link.contactPerson || link.supplierEmail || link.supplierPhone || 'Contact details still incomplete'}
                                            </p>
                                          </div>
                                          <div className="grid gap-1 text-sm text-slate-600 sm:grid-cols-2 sm:text-right">
                                            <span>MOQ {link.minimumOrderQty || 0}</span>
                                            <span>Reorder {link.reorderLevel}</span>
                                            <span>Lead time {link.leadTimeDays != null ? `${link.leadTimeDays}d` : 'Pending'}</span>
                                            <span>Supplier SKU {link.supplierSku || 'Pending'}</span>
                                          </div>
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-6 text-sm text-slate-500">
                                      This item does not have any supplier links yet, so it cannot flow cleanly into warehouse reorder actions.
                                    </div>
                                  )}
                                </div>
                              </div>

                              {selectedCatalogItem.remarks ? (
                                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                                  <p className="text-sm font-medium text-slate-900">Imported remarks</p>
                                  <p className="mt-2 text-sm leading-6 text-slate-600">{selectedCatalogItem.remarks}</p>
                                </div>
                              ) : null}
                            </>
                          ) : (
                            <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-12 text-center text-sm text-slate-500">
                              No catalog item matches the current search yet.
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>

                  <TabsContent value="suppliers" className="space-y-4">
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <CatalogMetricCard
                        title="Suppliers"
                        value={suppliers.length}
                        detail="Live supplier directory imported for catalog operations"
                        onClick={() => openCatalogMetricModal('suppliers-total')}
                      />
                      <CatalogMetricCard
                        title="Item mappings"
                        value={itemSuppliers.length}
                        detail={`Including ${supplierCatalogSummaries.reduce((sum, row) => sum + row.primaryMappings, 0)} primary relationships`}
                        onClick={() => openCatalogMetricModal('item-mappings')}
                      />
                      <CatalogMetricCard
                        title="Contactable suppliers"
                        value={catalogHealthSummary.suppliersWithEmail}
                        detail={`${catalogHealthSummary.suppliersWithPhone} also have a phone follow-up channel`}
                        onClick={() => openCatalogMetricModal('contactable-suppliers')}
                      />
                      <CatalogMetricCard
                        title="Supplier search results"
                        value={filteredSuppliers.length}
                        detail="Filtered roster based on supplier name, contact, or mapped item"
                        onClick={() => openCatalogMetricModal('supplier-search-results')}
                      />
                    </div>

                    <div className="grid gap-4 xl:grid-cols-[1fr_0.95fr]">
                      <Card className="rounded-2xl border-slate-200 shadow-sm">
                        <CardHeader className="space-y-4">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                              <CardTitle className="text-xl text-slate-950">Supplier roster</CardTitle>
                              <CardDescription className="mt-2 text-sm leading-6">
                                Review supplier readiness, see which vendors carry the most catalog weight, and spot records that still need contact cleanup.
                              </CardDescription>
                            </div>
                            <div className="relative w-full lg:max-w-sm">
                              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                              <Input
                                value={supplierSearch}
                                onChange={(event) => setSupplierSearch(event.target.value)}
                                className="pl-9"
                                placeholder="Search supplier, contact, or item"
                              />
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <ScrollArea className="h-[560px] pr-4">
                            <div className="overflow-hidden rounded-2xl border border-slate-200">
                              <Table className="[&_td]:py-2 [&_th]:h-9">
                                <TableHeader className="bg-slate-50">
                                  <TableRow>
                                    <TableHead>Supplier</TableHead>
                                    <TableHead className="text-right">Mappings</TableHead>
                                    <TableHead className="text-right">Primary</TableHead>
                                    <TableHead className="text-right">Low stock</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {filteredSuppliers.slice(0, 50).map((row) => {
                                    const isSelected = row.supplier.id === selectedSupplierSummary?.supplier.id;

                                    return (
                                      <TableRow
                                        key={row.supplier.id}
                                        className={cn(
                                          'cursor-pointer transition-colors hover:bg-slate-50',
                                          isSelected ? 'bg-slate-50' : ''
                                        )}
                                        onClick={() => setSelectedSupplierId(row.supplier.id)}
                                      >
                                        <TableCell>
                                          <div className="space-y-1">
                                            <p className="font-medium text-slate-950">{row.supplier.name}</p>
                                            <p className="text-xs text-slate-500">
                                              {row.supplier.email || row.supplier.contact_person || 'Contact details still incomplete'}
                                            </p>
                                          </div>
                                        </TableCell>
                                        <TableCell className="text-right">{row.totalMappings}</TableCell>
                                        <TableCell className="text-right">{row.primaryMappings}</TableCell>
                                        <TableCell className="text-right">{row.lowStockItems.length}</TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          </ScrollArea>
                        </CardContent>
                      </Card>

                      <Card className="rounded-2xl border-slate-200 shadow-sm">
                        <CardHeader>
                          <div className="flex items-center gap-2">
                            <Factory className="h-5 w-5 text-slate-500" />
                            <div>
                              <CardTitle className="text-xl text-slate-950">Supplier detail</CardTitle>
                              <CardDescription className="mt-1 text-sm leading-6">
                                Contact readiness, coverage depth, and the mapped items that depend on the selected supplier.
                              </CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-5">
                          {selectedSupplierSummary ? (
                            <>
                              <div className="space-y-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="text-xl font-semibold text-slate-950">{selectedSupplierSummary.supplier.name}</p>
                                  {selectedSupplierSummary.supplier.email ? (
                                    <Badge variant="secondary">Email ready</Badge>
                                  ) : (
                                    <Badge variant="outline">Email missing</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600">
                                  {selectedSupplierSummary.supplier.notes || 'No supplier notes have been captured yet.'}
                                </p>
                              </div>

                              <div className="grid gap-3 sm:grid-cols-2">
                                <CatalogMetricCard
                                  title="Mapped items"
                                  value={selectedSupplierSummary.mappedItems.length}
                                  detail={`${selectedSupplierSummary.primaryMappings} primary assignments`}
                                  onClick={() =>
                                    openCatalogMetricModal(
                                      'selected-supplier-mapped-items',
                                      selectedSupplierSummary.supplier.id
                                    )
                                  }
                                  className="rounded-2xl border-slate-200 bg-slate-50"
                                />
                                <CatalogMetricCard
                                  title="Operational averages"
                                  value={
                                    selectedSupplierSummary.averageLeadTimeDays != null
                                      ? `${selectedSupplierSummary.averageLeadTimeDays}d`
                                      : 'N/A'
                                  }
                                  detail={`Avg reorder ${selectedSupplierSummary.averageReorderLevel ?? 'N/A'}`}
                                  onClick={() =>
                                    openCatalogMetricModal(
                                      'selected-supplier-operational-averages',
                                      selectedSupplierSummary.supplier.id
                                    )
                                  }
                                  className="rounded-2xl border-slate-200 bg-slate-50"
                                />
                              </div>

                              <div className="grid gap-3 sm:grid-cols-2">
                                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
                                  <p className="text-sm font-medium text-slate-900">Contact channels</p>
                                  <div className="mt-3 space-y-2 text-sm text-slate-600">
                                    <div className="flex items-center justify-between gap-3">
                                      <span>Email</span>
                                      <span className="text-right text-slate-950">{selectedSupplierSummary.supplier.email || 'Missing'}</span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                      <span>Phone</span>
                                      <span className="text-right text-slate-950">
                                        {selectedSupplierSummary.supplier.telephone || selectedSupplierSummary.supplier.handphone || 'Missing'}
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                      <span>Contact person</span>
                                      <span className="text-right text-slate-950">{selectedSupplierSummary.supplier.contact_person || 'Missing'}</span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                      <span>Address</span>
                                      <span className="text-right text-slate-950">{selectedSupplierSummary.supplier.address || 'Missing'}</span>
                                    </div>
                                  </div>
                                </div>

                                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
                                  <p className="text-sm font-medium text-slate-900">Operational signals</p>
                                  <div className="mt-3 space-y-3 text-sm text-slate-600">
                                    <div className="flex items-start gap-2">
                                      <Boxes className="mt-0.5 h-4 w-4 text-slate-400" />
                                      <p>
                                        {selectedSupplierSummary.totalMappings} supplier mapping{selectedSupplierSummary.totalMappings === 1 ? '' : 's'} currently rely on this vendor.
                                      </p>
                                    </div>
                                    <div className="flex items-start gap-2">
                                      <PackageCheck className="mt-0.5 h-4 w-4 text-slate-400" />
                                      <p>
                                        {selectedSupplierSummary.lowStockItems.length} mapped item{selectedSupplierSummary.lowStockItems.length === 1 ? '' : 's'} are already at or below reorder level.
                                      </p>
                                    </div>
                                    <div className="flex items-start gap-2">
                                      <Mail className="mt-0.5 h-4 w-4 text-slate-400" />
                                      <p>
                                        {selectedSupplierSummary.supplier.email
                                          ? 'Email-ready for PO dispatch.'
                                          : 'Still missing supplier email, so PO sending will need manual follow-up.'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                  <ClipboardList className="h-4 w-4 text-slate-500" />
                                  <p className="text-sm font-medium text-slate-900">Mapped items</p>
                                </div>
                                <div className="overflow-hidden rounded-3xl border border-slate-200">
                                  <Table>
                                    <TableHeader className="bg-slate-50">
                                      <TableRow>
                                        <TableHead>Item</TableHead>
                                        <TableHead className="text-right">On hand</TableHead>
                                        <TableHead className="text-right">Low stock</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {selectedSupplierSummary.mappedItems.slice(0, 12).map((item) => {
                                        const isLowStock = selectedSupplierSummary.lowStockItems.some(
                                          (candidate) => candidate.id === item.id
                                        );

                                        return (
                                          <TableRow key={item.id}>
                                            <TableCell>
                                              <div className="space-y-1">
                                                <p className="font-medium text-slate-950">{item.name}</p>
                                                <p className="text-xs text-slate-500">
                                                  {item.given_id ?? 'No code'} · {item.category ?? 'Uncategorized'}
                                                </p>
                                              </div>
                                            </TableCell>
                                            <TableCell className="text-right">{item.warehouse_on_hand}</TableCell>
                                            <TableCell className="text-right">
                                              {isLowStock ? (
                                                <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700">
                                                  Review now
                                                </Badge>
                                              ) : (
                                                <span className="text-slate-500">Healthy</span>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-12 text-center text-sm text-slate-500">
                              No supplier matches the current search yet.
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>

                  <TabsContent value="ledger" className="space-y-4">
                    <DispenseLedgerView />
                  </TabsContent>
                </Tabs>
              </CardHeader>
            </Card>
          </TabsContent>
        ) : null}
      </Tabs>

      {catalogMetricModal && metricDialogConfig ? (
        <>
          <button
            type="button"
            aria-label="Close catalog metric details"
            className="fixed inset-x-0 bottom-0 top-16 z-40 bg-slate-950/35 md:top-[73px]"
            onClick={() => setCatalogMetricModal(null)}
          />
          <div className="fixed inset-x-2 bottom-4 top-[4.75rem] z-40 mx-auto flex max-w-5xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl md:inset-x-6 md:top-[5.5rem]">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 sm:px-6">
              <div className="min-w-0 space-y-2">
                <h3 className="text-2xl font-semibold text-slate-950">
                  {metricDialogConfig.title}
                </h3>
                <p className="text-sm leading-6 text-slate-600">
                  {metricDialogConfig.description}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                className="shrink-0 rounded-2xl"
                onClick={() => setCatalogMetricModal(null)}
              >
                Close
              </Button>
            </div>
            <ScrollArea className="flex-1 px-5 py-4 sm:px-6">
              {metricDialogConfig.content}
            </ScrollArea>
          </div>
        </>
      ) : null}

      <Dialog
        open={Boolean(pendingInactiveRequest)}
        onOpenChange={(open) => {
          if (!open) {
            setPendingInactiveRequest(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Request inactive item?</DialogTitle>
            <DialogDescription>
              {pendingInactiveRequest
                ? `${itemById.get(pendingInactiveRequest.itemId)?.name ?? 'This item'} is marked inactive for normal warehouse stock monitoring. Rooms can still request it, but warehouse will only see it once you confirm and submit the request.`
                : 'This item is marked inactive for normal warehouse stock monitoring.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Use this for unusual or non-routine items that should stay requestable without showing up in the normal low-stock reorder queue.
            </div>
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPendingInactiveRequest(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={confirmInactiveRequest}
              >
                Continue request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Direct-PO creation (backlog item #15). Lives at the root so it
          stays mounted regardless of which inventory tab is active. */}
      <NewPurchaseOrderDialog
        open={directPoDialogOpen}
        onOpenChange={(open) => {
          setDirectPoDialogOpen(open);
          if (!open) {
            setDirectPoInitialDraft(null);
          }
        }}
        initialDraft={directPoInitialDraft}
        onCreated={() => {
          setActiveTab('purchase-orders');
          void loadSnapshot();
        }}
      />

      <ConfirmIssueDialog
        open={!!confirmIssueRequest}
        onOpenChange={(open) => { if (!open) setConfirmIssueRequest(null); }}
        requestId={confirmIssueRequest?.id || null}
        roomName={confirmIssueRequest?.roomName || ''}
        userId={currentUser?.id || ''}
        isManager={isManager}
        onIssued={() => void loadSnapshot()}
      />

      <GoodsReceivingDialog
        open={!!receivingPO}
        onOpenChange={(open) => { if (!open) setReceivingPO(null); }}
        purchaseOrderId={receivingPO?.id || null}
        poNumber={receivingPO?.poNumber || ''}
        supplierName={receivingPO?.supplierName || ''}
        userId={currentUser?.id || ''}
        onReceived={() => void loadSnapshot()}
      />
    </div>
  );
};

export default InventoryView;
